import * as React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import routes from 'routes';
import {
  formatDisplayInteger,
  localizeDigitsForLocale,
} from 'i18n/arabicDigits';
import { formatTransactionTableDate, getLocalizedDatePickerFormat } from 'i18n/calendarLabels';
import { useTranslation } from 'react-i18next';
import {
  CheckOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  FileOutlined,
  MenuOutlined,
  PlusOutlined,
  ScanOutlined,
  SearchOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { ReactComponent as EyeIcon } from 'assets/images/icons/eyeIcon.svg';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import {
  DatePicker,
  Dropdown,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
} from 'antd';
import { ConfirmModal } from 'components/Custom/ConfirmModal';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { StyledCard } from 'components/Custom/card';
import { RoundInput } from 'components/Custom/inputs';
import {
  DrawerDateRangeFilter,
  useDrawerDateRangeFilter,
} from 'components/Custom/drawer-date-range-filter';
import {
  ScopeDropdownButton,
  ScopeEditCancelIcon,
  ScopeEditSaveButton,
} from 'components/Custom/ScopeDropdownButton';
import { FilterSelect } from 'components/Custom/selects';
import { CardTitleLargeSpan } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { AttachmentViewer } from 'components/Vat/Report/tables/transactions/components/AttachmentViewer/index';
import { MessageStates } from 'containers/MessageBox';
import dayjs from 'dayjs';
import { Provider, DateOptions } from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnMessageOn,
  turnModalOn,
} from 'store/app/actions';
import {
  AccountType,
  DrawerType,
  ProviderPlan,
  WindowType,
} from 'store/app/enums';
import { getDrawerData, getDrawerOpen } from 'store/app/selectors';
import type { DrawerData, MessageData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import { fetchChartOfAccountsForOrganisationRequest } from 'store/chart-of-accounts/actions';
import type { CoaAccountRow } from 'store/chart-of-accounts/models';
import {
  fetchCountriesRequest,
  fetchCurrenciesRequest,
} from 'store/common/actions';
import {
  getCommonCountries,
  getCommonCurrencies,
  getCurrenciesLoaded,
} from 'store/common/selectors';
import {
  bulkUpdateExcelTransactionsRequest,
  deleteExcelTransactionRequest,
  deleteExcelTransactionsFileRequest,
  fetchAllExcelTransactionsRequest,
  fetchExcelFilesMetadataRequest,
  fetchOrganisationContactsRequest,
  fetchTransactionItemsRequest,
  rescanReceipt,
  addExcelTransactionsRequest,
  saveExcelDrawerTransactionsBatchRequest,
  setTransactionsReviewedRequest,
  updateExcelTransactionRequest,
  uploadReceiptRequest,
  uploadReceiptToProvider,
  clearDrawerTransactionIdMap,
} from 'store/organisation/actions';
import type {
  ExcelFileMetadata,
  ExcelTransaction,
  Organisation,
  ValidatedExcelTransaction,
} from 'store/organisation/models';
import { ReviewStatus, UploadStatus } from 'store/organisation/models';
import {
  getAllExcelTransactionsLoading,
  getAllExcelTransactionsPage,
  getBulkUpdateExcelTransactionsSuccessCount,
  getChartOfAccountsForOrganisation,
  getIsPostingToProvider,
  getLastUploadedReceiptsBatchId,
  getOrganisationViesContacts,
} from 'store/organisation/selectors';
import type { StoreState } from 'store/types';
import type { ProviderPostStatus } from 'store/organisation/types';
import {
  fetchTransactionAttachmentRequest,
  uploadTransactionAttachmentRequest,
} from 'store/vat/actions';
import {
  getLastFetchedTransactionAttachments,
  getVatAttachmentLoading,
} from 'store/vat/selectors';
// Re-enable when the "+" filter selection button is restored.
// import { FilterSelectionDrawer } from 'components/MainComponent/FilterSelectionDrawer';
import type { MenuProps } from 'antd';
import FilterTransactionsImage from 'assets/images/filter-transactions-02.svg';
import UploadTransactionsImage from 'assets/images/upload-transactions.svg';
import { ReactComponent as IconBell } from 'assets/images/icons/iconBell.svg';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { ReactComponent as IconAttachment } from 'assets/images/icons/iconTransactionAttachment.svg';
import { ReactComponent as IconTransactionHasAttachment } from 'assets/images/icons/iconTransactionHasAttachment.svg';
import { QuestionMarkTooltip } from 'components/Custom/QuestionMarkTooltip';
import type { ScopeTourStep } from 'components/Custom/ScopeTour';
import {
  SCOPE_TOUR_STEP_TITLE_CLASS,
  ScopeTour,
} from 'components/Custom/ScopeTour';
import { excelColumnNames } from 'pages/organisations/organisation';
import {
  PAGE_SIZE_EXCEL_TRANSACTIONS,
  TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
  TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_EXCEL_KEY,
  TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_KEY,
  TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_RECEIPTS_KEY,
  TRANSACTIONS_READY_TO_POST_TOUR_SHOWN_KEY,
} from 'shared/constants/transactionsTourKeys';
import {
  getContactTypeLabel,
  getContactTypeOptions,
} from 'utils/display-enum-label';
import { shouldSkipChartOfAccountsBurst } from 'utils/chartOfAccountsFetchGuard';
import { entitySupportsDocumentScanning } from 'utils/captureTransactionsAccess';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { useRegisterPwaTransactionsBottomActions } from 'components/PwaBottomNav/transactions-actions-context';
import {
  PwaReviewPage,
  type PwaReviewQueue,
  type PwaReviewSort,
} from 'pwa/components/PwaReviewPage';
import { PwaReviewActivitySheet } from 'pwa/components/PwaReviewActivitySheet';
import {
  appendSelectTransactionUuidFilter,
  buildTransactionsRefreshFilters,
  convertScopeTableFiltersToApiBody,
  parseSelectTransactionFromSearch,
  removeSelectTransactionFromSearch,
  resolveTabFileImportIdsForRequest,
} from 'utils/entityTransactionsRequestFilters';
import { shouldSkipExcelFilesMetadataBurst } from 'utils/excelFilesMetadataFetchGuard';
import {
  exportEntityTransactionsToExcel,
  getEntityTransactionExportColumns,
  getEntityTransactionsExportFilename,
} from 'utils/export-entity-transactions-to-excel';
import { getProviderName } from 'utils/get-provider-name';
import {
  getIs3rdPartyProvider,
  shouldFetchOrganisationProviderData,
  showsCoaAccountCodeColumn,
  showsQuantityAndUnitPriceColumns,
} from 'utils/get-provider-type';
import {
  formatTransactionAmountDisplay,
  formatTransactionNumericInputValue,
  formatTransactionQuantityDisplay,
  parseLocaleNumber,
  toApiNumber,
  toApiQuantity,
  useNumberFormat,
} from 'utils/numberFormat';
import { formatFormattedAmountWithCurrency } from 'utils/currencyFormat';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
  setPreferencesInSection,
  getStoredPreferenceDetails,
  usePreferences,
} from 'utils/preferences';
import {
  partitionFilesByValidation,
  RECEIPT_UPLOAD_ACCEPT,
  validateReceiptUploadFile,
} from 'utils/safe-upload';
import { shouldSkipTransactionItemsBurst } from 'utils/transactionItemsFetchGuard';
import { isUploadDateInRange } from 'utils/drawer-date-range';
import { isTransactionItemsEnabled } from 'utils/feature-flag-utils';
import { useFitItemsPerPage } from 'hooks/useFitItemsPerPage';
import { useFetchTrackingCategories } from 'hooks/useFetchTrackingCategories';
import { ShowUploadsPanelContext } from '../show-uploads-panel-context';
import { useReceiptsScanPrerequisitesMet } from '../receipts-scan-prerequisites';
import { VALIDATION_COLUMN_KEY_MAP } from '../upload-transactions-columns-modal';
import {
  applyAccountSelectionToTransactionFields,
  buildAccountNameByIdMap,
  buildAccountNameOptionsFromCoa,
  coaRowId,
  resolveAccountDisplayLabel,
  resolveAccountSelectEditState,
} from './account-display';
import { buildContactNameValueOptions } from './contact-display';
import {
  shouldIncludeTrackingCategoryFields,
  resolveTrackingOptionDisplayLabel,
  trackingSelectOptionsForValue,
} from './tracking-display';
import {
  ACCOUNT_NAME_DISPLAY_MAX_LENGTH,
  DEFAULT_PAGE_SIZE,
  DESCRIPTION_DISPLAY_MAX_LENGTH,
  ITEM_DISPLAY_MAX_LENGTH,
  DRAWER_EXCLUDED_FIELD_KEYS,
  EDIT_DRAWER_OPTIONAL_KEYS,
  EDITABLE_ROW_KEYS,
  ENTITY_TOOLBAR_MUTED_ICON_COLOR,
  ENTITY_TRANSACTION_COLUMN_MIN_WIDTHS,
  getEditDrawerLabel,
  getEntityTransactionColumnTitle,
  ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS,
  ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY,
  ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY,
  ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY,
  ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF,
  ENTITY_TRANSACTIONS_TAB_HORIZONTAL_PADDING_PX,
  ENTITY_TRANSACTIONS_PWA_MOBILE_PADDING_PX,
  resolvePwaMobileTransactionColumnKeys,
  ENTITY_TRANSACTIONS_TABLE_EXCLUDED_COLUMN_KEYS,
  FILE_PANEL_RESIZE_STRIP_BG_ACTIVE,
  FILE_PANEL_RESIZE_STRIP_BG_HOVER,
  FILE_PANEL_RESIZE_STRIP_BG_IDLE,
  FILE_PANEL_RESIZE_STRIP_HIT_PX,
  FILE_PANEL_WIDTH_DEFAULT,
  FILE_PANEL_WIDTH_MAX,
  FILE_PANEL_WIDTH_MIN,
  injectDefaultTransactionTabColumns,
  injectTrackingCategoryColumns,
  MASS_UPDATE_FIELD_MARK_AS_REVIEWED,
  LEDGER_POST_BANK_ACCOUNT_PREF,
  MONETARY_AUXILIARY_COLUMN_KEYS,
  NOT_CLICKED_ATTACHMENT,
  NUMERIC_COLUMN_KEYS,
  OPTIONAL_NUMERIC_COLUMN_KEYS,
  sanitiseQuantityInput,
  TRANSACTION_AMOUNT_FIELD_KEYS,
  PRE_TOTAL_COLUMN_BLOCK_KEYS,
  getEntityTransactionsNonReorderableColumnKeys,
  getEntityTransactionsAlwaysVisibleColumnKeys,
  getEntityTransactionsTailToggleableColumnKeys,
  providerIcons,
  getSourceTypeDisplayLabel,
  getSourceTypeOptions,
  isEntityTransactionColumnReadOnly,
  stripExcludedEntityTransactionTableColumns,
  TAX_NAME_DISPLAY_MAX_LENGTH,
  TRACKING_CATEGORY_1_COLUMN_KEY,
  TRACKING_CATEGORY_2_COLUMN_KEY,
  TRACKING_OPTION_ID_FIELDS,
  TRANSACTIONS_TAB_SIDE_GAP_PX,
  TRANSACTIONS_TAB_SPLIT_MIN_HEIGHT_CSS,
  TRANSACTIONS_FILE_PANEL_FIXED_TOP_PX,
  PWA_FILE_LIST_PAGE_SIZE,
} from './constants';
import {
  applyTrackingOptionSelectionToTransactionFields,
  applyCalculatedGrossAndNet,
  buildDocumentSharedMassUpdatePayload,
  buildTransactionSaveBody,
  clampFilePanelWidth,
  collectTransactionsSameDocument,
  computeCaptureFileCountsFromMetadata,
  createBlankManualExcelTransaction,
  extractDocumentSharedFieldPatch,
  fetchAllTransactionsForDocumentFile,
  filterContactOptionsForSourceType,
  formatTransactionEditValidationMessage,
  getTransactionFileFilterId,
  getTransactionReversalType,
  hasPerLineRowChanges,
  hasRowChanges,
  isClientOnlyManualTransactionId,
  isDocumentFileFullyLoadedForEdit,
  isTrackingOptionIdField,
  isTransactionDeletedRow,
  isTransactionEditedRow,
  notifyReceiptTaxEditWarning,
  prepareDrawerTransactionSaveBatch,
  resolveCalculatedGrossAndNet,
  resolveMassUpdateContactOptionsForSourceTypes,
  resolveTransactionTotalsCurrencyCode,
  sumDocumentDrawerAmountTotals,
  trackingOptionIdFieldToDimensionIndex,
  trackingTableColumnKeyToOptionField,
  transactionQualifiesForMarkAsReviewed,
  validateTransactionEditSave,
} from './entity-transaction-helpers';
import { ExcelActivityLogsDrawerContent } from './excel-activity-logs-drawer';
import { TransactionHistoryModalContent } from './transaction-history-modal-content';
import {
  resolveEditInDrawerFromPreferences,
  resolveTransactionsTabColumnsFromPreferences,
  toTransactionsTabColumnsState,
  transactionsTabColumnsConfigKeyForOrg,
  type TransactionsTabColumnsPersistConfig,
} from './preferences';
import { renderSortIcon } from './render-sort-icon';
import {
  TransactionEditModeSegmented,
  TransactionsTabSettingsDrawerContent,
} from './settings-drawer';
import {
  CUSTOMISE_COLUMNS_ICON_FONT_SIZE,
  CustomiseColumnsIconButtonWithTooltip,
  customiseColumnsIconButtonStyle,
} from 'components/Custom/customise-columns-icon-button';
import {
  EntityTransactionsCardInner,
  EntityTransactionsHeader,
  EntityTransactionsHeaderActions,
  EntityTransactionsMassUpdateControls,
  EntityTransactionsMetaBar,
  EntityTransactionsMetaDivider,
  EntityTransactionsMetaText,
  EntityTransactionsFilterTotals,
  EntityTransactionsShowDeleted,
  EntityTransactionsToolbarIconButton,
  entityTransactionsToolbarButtonStyle,
  MassUpdateApplyButton,
  MassUpdateFieldSelectHighlightStyle,
  PreviewTableWrapper,
  ProviderPostDropdownMenuStyle,
  ProviderPostSplitButton,
  TransactionReviewDrawerSegmentedWrap,
  TransactionsFileCardWrap,
} from './styles';
import { TransactionsFileStateStepper } from './TransactionsFileStateStepper';
import {
  applyItemSelectionToTransactionFields,
  buildItemNameByIdMap,
  buildTransactionItemOptions,
  resolveItemDisplayLabel,
  resolveItemSelectEditState,
} from './item-display';
import {
  buildTaxNameByIdMap,
  buildTaxNameOptions,
  buildTaxNameValueOptions,
  buildTaxRateByIdMap,
  resolveTaxDisplayLabel,
  resolveTaxSelectEditState,
  syncTaxFieldsFromRateId,
} from './tax-display';
import {
  buildInterpretationRuleFieldOptions,
  openInterpretationRuleModalFromDraft,
} from '../interpretation-rules/open-interpretation-rules';
import {
  buildDraftFromSuggestion,
  suggestInterpretationRulesFromEditDiff,
} from '../interpretation-rules/suggest-rule-from-edit';
import { SuggestInterpretationRuleModal } from '../interpretation-rules/SuggestInterpretationRuleModal';
import type { InterpretationRuleDraft } from '../interpretation-rules/types';
import { TransactionEditDrawerWithAttachments } from './transaction-edit-drawer';
import {
  computeSidePanelSignature,
  hasProcessingSidePanelFile,
  pendingRescanDrawerReopenRef,
  resolveFirstSidePanelFileId,
} from './pending-rescan-drawer-reopen';
import { normalizeExcelTransactionFromApi } from 'store/organisation/reducer/excelHandlers';

interface TransactionsTabProps {
  organisationId: string;
  connectionId?: string;
  organisation?: Organisation | null;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  onUploadFile?: () => void;
  onAddTransaction?: () => void;
  showUploadsPanel?: boolean;
  onToggleUploadsPanel?: () => void;
  /** Optional: when receipts upload is initiated from Transactions tab, allow parent to switch to Capture. */
  onReceiptsUploadStart?: (optimisticRescanFileName?: string) => void;
  /** Lets parent trigger a table-aware transactions refresh (page size, filters, tab scope). */
  onRegisterRefetchExcelTransactions?: (fn: (() => void) | null) => void;
  /** Bumped by the entity shell when a drawer-triggered rescan finishes, so the drawer can reopen. */
  rescanDrawerReopenSignal?: number;
  /** Parent invokes this once the transactions tab is mounted to open the review drawer. */
  onRegisterRescanDrawerReopen?: (fn: (() => void) | null) => void;
  /** Optional promo shown above main content when Capture Documents is not yet used. */
  captureDocumentsPromo?: React.ReactNode;
}

// Module-scoped burst dedupe so identical fetches are skipped even across
// quick unmount/remount cycles of the tab component.
const txFetchBurstByKey = new Map<string, number>();
const CONTACT_NEW_SUFFIX_REGEX = /\s\(new\)$/i;

const shouldSkipTxFetchBurst = (
  requestKey: string,
  windowMs = 1500
): boolean => {
  const now = Date.now();
  const lastAt = txFetchBurstByKey.get(requestKey);
  if (lastAt != null && now - lastAt < windowMs) return true;
  txFetchBurstByKey.set(requestKey, now);
  // Lightweight cleanup to avoid unbounded growth.
  if (txFetchBurstByKey.size > 200) {
    for (const [key, at] of txFetchBurstByKey.entries()) {
      if (now - at > windowMs * 10) txFetchBurstByKey.delete(key);
    }
  }
  return false;
};

type TransactionsFileCardHoverActionsProps = {
  showRescan: boolean;
  onRescan: () => void;
  onDelete: () => void;
  rescanTooltip: string;
  deleteTooltip: string;
  rescanLabel: string;
  deleteLabel: string;
};

const TransactionsFileCardHoverActions: React.FC<
  TransactionsFileCardHoverActionsProps
> = ({
  showRescan,
  onRescan,
  onDelete,
  rescanTooltip,
  deleteTooltip,
  rescanLabel,
  deleteLabel,
}) => (
  <span
    className="transactions-file-card-hover-actions"
    onClick={(e) => e.stopPropagation()}
  >
    {showRescan ? (
      <Tooltip title={rescanTooltip} placement="left">
        <button
          type="button"
          className="transactions-file-card-hover-action"
          onClick={onRescan}
        >
          {rescanLabel}
        </button>
      </Tooltip>
    ) : null}
    <Tooltip title={deleteTooltip} placement="left">
      <button
        type="button"
        className="transactions-file-card-hover-action"
        onClick={onDelete}
      >
        {deleteLabel}
      </button>
    </Tooltip>
  </span>
);

type TransactionsFileCardMenuProps = {
  showRescan: boolean;
  onViewFile: () => void;
  onRescan: () => void;
  onDelete: () => void;
  viewFileLabel: string;
  rescanLabel: string;
  deleteLabel: string;
  moreActionsLabel: string;
};

const TransactionsFileCardMenu: React.FC<TransactionsFileCardMenuProps> = ({
  showRescan,
  onViewFile,
  onRescan,
  onDelete,
  viewFileLabel,
  rescanLabel,
  deleteLabel,
  moreActionsLabel,
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const items: MenuProps['items'] = [
    {
      key: 'view-file',
      icon: <EyeOutlined aria-hidden />,
      label: viewFileLabel,
    },
    ...(showRescan
      ? [
          {
            key: 'rescan' as const,
            icon: <ScanOutlined aria-hidden />,
            label: rescanLabel,
          },
        ]
      : []),
    {
      key: 'delete',
      icon: <DeleteOutlined aria-hidden />,
      label: deleteLabel,
      danger: true,
    },
  ];

  return (
    <span
      className="transactions-file-card-hover-actions transactions-file-card-menu"
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        open={menuOpen}
        onOpenChange={setMenuOpen}
        menu={{
          items,
          onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            setMenuOpen(false);
            const runAfterMenuClosed = (fn: () => void) => {
              window.requestAnimationFrame(fn);
            };
            if (key === 'view-file') {
              runAfterMenuClosed(onViewFile);
              return;
            }
            if (key === 'rescan') {
              runAfterMenuClosed(onRescan);
              return;
            }
            if (key === 'delete') {
              runAfterMenuClosed(onDelete);
            }
          },
        }}
      >
        <button
          type="button"
          className="transactions-file-card-menu-trigger"
          aria-label={moreActionsLabel}
          aria-haspopup="menu"
        >
          <EllipsisOutlined aria-hidden />
        </button>
      </Dropdown>
    </span>
  );
};

export const TransactionsTab: React.FC<TransactionsTabProps> = ({
  organisationId,
  connectionId: connectionIdProp,
  organisation: organisationProp,
  handlePreferences,
  showUploadsPanel: showUploadsPanelProp = true,
  onUploadFile,
  onReceiptsUploadStart,
  onRegisterRefetchExcelTransactions,
  rescanDrawerReopenSignal,
  onRegisterRescanDrawerReopen,
  captureDocumentsPromo,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
const navigate = useNavigate();
  const dispatch = useDispatch();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const transactionsTabPaddingPx = pwaMobileNav
    ? ENTITY_TRANSACTIONS_PWA_MOBILE_PADDING_PX
    : ENTITY_TRANSACTIONS_TAB_HORIZONTAL_PADDING_PX;
  const lastUploadedReceiptsBatchId = useSelector(
    getLastUploadedReceiptsBatchId
  );
  const context = React.useContext(ShowUploadsPanelContext);
  const showUploadsPanel = context?.showUploadsPanel ?? showUploadsPanelProp;
  const receiptFileInputRef = React.useRef<HTMLInputElement | null>(null);
  /** File id when rescan was requested from the review drawer (survives confirm modal). */
  const rescanFromDrawerFileIdRef = React.useRef<string | null>(null);
  /** Prevents navigateDrawerToFile being called repeatedly for the same target. */
  const rescanReopenNavigationForFileRef = React.useRef<string | null>(null);

  const allExcelTransactionsPage = useSelector(getAllExcelTransactionsPage);
  const allExcelTransactionsLoading = useSelector(
    getAllExcelTransactionsLoading
  );
  const isPostingToProvider = useSelector(getIsPostingToProvider);
  const currencies = useSelector(getCommonCurrencies);
  const currenciesLoaded = useSelector(getCurrenciesLoaded);
  const countries = useSelector(getCommonCountries);
  const bulkUpdateExcelTransactionsSuccessCount = useSelector(
    getBulkUpdateExcelTransactionsSuccessCount
  );

  const [uploadSelectionMode, setUploadSelectionMode] =
    React.useState<boolean>(false);
  const [selectedUploadFileIds, setSelectedUploadFileIds] = React.useState<
    string[]
  >([]);
  /** Selected COA account when posting for Ledger provider plan. */
  const [ledgerPostAccountId, setLedgerPostAccountId] = React.useState<
    string | undefined
  >(undefined);
  const [rightPanelTransactionTab, setRightPanelTransactionTab] =
    React.useState<'CAPTURED' | 'REVIEWED' | 'POSTED'>('CAPTURED');
  const [pwaReviewSort, setPwaReviewSort] =
    React.useState<PwaReviewSort>('newest');
  const [pwaActivitySheetOpen, setPwaActivitySheetOpen] = React.useState(false);

  const transactionsFilePanelWidthPrefKey =
    organisationId?.trim() &&
    `transactions-tab-file-panel-width-${organisationId}`;

  const [filePanelWidth, setFilePanelWidth] = React.useState(
    FILE_PANEL_WIDTH_DEFAULT
  );
  const [filePanelResizeActive, setFilePanelResizeActive] =
    React.useState(false);
  const [filePanelFixedTopPx, setFilePanelFixedTopPx] = React.useState(
    TRANSACTIONS_FILE_PANEL_FIXED_TOP_PX
  );
  const filePanelResizeStartRef = React.useRef({
    clientX: 0,
    width: FILE_PANEL_WIDTH_DEFAULT,
  });

  const updateFilePanelFixedTop = React.useCallback(() => {
    if (typeof window === 'undefined') return;

    const header = document.querySelector(
      '.header-container'
    ) as HTMLElement | null;
    const nav = document.querySelector(
      '.entity-details-tab-nav-wrap'
    ) as HTMLElement | null;

    // Use offsetHeight (not getBoundingClientRect) so scroll doesn't move the panel.
    const headerHeight = header?.offsetHeight ?? 70;
    const navHeight = nav?.offsetHeight ?? 50;
    const nextTop = Math.max(
      TRANSACTIONS_FILE_PANEL_FIXED_TOP_PX,
      Math.ceil(headerHeight + navHeight)
    );

    setFilePanelFixedTopPx((prev) => (prev === nextTop ? prev : nextTop));
  }, []);

  React.useLayoutEffect(() => {
    if (showUploadsPanel === false) return undefined;
    if (pwaMobileNav) return undefined;

    updateFilePanelFixedTop();
    const rafId = window.requestAnimationFrame(updateFilePanelFixedTop);
    const timeoutId = window.setTimeout(updateFilePanelFixedTop, 0);

    window.addEventListener('resize', updateFilePanelFixedTop);

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateFilePanelFixedTop);
    const header = document.querySelector('.header-container');
    const nav = document.querySelector('.entity-details-tab-nav-wrap');
    if (header) resizeObserver?.observe(header);
    if (nav) resizeObserver?.observe(nav);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', updateFilePanelFixedTop);
      resizeObserver?.disconnect();
    };
  }, [showUploadsPanel, organisationId, pwaMobileNav, updateFilePanelFixedTop]);

  React.useEffect(() => {
    if (!organisationId?.trim()) {
      setFilePanelWidth(FILE_PANEL_WIDTH_DEFAULT);
      return;
    }
    const name = `transactions-tab-file-panel-width-${organisationId}`;
    const raw = handlePreferences(PreferenceActions.GET, {
      name,
    });
    const parsed =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string'
        ? parseInt(raw, 10)
        : NaN;
    if (Number.isFinite(parsed)) setFilePanelWidth(clampFilePanelWidth(parsed));
    else setFilePanelWidth(FILE_PANEL_WIDTH_DEFAULT);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when entity changes
  }, [organisationId]);

  React.useEffect(() => {
    if (!filePanelResizeActive) return;
    const onMove = (e: MouseEvent) => {
      const { clientX: startX, width: startW } =
        filePanelResizeStartRef.current;
      setFilePanelWidth(clampFilePanelWidth(startW + (startX - e.clientX)));
    };
    const onUp = () => {
      setFilePanelResizeActive(false);
      document.body.style.removeProperty('user-select');
      setFilePanelWidth((w) => {
        if (transactionsFilePanelWidthPrefKey)
          handlePreferences(PreferenceActions.SET, {
            name: transactionsFilePanelWidthPrefKey,
            value: w,
          });
        return w;
      });
    };
    document.body.style.setProperty('user-select', 'none');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.removeProperty('user-select');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers use latest closure via refs where needed
  }, [
    filePanelResizeActive,
    transactionsFilePanelWidthPrefKey,
    handlePreferences,
  ]);

  // Fetch currencies if not already loaded. An empty list is a valid API
  // result — do not treat `length === 0` as "not loaded" (that loops).
  React.useEffect(() => {
    if (currenciesLoaded) return;
    dispatch(fetchCurrenciesRequest());
  }, [dispatch, currenciesLoaded]);

  // Fetch countries if not already loaded
  React.useEffect(() => {
    if (!countries || countries.length === 0) {
      dispatch(fetchCountriesRequest());
    }
  }, [dispatch, countries]);

  const selectedOrganisation = useSelector((state: StoreState) =>
    state.organisation.organisations?.find((org) => org.uuid === organisationId)
  );
  const selectedOrganisationByConnectionId = useSelector((state: StoreState) =>
    connectionIdProp
      ? state.organisation.organisations?.find(
          (org) => org.connectionId === connectionIdProp
        )
      : undefined
  );
  // Prefer live store organisation so metadata refreshes propagate immediately.
  const organisation =
    selectedOrganisation ??
    selectedOrganisationByConnectionId ??
    organisationProp;
  const resolvedOrganisationId = organisationId || organisation?.uuid || '';
  const connectionId = connectionIdProp ?? organisation?.connectionId ?? '';
  const organisationViesContactsSlice = useSelector(
    getOrganisationViesContacts
  );
  const viesContacts = React.useMemo(() => {
    if (!connectionId?.trim() || !organisationViesContactsSlice)
      return undefined;
    if (organisationViesContactsSlice.connectionId !== connectionId) {
      return undefined;
    }
    return organisationViesContactsSlice.contacts;
  }, [connectionId, organisationViesContactsSlice]);
  const providerName = organisation?.providerName?.toLowerCase();

  const isReceiptsProvider = providerName !== Provider.EXCEL.toLowerCase();
  const isReceiptsOnlyProvider =
    providerName === Provider.RECEIPTS.toLowerCase();
  const isExcelProvider = providerName === Provider.EXCEL.toLowerCase();
  const scanPrerequisitesMet = useReceiptsScanPrerequisitesMet(
    resolvedOrganisationId || undefined,
    organisation
  );
  /** Soft-delete toggle + transaction history (eye / View history) ? Excel & Receipts only */
  const supportsDeletedAndHistory =
    isExcelProvider || isReceiptsOnlyProvider;
  const selectTransactionId = React.useMemo(() => {
    if (!isExcelProvider && !isReceiptsOnlyProvider) return undefined;
    return parseSelectTransactionFromSearch(location.search);
  }, [isExcelProvider, isReceiptsOnlyProvider, location.search]);
  /** Excel: keep uuid table filter after URL param is consumed. */
  const [pinnedExcelSelectTransactionFilterId, setPinnedExcelSelectTransactionFilterId] =
    React.useState<string | undefined>(undefined);
  /** Receipts: keep deep-link target after URL param is removed (in-flight drawer open). */
  const [activeSelectTransactionDeepLinkId, setActiveSelectTransactionDeepLinkId] =
    React.useState<string | undefined>(undefined);
  const selectTransactionFilterId =
    selectTransactionId ??
    (isExcelProvider ? pinnedExcelSelectTransactionFilterId : undefined);
  const receiptsDeepLinkTransactionId =
    selectTransactionId ?? activeSelectTransactionDeepLinkId;
  const selectTransactionDeepLinkConsumedRef = React.useRef(false);
  const applySelectTransactionUuidApiFilter =
    Boolean(selectTransactionFilterId) && isExcelProvider;

  const consumeSelectTransactionDeepLink = React.useCallback(
    (transactionUuid: string) => {
      const normalized = transactionUuid?.trim();
      if (!normalized || selectTransactionDeepLinkConsumedRef.current) {
        return;
      }
      selectTransactionDeepLinkConsumedRef.current = true;
      if (isExcelProvider) {
        setPinnedExcelSelectTransactionFilterId(normalized);
      }
      if (isReceiptsOnlyProvider) {
        setActiveSelectTransactionDeepLinkId(normalized);
      }
      if (!parseSelectTransactionFromSearch(location.search)) {
        return;
      }
      const orgPath = routes.organisations.organisation.replace(
        ':id',
        organisationId
      );
      if (location.pathname !== orgPath) {
        return;
      }
      const nextSearch = removeSelectTransactionFromSearch(location.search);
      navigate(
        {
          pathname: orgPath,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true }
      );
    },
    [
      navigate,
      location.pathname,
      location.search,
      organisationId,
      isExcelProvider,
      isReceiptsOnlyProvider,
    ]
  );  const showFileStateTabs = !isExcelProvider;
  const is3rdPartyProvider = getIs3rdPartyProvider(providerName);
  const showPostedFileState = !isExcelProvider && !isReceiptsOnlyProvider;
  const showReadyToPostTour = is3rdPartyProvider && showPostedFileState;
  const transactionsReviewedTabLabelKey =
    isReceiptsOnlyProvider || isExcelProvider
      ? 'organisation.file-badge-reviewed'
      : 'organisation.transactions-view-tab-reviewed';
  const isXeroProvider = providerName === Provider.XERO.toLowerCase();
  const isQboProvider =
    providerName === Provider.QUICKBOOKS.toLowerCase() ||
    Boolean(providerName?.includes('quickbook'));
  const isZohoProvider = providerName === Provider.ZOHO.toLowerCase();
  const showProviderPostDropdown = isXeroProvider || isZohoProvider;
  const providerPostMenuItems: MenuProps['items'] = React.useMemo(() => {
    if (isXeroProvider) {
      return [
        { key: 'DRAFT', label: t('organisation.file-post-as-draft') },
        {
          key: 'SUBMITTED',
          label: t('organisation.file-post-as-awaiting-approval'),
        },
        { key: 'AUTHORISED', label: t('organisation.file-post-as-authorised') },
      ];
    }
    if (isZohoProvider) {
      return [
        { key: 'DRAFT', label: t('organisation.file-post-as-draft') },
        { key: 'AUTHORISED', label: t('organisation.file-post-as-authorised') },
      ];
    }
    return [];
  }, [isXeroProvider, isZohoProvider, t]);
  const isLedgerProviderPlan =
    organisation?.providerPlan === ProviderPlan.LEDGER;
  const ledgerProviderPlanName = organisation?.providerPlan
    ? organisation.providerPlan.charAt(0) +
      organisation.providerPlan.slice(1).toLowerCase()
    : '';

  React.useEffect(() => {
    if (!resolvedOrganisationId?.trim() || !isLedgerProviderPlan) {
      setLedgerPostAccountId(undefined);
      return;
    }
    const raw = handlePreferences(PreferenceActions.GET, {
      name: `${LEDGER_POST_BANK_ACCOUNT_PREF}-${resolvedOrganisationId}`,
    });
    const saved =
      typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
    setLedgerPostAccountId(saved);
    // Only re-load when the entity/plan changes — not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedOrganisationId, isLedgerProviderPlan]);

  const persistLedgerPostAccountId = React.useCallback(
    (accountId: string | undefined) => {
      setLedgerPostAccountId(accountId);
      if (!resolvedOrganisationId?.trim()) return;
      handlePreferences(PreferenceActions.SET, {
        name: `${LEDGER_POST_BANK_ACCOUNT_PREF}-${resolvedOrganisationId}`,
        value: accountId ?? '',
      });
    },
    [handlePreferences, resolvedOrganisationId]
  );
  const getDeleteFileTooltipText = React.useCallback(
    () =>
      isExcelProvider || isReceiptsOnlyProvider
        ? t('organisation.file-delete-tooltip-revert')
        : t('organisation.file-delete-document-lines'),
    [isExcelProvider, isReceiptsOnlyProvider, t]
  );
  const isAccountIdProvider = isReceiptsOnlyProvider || isExcelProvider;
  const excelProviderHiddenColumnKeys = React.useMemo(
    () =>
      new Set(
        isExcelProvider
          ? [
              'uploadDate',
              'dueDate',
              'currency',
              'exchangeRate',
              'discountAmount',
            ]
          : []
      ),
    [isExcelProvider]
  );
  const isGroupProvider = providerName === Provider.GROUP.toLowerCase();
  /** Edit-in-drawer preference switch: all entity providers except Group and Excel. */
  const showEditInDrawerSetting =
    Boolean(providerName) && !isGroupProvider && !isExcelProvider;

  /** Extraction rules: Receipts, Xero/QBO, and other scan-capable entities ? not Excel or Group. */
  const supportsInterpretationRules =
    entitySupportsDocumentScanning(organisation?.providerName);
  /** Review drawer: create capture rule from field / post-save prompt — desktop only. */
  const reviewCaptureRuleCreationEnabled =
    supportsInterpretationRules && !pwaMobileNav;

  /** Tracking category columns (+ API fetch): third-party bookkeeping only, not Excel or Receipts. */
  const showTrackingCategoryColumns =
    Boolean(providerName) &&
    providerName !== Provider.EXCEL.toLowerCase() &&
    providerName !== Provider.RECEIPTS.toLowerCase();

  /** Item column + /items fetch for third-party bookkeeping providers. */
  const showTransactionItemColumns =
    showTrackingCategoryColumns && isTransactionItemsEnabled();

  /** Quantity + unit price: Xero / QuickBooks / Zoho / Receipts (not Excel). */
  const showQuantityUnitPriceColumns = showsQuantityAndUnitPriceColumns(
    organisation?.providerName
  );

  /** Filter provider-hidden keys only — never append columns (e.g. into lockedColumns). */
  const stripProviderExcludedColumns = React.useCallback(
    (columnKeys: string[]) =>
      stripExcludedEntityTransactionTableColumns(columnKeys).filter(
        (k) =>
          !excelProviderHiddenColumnKeys.has(k) &&
          (showTransactionItemColumns || k !== 'itemId') &&
          (showQuantityUnitPriceColumns ||
            (k !== 'quantity' && k !== 'unitPrice' && k !== 'taxInclusive')) &&
          (showTrackingCategoryColumns ||
            !ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS.includes(k))
      ),
    [
      excelProviderHiddenColumnKeys,
      showTransactionItemColumns,
      showQuantityUnitPriceColumns,
      showTrackingCategoryColumns,
    ]
  );

  const organisationTrackingCategories = organisation?.trackingCategories ?? [];

  useFetchTrackingCategories(
    resolvedOrganisationId,
    organisation,
    showTrackingCategoryColumns
  );

  // Fallback: on hard refresh on Transactions tab, ensure transaction items load
  // if parent-level timing missed it (third-party bookkeeping providers only).
  React.useEffect(() => {
    if (!showTransactionItemColumns) return;
    if (!resolvedOrganisationId.trim()) return;
    if (!organisation) return;
    if (!shouldFetchOrganisationProviderData(organisation)) return;
    if (organisation.transactionItems !== undefined) return;
    if (shouldSkipTransactionItemsBurst(resolvedOrganisationId)) return;
    dispatch(fetchTransactionItemsRequest(resolvedOrganisationId));
  }, [
    dispatch,
    resolvedOrganisationId,
    showTransactionItemColumns,
    organisation,
    organisation?.transactionItems,
  ]);

  const handleReceiptFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length || !organisationId) {
        e.target.value = '';
        return;
      }

      const fileArray = Array.from(files);
      const { allowed, rejected } = partitionFilesByValidation(
        fileArray,
        validateReceiptUploadFile
      );

      if (!allowed.length) {
        if (rejected.length) {
          const message: MessageData = {
            title: t('common.unsupported-file-type-title'),
            description: t(
              'common.only-bmp-jpeg-jpg-gif-png-and-pdf-files-can-be-uploaded'
            ),
            type: MessageStates.WARNING,
          };
          dispatch(turnMessageOn(message));
        }
        e.target.value = '';
        return;
      }

      if (rejected.length) {
        const message: MessageData = {
          title: t('organisation.some-files-skipped-title'),
          description: t('organisation.some-files-skipped-description'),
          type: MessageStates.WARNING,
        };
        dispatch(turnMessageOn(message));
      }

      dispatch(uploadReceiptRequest(organisationId, allowed));
      e.target.value = '';
      // Switch to Capture so user sees the scan progress panel there.
      onReceiptsUploadStart?.();
    },
    [dispatch, organisationId, onReceiptsUploadStart]
  );

  const currencyOptions = React.useMemo(
    () =>
      [...(currencies || [])]
        .sort((a, b) => a.localeCompare(b))
        .map((c) => ({ label: c, value: c })),
    [currencies]
  );

  const countryOptions = React.useMemo(
    () =>
      [...(countries || [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ label: c.name, value: c.iso })),
    [countries]
  );

  const { contactOptions, contactOptionLookup } = React.useMemo(() => {
    const options: { label: string; value: string }[] = [];
    const lookup = new Map<string, { label: string; value: string }>();
    const seen = new Set<string>();
    const addLookupKey = (
      raw: string | null | undefined,
      option: { label: string; value: string }
    ) => {
      const key = String(raw ?? '').trim();
      if (!key || lookup.has(key)) return;
      lookup.set(key, option);
    };

    (viesContacts ?? []).forEach((c) => {
      const contactUuid = (c.contactUuid ?? '').trim();
      const providerContactId = (c.providerContactId ?? '').trim();
      const id = contactUuid || providerContactId;
      if (!id || seen.has(id)) return;
      seen.add(id);
      const name = (c.name ?? '').trim();
      const taxNumber = (c.taxNumber ?? '').trim();
      const label =
        name && taxNumber ? `${name} (${taxNumber})` : name || taxNumber || id;
      const option = { label, value: id };
      options.push(option);
      addLookupKey(contactUuid, option);
      addLookupKey(providerContactId, option);
      addLookupKey(name, option);
      addLookupKey(label, option);
    });

    return { contactOptions: options, contactOptionLookup: lookup };
  }, [viesContacts]);

  const contactTypeByContactId = React.useMemo(() => {
    const map = new Map<string, string>();
    (viesContacts ?? []).forEach((c) => {
      const contactType = (c.contactType ?? '').trim();
      if (!contactType) return;
      const contactUuid = (c.contactUuid ?? '').trim();
      const providerContactId = (c.providerContactId ?? '').trim();
      if (contactUuid) map.set(contactUuid, contactType);
      if (providerContactId) map.set(providerContactId, contactType);
    });
    return map;
  }, [viesContacts]);

  const findMatchingContactOption = React.useCallback(
    (...values: Array<unknown>) => {
      for (const raw of values) {
        const key = String(raw ?? '')
          .replace(CONTACT_NEW_SUFFIX_REGEX, '')
          .trim();
        if (!key) continue;
        const match = contactOptionLookup.get(key);
        if (match) return match;
      }
      return undefined;
    },
    [contactOptionLookup]
  );

  const formatNewContactLabel = React.useCallback((raw: unknown) => {
    const trimmed = String(raw ?? '')
      .replace(CONTACT_NEW_SUFFIX_REGEX, '')
      .trim();
    if (!trimmed) return '';
    return `${trimmed} (new)`;
  }, []);

  const buildContactOptionsWithCurrent = React.useCallback(
    (sourceType: string | null | undefined, ...values: Array<unknown>) => {
      const matchingContact = findMatchingContactOption(...values);
      const pinnedContactId = matchingContact?.value ?? values[0];
      const filteredContactOptions = filterContactOptionsForSourceType(
        contactOptions,
        contactTypeByContactId,
        sourceType,
        organisation?.providerName,
        pinnedContactId != null ? String(pinnedContactId) : null
      );

      if (matchingContact) {
        return {
          matchingContact,
          options: filteredContactOptions,
          syntheticValue: '',
        };
      }

      const syntheticValue =
        values
          .map((raw) =>
            String(raw ?? '')
              .replace(CONTACT_NEW_SUFFIX_REGEX, '')
              .trim()
          )
          .find(Boolean) ?? '';

      if (!syntheticValue) {
        return {
          matchingContact: undefined,
          options: filteredContactOptions,
          syntheticValue: '',
        };
      }

      return {
        matchingContact: undefined,
        options: [
          {
            value: syntheticValue,
            label: formatNewContactLabel(syntheticValue),
          },
          ...filteredContactOptions,
        ],
        syntheticValue,
      };
    },
    [
      contactOptions,
      contactTypeByContactId,
      organisation?.providerName,
      findMatchingContactOption,
      formatNewContactLabel,
    ]
  );

  const chartOfAccountsForOrganisationSelector = React.useMemo(
    () => getChartOfAccountsForOrganisation(resolvedOrganisationId),
    [resolvedOrganisationId]
  );
  const chartOfAccountsForOrganisation = useSelector(
    chartOfAccountsForOrganisationSelector
  );

  /** Chart of accounts for Account Name dropdown. */
  const organisationChartOfAccounts = (chartOfAccountsForOrganisation ??
    []) as CoaAccountRow[];

  const accountNameByIdMap = React.useMemo(
    () => buildAccountNameByIdMap(organisationChartOfAccounts),
    [organisationChartOfAccounts]
  );

  const coaAccountOptionsUseIds = React.useMemo(
    () => organisationChartOfAccounts.some((row) => !!coaRowId(row)),
    [organisationChartOfAccounts]
  );

  const showAccountCodeInCoaLabels = showsCoaAccountCodeColumn(
    organisation?.providerName
  );

  /** Flat list for account name dropdown ? sorted by name, optional `{code} - {name}` label. */
  const accountNameOptions = React.useMemo(
    () =>
      buildAccountNameOptionsFromCoa(
        organisationChartOfAccounts,
        showAccountCodeInCoaLabels
      ),
    [organisationChartOfAccounts, showAccountCodeInCoaLabels]
  );

  /** Ledger post dropdown: bank accounts only. */
  const ledgerBankAccountOptions = React.useMemo(
    () =>
      buildAccountNameOptionsFromCoa(
        organisationChartOfAccounts.filter(
          (row) =>
            String(row.type ?? '').toUpperCase() === AccountType.BANK
        ),
        showAccountCodeInCoaLabels
      ),
    [organisationChartOfAccounts, showAccountCodeInCoaLabels]
  );

  // Drop a saved Ledger account if it is no longer in the COA bank list.
  React.useEffect(() => {
    if (!ledgerPostAccountId || ledgerBankAccountOptions.length === 0) return;
    const stillValid = ledgerBankAccountOptions.some(
      (opt) => opt.value === ledgerPostAccountId
    );
    if (!stillValid) setLedgerPostAccountId(undefined);
  }, [ledgerBankAccountOptions, ledgerPostAccountId]);

  /** Type grouping removed ? always use flat `accountNameOptions`. */
  const accountNameOptionsGrouped = React.useMemo(
    () =>
      [] as { label: string; options: { label: string; value: string }[] }[],
    []
  );

  const transactionItemOptions = React.useMemo(
    () => buildTransactionItemOptions(organisation?.transactionItems ?? []),
    [organisation?.transactionItems]
  );

  const itemNameByIdMap = React.useMemo(
    () => buildItemNameByIdMap(organisation?.transactionItems ?? []),
    [organisation?.transactionItems]
  );

  const organisationTaxRates =
    organisation?.taxRatesUnique && organisation.taxRatesUnique.length > 0
      ? organisation.taxRatesUnique
      : organisation?.taxRates ?? [];

  const taxNameByIdMap = React.useMemo(
    () => buildTaxNameByIdMap(organisationTaxRates),
    [organisationTaxRates]
  );

  const taxRateByIdMap = React.useMemo(
    () => buildTaxRateByIdMap(organisationTaxRates),
    [organisationTaxRates]
  );

  const taxNameOptions = React.useMemo(
    () => buildTaxNameOptions(organisationTaxRates),
    [organisationTaxRates]
  );

  const taxNameValueOptions = React.useMemo(
    () => buildTaxNameValueOptions(organisationTaxRates),
    [organisationTaxRates]
  );

  const contactNameOptions = React.useMemo(
    () => buildContactNameValueOptions(viesContacts),
    [viesContacts]
  );

  const interpretationRuleFieldOptions = React.useMemo(
    () =>
      buildInterpretationRuleFieldOptions({
        accountNameOptions,
        taxNameOptions: taxNameValueOptions,
        contactNameOptions,
        itemOptions: transactionItemOptions,
        trackingCategories: organisationTrackingCategories,
        showTrackingCategoryFields: shouldIncludeTrackingCategoryFields(
          organisation
        ),
        sourceTypeOptions: getSourceTypeOptions(t),
      }),
    [
      accountNameOptions,
      contactNameOptions,
      organisation,
      organisationTrackingCategories,
      t,
      taxNameValueOptions,
      transactionItemOptions,
    ]
  );

  const handleCreateInterpretationRule = React.useCallback(
    (draft: Partial<InterpretationRuleDraft>) => {
      if (!resolvedOrganisationId) return;
      openInterpretationRuleModalFromDraft({
        dispatch,
        t,
        organisationId: resolvedOrganisationId,
        fieldOptions: interpretationRuleFieldOptions,
        draft,
      });
    },
    [dispatch, interpretationRuleFieldOptions, resolvedOrganisationId, t]
  );

  const handleAfterSuccessfulDrawerSave = React.useCallback(
    (
      payloads: import('./entity-transaction-helpers').TransactionDrawerSavePayload[]
    ) => {
      if (!reviewCaptureRuleCreationEnabled || !resolvedOrganisationId) return;
      for (const payload of payloads) {
        if (!hasRowChanges(payload.draft, payload.original)) continue;
        const suggestion = suggestInterpretationRulesFromEditDiff(
          payload.original,
          { ...payload.original, ...payload.draft }
        );
        if (!suggestion) continue;
        dispatch(
          turnModalOn({
            title: '',
            type: WindowType.WARNING,
            component: (
              <SuggestInterpretationRuleModal
                suggestion={suggestion}
                onCreateRule={() =>
                  handleCreateInterpretationRule(
                    buildDraftFromSuggestion(suggestion)
                  )
                }
              />
            ),
          })
        );
        break;
      }
    },
    [dispatch, handleCreateInterpretationRule, resolvedOrganisationId, reviewCaptureRuleCreationEnabled, t]
  );

  const transactionEditSaveValidation = React.useMemo(
    () => ({
      requireDescription: is3rdPartyProvider,
      useTaxRateIdForTaxName: isAccountIdProvider,
    }),
    [is3rdPartyProvider, isAccountIdProvider]
  );

  const showTransactionEditValidationError = React.useCallback(
    (missingLabels: string[], contextSuffix?: string) => {
      const base = formatTransactionEditValidationMessage(missingLabels);
      if (!base) return;
      dispatch(
        turnMessageOn({
          title: t('organisation.cannot-save-transaction'),
          description: contextSuffix ? `${base} ${contextSuffix}` : base,
          type: MessageStates.WARNING,
        } as MessageData)
      );
    },
    [dispatch, t]
  );

  const showDrawerBulkSaveValidationError = React.useCallback(
    (description: string, lineCount: number) => {
      if (!description.trim()) return;
      dispatch(
        turnMessageOn({
          title:
            lineCount > 1
              ? t('organisation.cannot-save-document')
              : t('organisation.cannot-save-transaction'),
          description,
          type: MessageStates.WARNING,
        } as MessageData)
      );
    },
    [dispatch, t]
  );

  const handleReceiptTaxFieldFocus = React.useCallback(() => {
    notifyReceiptTaxEditWarning({
      enabled: showQuantityUnitPriceColumns,
      dispatch,
      t,
    });
  }, [showQuantityUnitPriceColumns, dispatch, t]);

  const [attachmentsTransactionId, setAttachmentsTransactionId] =
    React.useState<string>(NOT_CLICKED_ATTACHMENT);
  /** Transaction date + number shown in the attachment drawer header. */
  const [attachmentDrawerTransactionRef, setAttachmentDrawerTransactionRef] =
    React.useState<string>('');
  /** Source type of the transaction whose attachment drawer is open. */
  const [attachmentSourceType, setAttachmentSourceType] =
    React.useState<string>('');

  // Chart of accounts for this organisation is fetched once per entity by the
  // parent `OrganisationDetails` component. Here we only consume it via
  // `chartOfAccountsForOrganisation` selector to avoid duplicate API calls.
  const didUploadAttachmentRef = React.useRef(false);
  /** When user opens attachment panel, we open the edit drawer only once attachments exist (or after upload). forceOpenDrawer: when true (e.g. review-mode row), open drawer regardless of switch. */
  const pendingAttachmentEditDrawerRef = React.useRef<{
    transactionId: string;
    original: ValidatedExcelTransaction;
    forceOpenDrawer?: boolean;
    targetFileId?: string;
    targetFileRowCount?: number | null;
    /** Drawer opened from receipts deep link: do not change main file-panel selection. */
    preserveMainFileSelection?: boolean;
    /** File rows loaded for drawer only (main table fetch/selection unchanged). */
    preloadedDrawerTransactions?: ValidatedExcelTransaction[];
  } | null>(null);
  const drawerPreserveMainFileSelectionRef = React.useRef(false);
  const receiptsDeepLinkDrawerOpeningRef = React.useRef(false);
  const pendingDrawerFileNavigationRef = React.useRef<{
    fileId: string;
    targetFileRowCount?: number | null;
  } | null>(null);
  /** Set when we open the edit drawer from attachment flow; when drawer closes (X or mask), close attachment panel too. */
  const editDrawerOpenFromAttachmentRef = React.useRef(false);
  const handlePersistedEditInDrawerChangeRef = React.useRef<
    (nextEditInDrawer: boolean) => void
  >(() => {});
  const onAttachmentClickRef = React.useRef<
    (record: Record<string, unknown>) => void
  >(() => {});
  /** Current page table records for drawer prev/next navigation; updated when allTransactionsTableData is available. */
  const currentPageTableDataRef = React.useRef<Record<string, unknown>[]>([]);
  const drawerDocumentNavigateRef = React.useRef<
    (record: Record<string, unknown>) => void
  >(() => {});

  const lastFetchedAttachments = useSelector(
    getLastFetchedTransactionAttachments
  );
  const attachmentLoading = useSelector(getVatAttachmentLoading);
  const drawerOpen = useSelector(getDrawerOpen);
  const drawerData = useSelector(getDrawerData);
  const transactionsSettingsDrawerOpen =
    drawerOpen && drawerData?.type === DrawerType.CUSTOMIZE_COLUMNS;

  const entityTransactionAttachments = React.useMemo(() => {
    if (attachmentsTransactionId === NOT_CLICKED_ATTACHMENT) return [];
    if (lastFetchedAttachments?.transactionId === attachmentsTransactionId) {
      return lastFetchedAttachments.attachments ?? [];
    }
    return [];
  }, [attachmentsTransactionId, lastFetchedAttachments]);
  const pageSizeSaved = handlePreferences(PreferenceActions.GET, {
    name: PAGE_SIZE_EXCEL_TRANSACTIONS + '-' + organisationId,
  });
  const effectivePageSize =
    typeof pageSizeSaved === 'number' && Number.isFinite(pageSizeSaved)
      ? pageSizeSaved
      : DEFAULT_PAGE_SIZE;

  /** Mirrored preference; synced from storage on entity change and when the settings drawer switch updates. */
  const [editInDrawerRaw, setEditInDrawer] = React.useState(false);
  // On PWA mobile there is no inline table, so always use the drawer for edits.
  const editInDrawer = pwaMobileNav ? true : editInDrawerRaw;
  const editInDrawerRef = React.useRef(editInDrawer);
  editInDrawerRef.current = editInDrawer;
  const [reviewDrawerRelaunchKey, setReviewDrawerRelaunchKey] =
    React.useState(0);

  React.useEffect(() => {
    if (!resolvedOrganisationId?.trim()) {
      setEditInDrawer(false);
      return;
    }
    setEditInDrawer(
      resolveEditInDrawerFromPreferences(
        handlePreferences,
        resolvedOrganisationId
      )
    );
    // Only re-load when the entity id changes ? not on every parent render (handlePreferences identity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedOrganisationId]);

  const [selectedImportIds, setSelectedImportIds] = React.useState<string[]>(
    []
  );

  /** Mass update: when true, table shows checkboxes and selection bar with bulk field update. */
  const [massUpdateMode, setMassUpdateMode] = React.useState(false);
  const [showDeletedTransactions, setShowDeletedTransactions] =
    React.useState(false);
  const [visibleColumns, setVisibleColumns] = React.useState<string[] | null>(
    null
  );
  const [lockedColumns, setLockedColumns] = React.useState<string[]>([]);
  const [columnOrder, setColumnOrder] = React.useState<string[] | undefined>(
    undefined
  );
  const [historyModalOpen, setHistoryModalOpen] = React.useState(false);
  const [historyTransactionUuid, setHistoryTransactionUuid] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    if (!supportsDeletedAndHistory) {
      setShowDeletedTransactions(false);
      setHistoryModalOpen(false);
      setHistoryTransactionUuid(null);
    }
  }, [supportsDeletedAndHistory]);
  const [massUpdateSelectedRowKeys, setMassUpdateSelectedRowKeys] =
    React.useState<React.Key[]>([]);
  const [massUpdateField, setMassUpdateField] = React.useState<string>('');
  const [massUpdateValue, setMassUpdateValue] =
    React.useState<unknown>(undefined);
  const [massUpdateApplying, setMassUpdateApplying] = React.useState(false);
  const [transactionsExporting, setTransactionsExporting] =
    React.useState(false);
  const bulkUpdateSuccessCountRef = React.useRef(0);

  // Keep track of current table state so we can re-fetch when file-selection changes
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] =
    React.useState(effectivePageSize);
  const [inlineEditFilePageSizeOverride, setInlineEditFilePageSizeOverride] =
    React.useState<number | null>(null);
  const activeTablePageSize =
    inlineEditFilePageSizeOverride ?? currentPageSize ?? effectivePageSize;
  const paginationSummaryOverride = React.useMemo(
    () =>
      inlineEditFilePageSizeOverride != null
        ? t('organisation.transactions-viewing-n-results', {
            count: formatDisplayInteger(
              inlineEditFilePageSizeOverride,
              i18n.language
            ),
          })
        : undefined,
    [inlineEditFilePageSizeOverride, i18n.language, t]
  );
  const [currentFiltersState, setCurrentFiltersState] = React.useState<
    Record<string, unknown> | undefined
  >(undefined);
  const handleUploadAttachment = React.useCallback(
    (file: File) => {
      if (
        !attachmentsTransactionId ||
        attachmentsTransactionId === NOT_CLICKED_ATTACHMENT ||
        !connectionId
      )
        return;
      didUploadAttachmentRef.current = true;
      const providerName = organisation?.providerName ?? '';
      dispatch(
        uploadTransactionAttachmentRequest(
          connectionId,
          attachmentsTransactionId,
          attachmentsTransactionId,
          providerName,
          file,
          attachmentSourceType
        )
      );
    },
    [
      connectionId,
      attachmentsTransactionId,
      attachmentSourceType,
      organisation?.providerName,
      dispatch,
    ]
  );

  const onCloseAttachments = React.useCallback(() => {
    setAttachmentsTransactionId(NOT_CLICKED_ATTACHMENT);
    setAttachmentSourceType('');
    pendingAttachmentEditDrawerRef.current = null;
  }, []);

  const excelFiles: ExcelFileMetadata[] = React.useMemo(
    () =>
      (organisation?.excelFilesMetadata ?? []).filter(
        (file) =>
          file.status === UploadStatus.ACTIVE &&
          (file.totalRows == null || Number(file.totalRows) > 0)
      ),
    [organisation?.excelFilesMetadata]
  );

  const resetInlineEditFilePageSizeOverride = React.useCallback(() => {
    setInlineEditFilePageSizeOverride(null);
  }, []);

  const clearTemporaryFileEditSelection = React.useCallback(() => {
    resetInlineEditFilePageSizeOverride();
    // Keep the file-panel selection so the table stays on the invoice that was
    // open in the review drawer (instead of snapping back to all tab files).
    drawerPreserveMainFileSelectionRef.current = false;
  }, [resetInlineEditFilePageSizeOverride]);

  const resolveInlineEditFileRowCount = React.useCallback(
    (excelMetadataId: string | null | undefined) => {
      const normalizedId = String(excelMetadataId ?? '').trim();
      if (!normalizedId) return null;
      const file = excelFiles.find(
        (f) => String(f.excelMetadataId ?? '').trim() === normalizedId
      );
      const totalRows = Number(file?.totalRows);
      return Number.isFinite(totalRows) && totalRows > 0 ? totalRows : null;
    },
    [excelFiles]
  );

  const preparePendingAttachmentEditDrawer = React.useCallback(
    (original: ValidatedExcelTransaction, forceOpenDrawer: boolean) => {
      const pending: {
        transactionId: string;
        original: ValidatedExcelTransaction;
        forceOpenDrawer?: boolean;
        targetFileId?: string;
        targetFileRowCount?: number | null;
      } = {
        transactionId: String(original.uuid ?? ''),
        original,
        forceOpenDrawer,
      };

      if (!forceOpenDrawer || isExcelProvider) {
        return pending;
      }

      const fileId = getTransactionFileFilterId(original, excelFiles);
      const fileRowCount = resolveInlineEditFileRowCount(fileId);

      if (!fileId) {
        resetInlineEditFilePageSizeOverride();
        return pending;
      }

      pending.targetFileId = fileId;
      pending.targetFileRowCount = fileRowCount;

      setSelectedImportIds([fileId]);
      setCurrentPage(1);

      return pending;
    },
    [
      isExcelProvider,
      excelFiles,
      resolveInlineEditFileRowCount,
      resetInlineEditFilePageSizeOverride,
    ]
  );

  const loadAllDocumentTransactionsForDrawer = React.useCallback(
    async (
      original: ValidatedExcelTransaction
    ): Promise<ValidatedExcelTransaction[]> => {
      const fileId = getTransactionFileFilterId(original, excelFiles);
      if (!fileId || !organisationId) {
        return collectTransactionsSameDocument(
          currentPageTableDataRef.current,
          original
        );
      }
      const rows = await fetchAllTransactionsForDocumentFile({
        organisationId,
        expectedRowCount: resolveInlineEditFileRowCount(fileId),
        apiFilters: convertScopeTableFiltersToApiBody(
          buildTransactionsRefreshFilters({
            tableFilters: {},
            tabFileImportIds: [fileId],
          })
        ),
      });
      const grouped = collectTransactionsSameDocument(
        rows.map((row) => ({ __transactionRow: row })),
        original
      );
      return grouped.length ? grouped : [original];
    },
    [excelFiles, organisationId, resolveInlineEditFileRowCount]
  );

  const openEditDrawerForTransaction = React.useCallback(
    async (original: ValidatedExcelTransaction) => {
      const uuid = String(original.uuid ?? '').trim();
      if (!uuid || !connectionId) return;

      const pending = preparePendingAttachmentEditDrawer(original, true);
      pendingAttachmentEditDrawerRef.current = pending;

      if (!isExcelProvider) {
        try {
          const preloaded = await loadAllDocumentTransactionsForDrawer(original);
          const current = pendingAttachmentEditDrawerRef.current;
          if (!current || current.transactionId !== pending.transactionId) {
            return;
          }
          pendingAttachmentEditDrawerRef.current = {
            ...current,
            preloadedDrawerTransactions: preloaded,
          };
        } catch {
          const current = pendingAttachmentEditDrawerRef.current;
          if (!current || current.transactionId !== pending.transactionId) {
            return;
          }
          pendingAttachmentEditDrawerRef.current = {
            ...current,
            preloadedDrawerTransactions: collectTransactionsSameDocument(
              currentPageTableDataRef.current,
              original
            ),
          };
        }
      }

      const sourceType = String(original.sourceType ?? '');
      const rawDate = original.transactionDate;
      const date =
        rawDate != null &&
        (typeof rawDate === 'string' ||
          typeof rawDate === 'number' ||
          rawDate instanceof Date)
          ? formatTransactionTableDate(rawDate, i18n.language)
          : '';
      const num =
        original.transactionNumber != null
          ? String(original.transactionNumber).trim()
          : '';
      setAttachmentDrawerTransactionRef(
        [date, num].filter(Boolean).join(' · ') || ''
      );
      setAttachmentSourceType(sourceType);
      setAttachmentsTransactionId(uuid);
      dispatch(
        fetchTransactionAttachmentRequest(
          connectionId,
          uuid,
          uuid,
          organisation?.providerName ?? '',
          sourceType,
          true
        )
      );
    },
    [
      connectionId,
      dispatch,
      i18n.language,
      isExcelProvider,
      loadAllDocumentTransactionsForDrawer,
      organisation?.providerName,
      preparePendingAttachmentEditDrawer,
    ]
  );

  const navigateDrawerToFile = React.useCallback(
    async (fileId: string) => {
      const normalizedFileId = String(fileId ?? '').trim();
      if (!normalizedFileId || !organisationId) return;

      setSelectedImportIds((prev) =>
        prev.length === 1 && prev[0] === normalizedFileId
          ? prev
          : [normalizedFileId]
      );
      setCurrentPage((prev) => (prev === 1 ? prev : 1));

      try {
        const rows = await fetchAllTransactionsForDocumentFile({
          organisationId,
          expectedRowCount: resolveInlineEditFileRowCount(normalizedFileId),
          apiFilters: convertScopeTableFiltersToApiBody(
            buildTransactionsRefreshFilters({
              tableFilters: {},
              tabFileImportIds: [normalizedFileId],
            })
          ),
        });
        const original = rows[0];
        if (!original) return;
        await openEditDrawerForTransaction(original);
      } catch {
        const matching = currentPageTableDataRef.current.find((record) => {
          const row = record.__transactionRow as
            | ValidatedExcelTransaction
            | undefined;
          return (
            getTransactionFileFilterId(row, excelFiles) === normalizedFileId
          );
        });
        const original = matching?.__transactionRow as
          | ValidatedExcelTransaction
          | undefined;
        if (original) {
          await openEditDrawerForTransaction(original);
        }
      }
    },
    [
      excelFiles,
      openEditDrawerForTransaction,
      organisationId,
      resolveInlineEditFileRowCount,
    ]
  );

  const onAttachmentClick = React.useCallback(
    (record: Record<string, unknown>) => {
      const original = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (original && editInDrawerRef.current) {
        void openEditDrawerForTransaction(original);
        return;
      }

      const uuid = record.__transactionUuid ?? record.uuid;
      if (typeof uuid !== 'string' || !connectionId) return;
      setAttachmentsTransactionId(uuid);
      const sourceType =
        typeof record.sourceType === 'string' ? record.sourceType : '';
      setAttachmentSourceType(sourceType);
      const rawDate = record.transactionDate;
      const date =
        rawDate != null &&
        (typeof rawDate === 'string' ||
          typeof rawDate === 'number' ||
          rawDate instanceof Date)
          ? formatTransactionTableDate(rawDate, i18n.language)
          : '';
      const num =
        record.transactionNumber != null
          ? String(record.transactionNumber).trim()
          : '';
      setAttachmentDrawerTransactionRef(
        [date, num].filter(Boolean).join(' ? ') || ''
      );
      const providerName = organisation?.providerName ?? '';
      dispatch(
        fetchTransactionAttachmentRequest(
          connectionId,
          uuid,
          uuid,
          providerName,
          sourceType,
          true
        )
      );

      if (original) {
        pendingAttachmentEditDrawerRef.current =
          preparePendingAttachmentEditDrawer(original, editInDrawerRef.current);
      }
    },
    [
      connectionId,
      organisation?.providerName,
      dispatch,
      openEditDrawerForTransaction,
      preparePendingAttachmentEditDrawer,
    ]
  );

  // Fallback: on hard refresh, ensure file metadata is fetched for this org
  // even if parent-level timing misses it. Wait until the organisation is
  // actually present in the store ? the metadata success reducer needs to
  // merge into the org list, and dispatching before hydration silently drops
  // the response. `excelFilesMetadata !== undefined` is the "loaded" signal;
  // an empty array is a legitimate "no files" result and must not refetch.
  React.useEffect(() => {
    if (!resolvedOrganisationId) return;
    if (!organisation) return;
    if (organisation.excelFilesMetadata !== undefined) return;
    if (shouldSkipExcelFilesMetadataBurst(resolvedOrganisationId)) return;
    dispatch(fetchExcelFilesMetadataRequest(resolvedOrganisationId));
  }, [
    dispatch,
    resolvedOrganisationId,
    organisation,
    organisation?.excelFilesMetadata,
  ]);

  // Fallback: ensure chart of accounts are loaded for the Account Name dropdown.
  // Parent OrganisationDetails dispatches this on entity entry, but if the user
  // lands directly on the Transactions tab on a hard refresh and the parent
  // effect was somehow skipped, this catches the gap. The burst dedupe in the
  // shared guard prevents a duplicate dispatch with the parent.
  React.useEffect(() => {
    if (!resolvedOrganisationId) return;
    if (!organisation) return;
    if (!shouldFetchOrganisationProviderData(organisation)) return;
    if (
      organisation.chartOfAccountsLoaded ||
      (Array.isArray(organisation.chartOfAccounts) &&
        organisation.chartOfAccounts.length > 0)
    ) {
      return;
    }
    if (shouldSkipChartOfAccountsBurst(resolvedOrganisationId)) return;
    dispatch(
      fetchChartOfAccountsForOrganisationRequest(resolvedOrganisationId)
    );
  }, [
    dispatch,
    resolvedOrganisationId,
    organisation,
    organisation?.chartOfAccountsLoaded,
    organisation?.chartOfAccounts,
  ]);

  // Fallback: ensure contacts are loaded for the Name column dropdown. Uses a
  // per-tab ref keyed by connectionId so quick remounts don't double-fetch
  // before the response lands (the slice is global, not per-org).
  const contactsTabFetchedRef = React.useRef<Set<string>>(new Set<string>());
  React.useEffect(() => {
    if (!connectionId) return;
    if (
      organisationViesContactsSlice &&
      organisationViesContactsSlice.connectionId === connectionId
    ) {
      return;
    }
    if (contactsTabFetchedRef.current.has(connectionId)) return;
    contactsTabFetchedRef.current.add(connectionId);
    dispatch(fetchOrganisationContactsRequest(connectionId));
  }, [dispatch, connectionId, organisationViesContactsSlice]);

  const navigateDrawerToRecord = React.useCallback(
    (record: Record<string, unknown>) => {
      const row = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (!row) return;
      void openEditDrawerForTransaction(row);
    },
    [openEditDrawerForTransaction]
  );

  React.useLayoutEffect(() => {
    onAttachmentClickRef.current = onAttachmentClick;
  }, [onAttachmentClick]);

  React.useLayoutEffect(() => {
    drawerDocumentNavigateRef.current = navigateDrawerToRecord;
  }, [navigateDrawerToRecord]);

  React.useEffect(() => {
    const pending = pendingDrawerFileNavigationRef.current;
    if (!pending) return;

    const isTargetFileSelected =
      selectedImportIds.length === 1 && selectedImportIds[0] === pending.fileId;
    if (!isTargetFileSelected) return;

    const matchingRecords = currentPageTableDataRef.current.filter((record) => {
      const row = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      return getTransactionFileFilterId(row, excelFiles) === pending.fileId;
    });
    const fetchedAtFullFilePageSize =
      pending.targetFileRowCount != null &&
      inlineEditFilePageSizeOverride != null &&
      inlineEditFilePageSizeOverride >= pending.targetFileRowCount;
    const fullFileLoaded = isDocumentFileFullyLoadedForEdit(
      matchingRecords.length,
      pending.targetFileRowCount,
      { fetchedAtFullFilePageSize }
    );
    if (!fullFileLoaded || matchingRecords.length === 0) return;

    pendingDrawerFileNavigationRef.current = null;
    const firstRecord = matchingRecords[0];
    const original = firstRecord.__transactionRow as
      | ValidatedExcelTransaction
      | undefined;
    onAttachmentClickRef.current(firstRecord);
    if (original) {
      pendingAttachmentEditDrawerRef.current =
        preparePendingAttachmentEditDrawer(original, true);
    }
    if (rescanReopenNavigationForFileRef.current) {
      rescanReopenNavigationForFileRef.current = null;
    }
  }, [
    allExcelTransactionsPage,
    selectedImportIds,
    excelFiles,
    inlineEditFilePageSizeOverride,
    preparePendingAttachmentEditDrawer,
  ]);

  // When the drawer is closed (X or mask click), close the attachment viewer too if we had opened the edit drawer from the attachment flow.
  const prevDrawerOpenRef = React.useRef(drawerOpen);
  React.useEffect(() => {
    const wasOpen = prevDrawerOpenRef.current;
    prevDrawerOpenRef.current = drawerOpen;
    if (wasOpen && !drawerOpen && editDrawerOpenFromAttachmentRef.current) {
      editDrawerOpenFromAttachmentRef.current = false;
      pendingDrawerFileNavigationRef.current = null;
      skipNextFetchRef.current = false;
      clearTemporaryFileEditSelection();
      setAttachmentsTransactionId(NOT_CLICKED_ATTACHMENT);
      setAttachmentSourceType('');
    }
  }, [drawerOpen, clearTemporaryFileEditSelection]);

  // Open-drawer saved-id hops only live for the EDIT drawer session.
  const prevEditDrawerOpenRef = React.useRef(false);
  React.useEffect(() => {
    const isEditDrawerOpen =
      Boolean(drawerOpen) && drawerData?.type === DrawerType.EDIT;
    if (prevEditDrawerOpenRef.current && !isEditDrawerOpen) {
      dispatch(clearDrawerTransactionIdMap());
    }
    prevEditDrawerOpenRef.current = isEditDrawerOpen;
  }, [drawerOpen, drawerData?.type, dispatch]);

  const savePageSize = (pageSize: number) => {
    handlePreferences(PreferenceActions.SET, {
      name: PAGE_SIZE_EXCEL_TRANSACTIONS + '-' + organisationId,
      value: pageSize,
    });
  };

  const [fileSearchQuery, setFileSearchQuery] = React.useState('');
  const { appliedRange: fileDateAppliedRange, filterProps: fileDateFilterProps } =
    useDrawerDateRangeFilter({
      defaultOption: DateOptions.ALL,
      enableAutoFallback: false,
      items: excelFiles,
    });
  /** File counts per state tab ? matches the Transactions side panel file list. */
  const tabViewFileCounts = React.useMemo(() => {
    const counts = computeCaptureFileCountsFromMetadata(
      excelFiles,
      organisation?.providerName
    );
    return {
      CAPTURED: counts?.pendingReviewFiles ?? 0,
      REVIEWED: counts?.readyToPostFiles ?? 0,
    };
  }, [excelFiles, organisation?.providerName]);
  /** Sum `totalRows` from file metadata per state tab (transactions in each view). */
  const tabViewTransactionTotals = React.useMemo(() => {
    const totals = { CAPTURED: 0, REVIEWED: 0, POSTED: 0 };
    for (const file of excelFiles) {
      const rows =
        typeof file.totalRows === 'number'
          ? file.totalRows
          : Number(file.totalRows) || 0;
      const isReviewed =
        file.isReviewed === true ||
        (file as { isReviwed?: boolean }).isReviwed === true;

      if (isReceiptsOnlyProvider) {
        if (isReviewed) totals.REVIEWED += rows;
        else totals.CAPTURED += rows;
        continue;
      }

      if (file.isPosted === true) {
        totals.POSTED += rows;
        continue;
      }

      if (isReviewed) totals.REVIEWED += rows;
      else totals.CAPTURED += rows;
    }
    return totals;
  }, [excelFiles, isReceiptsOnlyProvider]);

  const getFileIdsForStateTab = React.useCallback(
    (tab: 'CAPTURED' | 'REVIEWED' | 'POSTED') => {
      if (isExcelProvider) {
        return excelFiles
          .map((file) => String(file.excelMetadataId ?? '').trim())
          .filter(Boolean);
      }

      return excelFiles
        .filter((file) => {
          const isReviewed =
            file.isReviewed === true ||
            (file as { isReviwed?: boolean }).isReviwed === true;

          if (isReceiptsOnlyProvider) {
            return tab === 'REVIEWED' ? isReviewed : !isReviewed;
          }

          if (tab === 'POSTED') {
            return file.isPosted === true;
          }

          if (file.isPosted === true) return false;
          return tab === 'REVIEWED' ? isReviewed : !isReviewed;
        })
        .map((file) => String(file.excelMetadataId ?? '').trim())
        .filter(Boolean);
    },
    [excelFiles, isExcelProvider, isReceiptsOnlyProvider]
  );
  const activeTabFileIds = React.useMemo(
    () => getFileIdsForStateTab(rightPanelTransactionTab),
    [getFileIdsForStateTab, rightPanelTransactionTab]
  );
  const excelFilesMetadataSignature = React.useMemo(
    () =>
      JSON.stringify(
        excelFiles.map((file) => ({
          excelMetadataId: String(file.excelMetadataId ?? '').trim(),
          isReviewed:
            file.isReviewed === true ||
            (file as { isReviwed?: boolean }).isReviwed === true,
          isPosted: file.isPosted === true,
        }))
      ),
    [excelFiles]
  );
  const [
    pendingTabReapplyAfterReviewSignature,
    setPendingTabReapplyAfterReviewSignature,
  ] = React.useState<string | null>(null);
  const queueActiveTabRefreshAfterReview = React.useCallback(() => {
    if (!showFileStateTabs) return;
    setPendingTabReapplyAfterReviewSignature(excelFilesMetadataSignature);
  }, [showFileStateTabs, excelFilesMetadataSignature]);
  const effectiveSelectedImportIds = React.useMemo(() => {
    if (!showFileStateTabs) return selectedImportIds;
    if (selectedImportIds.length === 0) return activeTabFileIds;

    const tabFileIdsSet = new Set(activeTabFileIds);
    const selectedInActiveTab = selectedImportIds.filter((id) =>
      tabFileIdsSet.has(id)
    );
    return selectedInActiveTab.length > 0
      ? selectedInActiveTab
      : activeTabFileIds;
  }, [activeTabFileIds, selectedImportIds, showFileStateTabs]);
  const requestTabFileImportIds = React.useMemo(
    () =>
      resolveTabFileImportIdsForRequest({
        showFileStateTabs,
        effectiveSelectedImportIds,
        files: excelFiles,
      }),
    [showFileStateTabs, effectiveSelectedImportIds, excelFiles]
  );
  const handleFileStateTabChange = React.useCallback(
    (nextTab: 'CAPTURED' | 'REVIEWED' | 'POSTED') => {
      // Allow the selection/tab fetch effect to run — a prior save can leave
      // skipNextFetchRef stuck true when selection was preserved.
      skipNextFetchRef.current = false;
      setRightPanelTransactionTab(nextTab);
      // On tab change, clear explicit selections; filtering still treats empty as
      // "all files in active tab" via effectiveSelectedImportIds.
      setSelectedImportIds([]);
      setSelectedUploadFileIds([]);
      // Batch "Select document" mode (third-party uploads): helpful default on Reviewed.
      setUploadSelectionMode(nextTab === 'REVIEWED' && is3rdPartyProvider);
      setCurrentPage(1);
      if (nextTab === 'POSTED') {
        setMassUpdateMode(false);
        setMassUpdateSelectedRowKeys([]);
        setMassUpdateField('');
        setMassUpdateValue(undefined);
      }
    },
    [is3rdPartyProvider]
  );
  const filteredExcelFiles = React.useMemo(() => {
    const filesByStatus = isExcelProvider
      ? excelFiles
      : excelFiles.filter((file) => {
          const isReviewed =
            file.isReviewed === true ||
            (file as { isReviwed?: boolean }).isReviwed === true;

          if (isReceiptsOnlyProvider) {
            if (rightPanelTransactionTab === 'REVIEWED') {
              return isReviewed;
            }
            return !isReviewed;
          }

          if (rightPanelTransactionTab === 'POSTED') {
            return file.isPosted === true;
          }

          if (file.isPosted === true) {
            return false;
          }

          if (rightPanelTransactionTab === 'REVIEWED') {
            return isReviewed;
          }
          return !isReviewed;
        });

    const dateFilteredFiles = filesByStatus.filter((file) =>
      isUploadDateInRange(file.uploadDate, fileDateAppliedRange)
    );

    let next = dateFilteredFiles;
    if (fileSearchQuery.trim()) {
      const q = fileSearchQuery.trim().toLowerCase();
      next = next.filter((f) => (f.fileName ?? '').toLowerCase().includes(q));
    }

    if (pwaMobileNav) {
      const sorted = [...next].sort((a, b) => {
        const aTime = Number(a.uploadDate) || 0;
        const bTime = Number(b.uploadDate) || 0;
        return pwaReviewSort === 'oldest' ? aTime - bTime : bTime - aTime;
      });
      return sorted;
    }

    return next;
  }, [
    excelFiles,
    fileSearchQuery,
    fileDateAppliedRange,
    isExcelProvider,
    isReceiptsOnlyProvider,
    rightPanelTransactionTab,
    pwaMobileNav,
    pwaReviewSort,
  ]);

  const executeRescanDrawerReopen = React.useCallback(() => {
    const pending = pendingRescanDrawerReopenRef.current;
    if (!pending?.ready) return;

    if (drawerOpen && drawerData?.type === DrawerType.EDIT) {
      pendingRescanDrawerReopenRef.current = null;
      rescanReopenNavigationForFileRef.current = null;
      return;
    }

    // Wait until the side panel list actually refreshed (signature changed) and
    // the rescan finished (no file still processing), otherwise we'd open the
    // previous top file. Keep the pending entry so this retries once the
    // refreshed metadata arrives.
    const metadata = organisation?.excelFilesMetadata ?? [];
    if (hasProcessingSidePanelFile(metadata)) return;
    const signatureChanged =
      pending.preRescanSignature == null ||
      computeSidePanelSignature(metadata) !== pending.preRescanSignature;
    if (!signatureChanged) return;

    const firstFileId = resolveFirstSidePanelFileId(metadata, {
      tab: 'CAPTURED',
      isReceiptsOnlyProvider,
      isExcelProvider,
    });
    if (!firstFileId) return;

    if (rescanReopenNavigationForFileRef.current === firstFileId) {
      return;
    }

    pendingRescanDrawerReopenRef.current = null;
    rescanReopenNavigationForFileRef.current = firstFileId;

    if (rightPanelTransactionTab !== 'CAPTURED') {
      setRightPanelTransactionTab('CAPTURED');
    }
    setFileListCurrentPage(1);
    navigateDrawerToFile(firstFileId);
  }, [
    organisation?.excelFilesMetadata,
    isReceiptsOnlyProvider,
    isExcelProvider,
    drawerOpen,
    drawerData?.type,
    navigateDrawerToFile,
    rightPanelTransactionTab,
  ]);

  React.useEffect(() => {
    onRegisterRescanDrawerReopen?.(executeRescanDrawerReopen);
    return () => onRegisterRescanDrawerReopen?.(null);
  }, [onRegisterRescanDrawerReopen, executeRescanDrawerReopen]);

  React.useEffect(() => {
    if (!rescanDrawerReopenSignal) return;
    executeRescanDrawerReopen();
  }, [
    rescanDrawerReopenSignal,
    organisation?.excelFilesMetadata,
    executeRescanDrawerReopen,
  ]);

  React.useEffect(() => {
    if (
      (isExcelProvider || isReceiptsOnlyProvider) &&
      rightPanelTransactionTab === 'POSTED'
    ) {
      setRightPanelTransactionTab('CAPTURED');
    }
  }, [isExcelProvider, isReceiptsOnlyProvider, rightPanelTransactionTab]);

  const sanitizeTableManagedFilters = React.useCallback(
    (filters?: Record<string, unknown>) => {
      if (!filters) return filters;
      const next: Record<string, unknown> = { ...filters };
      delete (next as any).excelMetadataId;
      delete (next as any).importId;
      delete (next as any).reviewStatus;
      delete (next as any).reversalType;
      return next;
    },
    []
  );

  React.useEffect(() => {
    if (!pendingTabReapplyAfterReviewSignature) return;
    if (!showFileStateTabs) {
      setPendingTabReapplyAfterReviewSignature(null);
      return;
    }
    if (organisation?.excelFilesMetadata == null) return;
    if (excelFilesMetadataSignature === pendingTabReapplyAfterReviewSignature) {
      return;
    }

    setPendingTabReapplyAfterReviewSignature(null);
    // Keep invoice selection when the file is still in this tab; otherwise
    // fall back to all files in the active tab (file may have moved tabs).
    const tabFileIdsSet = new Set(activeTabFileIds);
    const preservedSelection = selectedImportIds.filter((id) =>
      tabFileIdsSet.has(id)
    );
    setSelectedImportIds(preservedSelection);
    if (!organisationId) return;

    const page = currentPage || 1;
    const pageSize = currentPageSize ?? effectivePageSize;
    const refreshFileIds =
      preservedSelection.length > 0 ? preservedSelection : activeTabFileIds;
    const baseRefreshFilters = buildTransactionsRefreshFilters({
      tableFilters: sanitizeTableManagedFilters(currentFiltersState),
      tabFileImportIds: refreshFileIds,
    });
    const refreshFilters = applySelectTransactionUuidApiFilter
      ? appendSelectTransactionUuidFilter(
          baseRefreshFilters,
          selectTransactionFilterId!
        )
      : baseRefreshFilters;
    // Only skip the selection-driven fetch when selection actually changes
    // (would otherwise double-fetch). If selection is unchanged, do not leave
    // skipNextFetchRef stuck — that blocks later tab clicks.
    if (preservedSelection.length !== selectedImportIds.length) {
      skipNextFetchRef.current = true;
    }
    dispatch(
      fetchAllExcelTransactionsRequest(
        organisationId,
        page,
        pageSize,
        refreshFilters
      )
    );
  }, [
    pendingTabReapplyAfterReviewSignature,
    showFileStateTabs,
    organisation?.excelFilesMetadata,
    excelFilesMetadataSignature,
    organisationId,
    currentPage,
    currentPageSize,
    effectivePageSize,
    currentFiltersState,
    sanitizeTableManagedFilters,
    activeTabFileIds,
    selectedImportIds,
    dispatch,
    selectTransactionFilterId,
    applySelectTransactionUuidApiFilter,
  ]);

  const {
    setContainerNode: setFileListContainerNode,
    setItemNode: setFileListItemNode,
    setPagerNode: setFileListPagerNode,
    pageSize: fittedFileListPageSize,
    remeasure: remeasureFileListPageSize,
  } = useFitItemsPerPage({
    itemCount: filteredExcelFiles.length,
    gap: 8,
    minPageSize: 1,
    defaultPageSize: 1,
    fallbackItemHeight: 70,
  });
  const fileListPageSize = pwaMobileNav
    ? PWA_FILE_LIST_PAGE_SIZE
    : fittedFileListPageSize;
  const [fileListCurrentPage, setFileListCurrentPage] = React.useState(1);
  React.useEffect(() => {
    setFileListCurrentPage(1);
  }, [
    fileSearchQuery,
    rightPanelTransactionTab,
    fileDateAppliedRange,
    organisationId,
  ]);
  React.useEffect(() => {
    if (pwaMobileNav) return;
    remeasureFileListPageSize();
  }, [filePanelFixedTopPx, organisationId, pwaMobileNav, remeasureFileListPageSize]);
  const fileListTotalPages = Math.max(
    1,
    Math.ceil(filteredExcelFiles.length / fileListPageSize)
  );
  React.useEffect(() => {
    setFileListCurrentPage((currentPage) =>
      Math.min(currentPage, fileListTotalPages)
    );
  }, [fileListTotalPages]);
  React.useEffect(() => {
    if (!pwaMobileNav) return;
    document
      .querySelector('.entity-details-tab-panel')
      ?.scrollTo({ top: 0 });
  }, [
    fileListCurrentPage,
    pwaMobileNav,
    rightPanelTransactionTab,
    fileSearchQuery,
  ]);
  /** PWA Review uses cumulative "Load more"; desktop file panel stays page-windowed. */
  const displayedFiles = pwaMobileNav
    ? filteredExcelFiles.slice(0, fileListCurrentPage * fileListPageSize)
    : filteredExcelFiles.slice(
        (fileListCurrentPage - 1) * fileListPageSize,
        fileListCurrentPage * fileListPageSize
      );
  const pwaReviewHasMore =
    pwaMobileNav && displayedFiles.length < filteredExcelFiles.length;

  const pwaReviewQueue: PwaReviewQueue =
    rightPanelTransactionTab === 'REVIEWED'
      ? 'post'
      : rightPanelTransactionTab === 'POSTED'
      ? 'posted'
      : 'review';

  const handlePwaReviewQueueChange = React.useCallback(
    (queue: PwaReviewQueue) => {
      handleFileStateTabChange(
        queue === 'post'
          ? 'REVIEWED'
          : queue === 'posted'
          ? 'POSTED'
          : 'CAPTURED'
      );
    },
    [handleFileStateTabChange]
  );

  const [editingTransactionId, setEditingTransactionId] = React.useState<
    string | null
  >(null);
  /** Unsaved client-only row from "Add a Transaction" (not yet POSTed). */
  const [pendingManualEntry, setPendingManualEntry] =
    React.useState<ValidatedExcelTransaction | null>(null);
  const [editingDraft, setEditingDraft] =
    React.useState<Partial<ValidatedExcelTransaction> | null>(null);
  /** Which columns are currently editable. '__all__' = full-row edit (edit icon). */
  const [editingColumnKeys, setEditingColumnKeys] =
    React.useState<Set<string> | null>(null);

  /** Non-Excel: inline edit for all lines from the same import file on the current table page. */
  const [fileGroupEditSession, setFileGroupEditSession] = React.useState<{
    seedUuid: string;
    uuids: string[];
  } | null>(null);
  const [fileGroupDraftMap, setFileGroupDraftMap] = React.useState<
    Record<string, Partial<ValidatedExcelTransaction>>
  >({});
  const fileGroupEditSeedRef = React.useRef<ValidatedExcelTransaction | null>(
    null
  );
  const fileGroupEditColumnKeysRef = React.useRef<Set<string>>(
    new Set(['__all__'])
  );
  const pendingFileGroupEditRef = React.useRef(false);

  const isInlineFileGroupEditing = fileGroupEditSession != null;

  const clearFileGroupEdit = React.useCallback(() => {
    setFileGroupEditSession(null);
    setFileGroupDraftMap({});
    fileGroupEditSeedRef.current = null;
    pendingFileGroupEditRef.current = false;
  }, []);

  /** Exit inline table edit and clear file filter when switching to drawer view. */
  const clearInlineTableEditState = React.useCallback(() => {
    resetInlineEditFilePageSizeOverride();
    clearFileGroupEdit();
    resetInlineEditFilePageSizeOverride();
    setEditingTransactionId(null);
    setEditingDraft(null);
    setEditingColumnKeys(null);
    setPendingManualEntry(null);
    setPendingRowSwitch(null);
    setSelectedImportIds([]);
    setCurrentPage(1);
  }, [clearFileGroupEdit, resetInlineEditFilePageSizeOverride]);

  /** Record pending delete confirmation (null = modal closed). */
  const [deleteTransactionRecord, setDeleteTransactionRecord] =
    React.useState<Record<string, unknown> | null>(null);

  /** Import ID pending delete confirmation for filter-by-file (null = modal closed). */
  const [deleteFileImportId, setDeleteFileImportId] = React.useState<
    string | null
  >(null);

  /** Import ID pending rescan confirmation for filter-by-file (null = modal closed). */
  const [rescanFileImportId, setRescanFileImportId] = React.useState<
    string | null
  >(null);

  const preferencesHook = usePreferences();
  const { formatNumber, locale } = useNumberFormat();
  const currentUser = useSelector(getAuthUser);
  const store = useStore<StoreState>();

  const setEntityPreferencesBatch = React.useCallback(
    (updates: Preference[]) => {
      setPreferencesInSection(
        PreferenceSections.ENTITIES,
        updates,
        currentUser,
        dispatch,
        () => getStoredPreferenceDetails(store.getState())
      );
    },
    [currentUser, dispatch, store]
  );

  const fileFilterTourTargetRef = React.useRef<HTMLDivElement | null>(null);
  const uploadButtonTourTargetRef = React.useRef<HTMLSpanElement>(null);
  const editButtonTourTargetRef = React.useRef<HTMLDivElement | null>(null);
  const massUpdateButtonTourTargetRef = React.useRef<HTMLDivElement | null>(
    null
  );
  const editInDrawerTourTargetRef = React.useRef<HTMLDivElement | null>(null);
  const readyToPostTourPostButtonsRef = React.useRef<HTMLDivElement | null>(
    null
  );
  const openTransactionsSettingsDrawerRef = React.useRef<(() => void) | null>(
    null
  );
  const [fileFilterTourOpen, setFileFilterTourOpen] =
    React.useState<boolean>(false);
  const [readyToPostTourOpen, setReadyToPostTourOpen] =
    React.useState<boolean>(false);
  const fileFilterTourTriggeredRef = React.useRef<boolean>(false);
  const readyToPostTourTriggeredRef = React.useRef<boolean>(false);
  const [fileFilterTourManualTargetIndex, setFileFilterTourManualTargetIndex] =
    React.useState<number | null>(null); // When set, ref attaches to file at this index

  /** When the user tries to switch rows while editing (or clicks outside),
   *  store the target row info so we can prompt them to save/discard.
   *  uuid/original are null for a plain click-outside (no target row). */
  const [pendingRowSwitch, setPendingRowSwitch] = React.useState<{
    uuid: string | null;
    original: ValidatedExcelTransaction | null;
    dataKey?: string;
    mode: 'cell' | 'fullRow' | 'click-outside';
  } | null>(null);

  // Key for ScopeTable instance (kept stable while mounted)
  const [tableInstanceKey] = React.useState(0);

  const toggleFileSelection = (excelMetadataId: string) => {
    // Allow the selection-driven fetch effect to run — a prior save can leave
    // skipNextFetchRef stuck true when selection was preserved (same issue as
    // handleFileStateTabChange).
    skipNextFetchRef.current = false;
    setSelectedImportIds((prev) => {
      const wasSelected = prev.includes(excelMetadataId);
      const next = wasSelected
        ? prev.filter((id) => id !== excelMetadataId)
        : [...prev, excelMetadataId];

      // If this file was auto-selected via the file-filter tour preference,
      // allow the user to "opt out" permanently by deselecting it once.
      if (wasSelected && currentUser?.userUuid) {
        const tourImportId = preferencesHook(
          PreferenceActions.GET,
          PreferenceSections.TRANSACTIONS,
          { name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY },
          currentUser.userUuid
        );
        const importIdStr =
          typeof tourImportId === 'string' && tourImportId.trim()
            ? tourImportId
            : null;
        if (importIdStr === excelMetadataId) {
          preferencesHook(
            PreferenceActions.SET,
            PreferenceSections.TRANSACTIONS,
            {
              name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
              value: null,
            },
            currentUser.userUuid
          );
        }
      }

      return next;
    });
    // Reset to page 1 when filter changes so we show the first page of filtered results
    setCurrentPage(1);
  };

  const getEntityTransactionsDataLogId = (): string =>
    `entity-transactions-${organisationId}`;

  const openActivityLogs = React.useCallback(() => {
    if (pwaMobileNav) {
      setPwaActivitySheetOpen(true);
      return;
    }
    const dataLogId = getEntityTransactionsDataLogId();
    const connectionIdValue = organisation?.connectionId ?? '';
    const mappingId = '';

    const drawerData: DrawerData = {
      title: t('organisation.reconciliation-activity'),
      type: DrawerType.ACTIVITY_LOGS,
      component: (
        <ExcelActivityLogsDrawerContent
          organisationId={organisationId}
          connectionId={connectionIdValue}
          mappingId={mappingId}
          dataLogId={dataLogId}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  }, [
    dispatch,
    organisation?.connectionId,
    organisationId,
    pwaMobileNav,
    t,
  ]);

  const openTransactionHistoryModal = React.useCallback(
    (record: Record<string, unknown>) => {
      if (!supportsDeletedAndHistory) return;
      const uuid = (record.__transactionUuid ?? record.uuid) as
        | string
        | undefined;
      if (!uuid?.trim()) return;
      setHistoryTransactionUuid(uuid.trim());
      setHistoryModalOpen(true);
    },
    [supportsDeletedAndHistory]
  );

  const closeTransactionHistoryModal = React.useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryTransactionUuid(null);
  }, []);

  const handleActivityClick = () => {
    openActivityLogs();
  };

  const handleBottomNavPosted = React.useCallback(() => {
    if (!showPostedFileState) return;
    if (rightPanelTransactionTab === 'POSTED') {
      handleFileStateTabChange('CAPTURED');
      return;
    }
    handleFileStateTabChange('POSTED');
  }, [
    showPostedFileState,
    rightPanelTransactionTab,
    handleFileStateTabChange,
  ]);

  useRegisterPwaTransactionsBottomActions(pwaMobileNav, {
    showPosted: showPostedFileState,
    isPostedActive: rightPanelTransactionTab === 'POSTED',
    onActivity: openActivityLogs,
    onPosted: handleBottomNavPosted,
  });

  const handleConfirmRescanFile = (
    excelMetadataId: string,
    grouped?: boolean
  ) => {
    const file = (organisation?.excelFilesMetadata ?? []).find(
      (item) => item.excelMetadataId === excelMetadataId
    );
    const rescanFromDrawer = rescanFromDrawerFileIdRef.current != null;
    rescanFromDrawerFileIdRef.current = null;

    onReceiptsUploadStart?.(
      file?.fileName ?? t('organisation.file-document-fallback')
    );
    // The rescan replaces the underlying transaction(s), so any open edit
    // drawer is now stale ? close it (and its attachment viewer). Register the
    // file so the entity shell can reopen the drawer once the new scan is ready.
    if (rescanFromDrawer) {
      if (drawerOpen && drawerData?.type === DrawerType.EDIT) {
        clearTemporaryFileEditSelection();
        dispatch(turnDrawerOff());
        onCloseAttachments();
      }
      pendingRescanDrawerReopenRef.current = {
        ready: false,
        initialBatchId: lastUploadedReceiptsBatchId ?? null,
        preRescanSignature: computeSidePanelSignature(
          organisation?.excelFilesMetadata ?? []
        ),
      };
      rescanReopenNavigationForFileRef.current = null;
    }
    dispatch(rescanReceipt(organisationId, excelMetadataId, grouped));
    setRescanFileImportId(null);
  };

  const skipNextFetchRef = React.useRef(false);

  const handleDeleteFile = (excelMetadataId: string) => {
    const postDeleteSelection = selectedImportIds.filter(
      (id) => id !== excelMetadataId
    );
    setSelectedImportIds(postDeleteSelection);

    const page = currentPage || 1;
    const pageSize = activeTablePageSize;
    const baseFilters: Record<string, unknown> = {
      ...(currentFiltersState ?? {}),
    };
    if (postDeleteSelection.length > 0) {
      baseFilters.excelMetadataId = { value: postDeleteSelection };
    } else {
      delete (baseFilters as any).excelMetadataId;
    }
    const mergedFilters: Record<string, unknown> | undefined = baseFilters;

    skipNextFetchRef.current = true;
    dispatch(
      deleteExcelTransactionsFileRequest(organisationId, excelMetadataId, {
        page,
        pageSize,
        filters: mergedFilters,
      })
    );
  };

  // Clear selections for files that no longer exist in the metadata list
  // (safety measure in case metadata updates but selection wasn't cleared)
  React.useEffect(() => {
    const existingImportIds = new Set(
      excelFiles.map((file) => file.excelMetadataId)
    );
    setSelectedImportIds((prev) => {
      const filtered = prev.filter((id) => existingImportIds.has(id));
      // Only update if there's actually a change to prevent infinite loops
      if (filtered.length !== prev.length) {
        return filtered;
      }
      // Check if any IDs were actually removed (even if length is same)
      const hasChange = prev.some((id) => !existingImportIds.has(id));
      return hasChange ? filtered : prev;
    });
  }, [excelFiles]);

  // When user finishes first upload, modal stores excelMetadataId
  // (historically the importId) in user preferences. Auto-select that file
  // when it appears in excelFiles.
  // Previously, when the user finished their first upload, we auto-selected
  // that file based on a stored preference. This made it hard to fully clear
  // selection in the Transactions tab. We now rely solely on explicit user
  // selection instead of auto-selecting any file here.

  // Show file-filter tour on first upload: when we have pending excelMetadataId in
  // user preferences, the file is selected, and user hasn't seen the tour before.
  React.useEffect(() => {
    if (pwaMobileNav) return;
    if (fileFilterTourTriggeredRef.current) return;
    if (!currentUser?.userUuid) return;
    const tourImportId = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY },
      currentUser.userUuid
    );
    const importIdStr =
      typeof tourImportId === 'string' && tourImportId.trim()
        ? tourImportId
        : null;
    if (!importIdStr) return;
    const isSelected = selectedImportIds.includes(importIdStr);
    const hasFile = excelFiles.some((f) => f.excelMetadataId === importIdStr);
    if (!isSelected || !hasFile || !showUploadsPanel) return;

    const providerShownKey = isReceiptsProvider
      ? TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_RECEIPTS_KEY
      : TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_EXCEL_KEY;

    const providerTourShown = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: providerShownKey },
      currentUser.userUuid
    );
    if (providerTourShown === true) {
      // Clear pending so we don't re-trigger
      preferencesHook(
        PreferenceActions.SET,
        PreferenceSections.TRANSACTIONS,
        {
          name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
          value: null,
        },
        currentUser.userUuid
      );
      return;
    }

    // Backwards compatibility: if legacy shared flag is set, treat as shown for this provider too
    // (avoids re-showing tours to existing users after split).
    const legacyTourShown = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_KEY },
      currentUser.userUuid
    );
    if (legacyTourShown === true) {
      preferencesHook(
        PreferenceActions.SET,
        PreferenceSections.TRANSACTIONS,
        { name: providerShownKey, value: true },
        currentUser.userUuid
      );
      // Clear pending so we don't re-trigger
      preferencesHook(
        PreferenceActions.SET,
        PreferenceSections.TRANSACTIONS,
        {
          name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
          value: null,
        },
        currentUser.userUuid
      );
      return;
    }

    fileFilterTourTriggeredRef.current = true;
    // Delay so the file element is rendered, ref is attached, and data has loaded (e.g. after first-scan tab switch)
    const timer = setTimeout(() => {
      setFileFilterTourOpen(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [
    selectedImportIds,
    excelFiles,
    currentUser?.userUuid,
    showUploadsPanel,
    preferencesHook,
    isReceiptsProvider,
    pwaMobileNav,
  ]);

  // Ready to Post tab tour: third-party accounting entities only (not Receipts/Excel).
  React.useEffect(() => {
    if (pwaMobileNav) return;
    if (readyToPostTourTriggeredRef.current) return;
    if (!showReadyToPostTour) return;
    if (rightPanelTransactionTab !== 'REVIEWED') return;
    if (!currentUser?.userUuid) return;
    if (fileFilterTourOpen) return;

    const tourShown = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: TRANSACTIONS_READY_TO_POST_TOUR_SHOWN_KEY },
      currentUser.userUuid
    );
    if (tourShown === true) return;

    readyToPostTourTriggeredRef.current = true;
    const timer = window.setTimeout(() => setReadyToPostTourOpen(true), 350);
    return () => window.clearTimeout(timer);
  }, [
    showReadyToPostTour,
    rightPanelTransactionTab,
    currentUser?.userUuid,
    preferencesHook,
    fileFilterTourOpen,
    pwaMobileNav,
  ]);

  const handleShowFileFilterTour = React.useCallback(() => {
    if (excelFiles.length === 0) return;
    setFileFilterTourManualTargetIndex(0);
    // Defer open so ref gets attached first
    setTimeout(() => setFileFilterTourOpen(true), 50);
  }, [excelFiles.length]);

  const handleShowReadyToPostTour = React.useCallback(() => {
    if (!showReadyToPostTour) return;
    if (rightPanelTransactionTab !== 'REVIEWED') {
      handleFileStateTabChange('REVIEWED');
    }
    setTimeout(() => setReadyToPostTourOpen(true), 100);
  }, [
    showReadyToPostTour,
    rightPanelTransactionTab,
    handleFileStateTabChange,
  ]);

  const coverImgStyle = {
    width: 250,
    maxWidth: 250,
    height: 'auto' as const,
    display: 'block' as const,
  };
  const fileFilterTourSteps: ScopeTourStep[] = React.useMemo(() => {
    const buildStep = (
      titleKey: string,
      messageKey: string,
      target: HTMLElement | (() => HTMLElement) | null
    ): ScopeTourStep => ({
      target: target ?? undefined,
      description: (
        <div className="scope-tour-step-description">{t(messageKey)}</div>
      ),
      cover: (
        <div className="scope-tour-step-cover">
          <img
            src={FilterTransactionsImage}
            alt=""
            className="scope-tour-step-cover-img"
            style={coverImgStyle}
          />
          <div className={SCOPE_TOUR_STEP_TITLE_CLASS}>{t(titleKey)}</div>
        </div>
      ),
      placement: 'left',
    });
    const getFileFilterTarget = () =>
      fileFilterTourTargetRef.current ?? document.body;
    const getUploadTarget = () =>
      uploadButtonTourTargetRef.current ?? document.body;
    const getEditTarget = (): HTMLElement =>
      document.querySelector<HTMLElement>('[data-tour-edit-transaction]') ??
      editButtonTourTargetRef.current ??
      document.body;
    const getEditInDrawerTarget = (): HTMLElement =>
      editInDrawerTourTargetRef.current ?? document.body;
    const getMassUpdateTarget = (): HTMLElement =>
      massUpdateButtonTourTargetRef.current ?? document.body;

    if (isReceiptsProvider) {
      return [
        buildStep(
          'organisation.transactions-tab-receipts-tour-step1-title',
          'organisation.file-filter-tour-message',
          getFileFilterTarget
        ),
        buildStep(
          'organisation.transactions-tab-receipts-tour-step2-title',
          'organisation.transactions-tab-receipts-tour-step2-message',
          getUploadTarget
        ),
        buildStep(
          'organisation.transactions-tab-receipts-tour-step3-title',
          'organisation.transactions-tab-receipts-tour-step3-message',
          getEditTarget
        ),
        buildStep(
          'organisation.transactions-tab-receipts-tour-step4-title',
          'organisation.transactions-tab-receipts-tour-step4-message',
          getEditInDrawerTarget
        ),
        buildStep(
          'organisation.edit-multiple-transactions-tour-title',
          'organisation.transactions-tab-receipts-tour-step5-message',
          getMassUpdateTarget
        ),
      ];
    }
    return [
      buildStep(
        'organisation.file-filter-tour-title',
        'organisation.file-filter-tour-message',
        getFileFilterTarget
      ),
      buildStep(
        'organisation.file-filter-tour-upload-title',
        'organisation.file-filter-tour-upload-step',
        getUploadTarget
      ),
      buildStep(
        'organisation.edit-transaction-tour-title',
        'organisation.edit-transaction-tour-step',
        getEditTarget
      ),
      buildStep(
        'organisation.edit-multiple-transactions-tour-title',
        'organisation.edit-multiple-transactions-tour-step',
        getMassUpdateTarget
      ),
    ];
  }, [t, isReceiptsProvider]);

  const readyToPostTourSteps: ScopeTourStep[] = React.useMemo(() => {
    const buildStep = (
      titleKey: string,
      messageKey: string,
      messageParams: Record<string, string> | undefined,
      target: HTMLElement | (() => HTMLElement) | null,
      coverImage: string,
      placement: ScopeTourStep['placement'] = 'bottom'
    ): ScopeTourStep => ({
      target: target ?? undefined,
      description: (
        <div className="scope-tour-step-description">
          {t(messageKey, messageParams)}
        </div>
      ),
      cover: (
        <div className="scope-tour-step-cover">
          <img
            src={coverImage}
            alt=""
            className="scope-tour-step-cover-img"
            style={coverImgStyle}
          />
          <div className={SCOPE_TOUR_STEP_TITLE_CLASS}>{t(titleKey)}</div>
        </div>
      ),
      placement,
    });

    const getReadyToPostTabTarget = (): HTMLElement =>
      document.querySelector<HTMLElement>('[data-tour-ready-to-post-tab]') ??
      document.body;

    const getPostedTabTarget = (): HTMLElement =>
      document.querySelector<HTMLElement>('[data-tour-posted-tab]') ??
      document.body;

    const getPostButtonsTarget = (): HTMLElement =>
      readyToPostTourPostButtonsRef.current ?? getReadyToPostTabTarget();

    const providerLabel = getProviderName(organisation?.providerName);

    if (!showReadyToPostTour) {
      return [];
    }

    return [
      buildStep(
        'organisation.transactions-ready-to-post-tour-step1-title',
        'organisation.transactions-ready-to-post-tour-step1-message',
        { provider: providerLabel },
        getPostButtonsTarget,
        UploadTransactionsImage,
        'left'
      ),
      buildStep(
        'organisation.transactions-ready-to-post-tour-step2-title',
        'organisation.transactions-ready-to-post-tour-step2-message',
        undefined,
        getPostedTabTarget,
        FilterTransactionsImage,
        'bottom'
      ),
    ];
  }, [
    t,
    showReadyToPostTour,
    organisation?.providerName,
    coverImgStyle,
  ]);

  // When the selection of file-import "origin" IDs (or the available files
  // list) changes, re-fetch the transactions and include the selected import
  // IDs as an additional filter. This keeps the table in sync with the
  // right-hand file selection while avoiding extra calls tied to pagination
  // or sort changes (those are handled via the table onChange handler).
  const buildTransactionsRequestFilters = React.useCallback(
    (
      filters?: Record<string, unknown>,
      options?: { tabFileImportIds?: string[] }
    ) => {
      let result = buildTransactionsRefreshFilters({
        tableFilters: filters,
        tabFileImportIds:
          options?.tabFileImportIds !== undefined
            ? options.tabFileImportIds
            : requestTabFileImportIds,
      });
      if (applySelectTransactionUuidApiFilter && selectTransactionFilterId) {
        result = appendSelectTransactionUuidFilter(
          result,
          selectTransactionFilterId
        );
      }
      if (supportsDeletedAndHistory && showDeletedTransactions) {
        result = {
          ...(result ?? {}),
          reversalType: { value: ['DELETED'] },
        };
      }
      return result ?? {};
    },
    [
      requestTabFileImportIds,
      selectTransactionFilterId,
      applySelectTransactionUuidApiFilter,
      showDeletedTransactions,
      supportsDeletedAndHistory,
    ]
  );

  const handleExcelTransactionsPageChange = React.useCallback(
    (
      page: number,
      pageSize: number | undefined,
      filtersState: Record<string, unknown> | undefined
    ) => {
      const effectiveSize = pageSize ?? activeTablePageSize;

      let nextFilters = sanitizeTableManagedFilters(filtersState ?? undefined);

      if (isAccountIdProvider && nextFilters?.sort) {
        const sortFilter = nextFilters.sort as unknown as {
          value?: [string, string?];
        };
        const [field, dir] = sortFilter.value || [];
        if (field === 'accountCode') {
          sortFilter.value = ['accountName', dir];
          nextFilters = { ...nextFilters, sort: sortFilter };
        }
      }
      setCurrentPage(page);
      setCurrentPageSize(effectiveSize);
      // Only update filter state when filters actually change. Updating on every
      // page click recreates the object, re-runs the filter-fetch effect, and can
      // race a page-N request with a page-1 refetch (pager shows last, data doesn't).
      setCurrentFiltersState((prev) => {
        const prevKey = JSON.stringify(prev ?? {});
        const nextKey = JSON.stringify(nextFilters ?? {});
        if (prevKey === nextKey) return prev;
        skipNextFetchRef.current = true;
        return nextFilters;
      });

      const mergedFilters = buildTransactionsRequestFilters(nextFilters);

      if (!organisationId) return;
      dispatch(
        fetchAllExcelTransactionsRequest(
          organisationId,
          page,
          effectiveSize,
          mergedFilters
        )
      );
    },
    [
      activeTablePageSize,
      buildTransactionsRequestFilters,
      dispatch,
      isAccountIdProvider,
      organisationId,
      sanitizeTableManagedFilters,
    ]
  );

  const handleExcelTransactionsServerFilter = React.useCallback(
    (filters: Record<string, any>, pageSize?: number) => {
      handleExcelTransactionsPageChange(1, pageSize, filters);
    },
    [handleExcelTransactionsPageChange]
  );

  const openReceiptsDeepLinkReviewDrawer = React.useCallback(
    async (original: ValidatedExcelTransaction) => {
      const txId = String(original.uuid ?? '').trim();
      if (!txId || !organisationId || !connectionId) return;

      const fileId = getTransactionFileFilterId(original, excelFiles);
      if (!fileId) return;

      const fileRowCount = resolveInlineEditFileRowCount(fileId);

      try {
        if (typeof window === 'undefined') return;
        const fetched = await fetchAllTransactionsForDocumentFile({
          organisationId,
          expectedRowCount: fileRowCount,
          apiFilters: convertScopeTableFiltersToApiBody(
            buildTransactionsRefreshFilters({
              tableFilters: {},
              tabFileImportIds: [fileId],
            })
          ),
        });
        const preloaded = collectTransactionsSameDocument(
          fetched.map((row) => ({ __transactionRow: row })),
          original
        );
        if (!preloaded.length) return;

        const seed =
          preloaded.find((row) => String(row.uuid) === txId) ?? original;
        const sourceType = String(seed.sourceType ?? '');
        const rawDate = seed.transactionDate;
        const date =
          rawDate != null &&
          (typeof rawDate === 'string' || typeof rawDate === 'number')
            ? formatTransactionTableDate(rawDate, i18n.language)
            : '';
        const num =
          seed.transactionNumber != null
            ? String(seed.transactionNumber).trim()
            : '';

        drawerPreserveMainFileSelectionRef.current = true;
        pendingAttachmentEditDrawerRef.current = {
          transactionId: txId,
          original: seed,
          forceOpenDrawer: true,
          preserveMainFileSelection: true,
          preloadedDrawerTransactions: preloaded,
          targetFileId: fileId,
          targetFileRowCount: fileRowCount,
        };

        setAttachmentDrawerTransactionRef(
          [date, num].filter(Boolean).join(' ? ') || ''
        );
        setAttachmentSourceType(sourceType);
        setAttachmentsTransactionId(txId);
        dispatch(
          fetchTransactionAttachmentRequest(
            connectionId,
            txId,
            txId,
            organisation?.providerName ?? '',
            sourceType,
            true
          )
        );
      } catch {
        drawerPreserveMainFileSelectionRef.current = false;
        pendingAttachmentEditDrawerRef.current = null;
      }
    },
    [
      organisationId,
      connectionId,
      excelFiles,
      resolveInlineEditFileRowCount,
      organisation?.providerName,
      dispatch,
    ]
  );

  /** Receipts + ?selectTransaction=: Reviewed tab, resolve row, open review drawer for its file. */
  const receiptsSelectTransactionDeepLinkRef = React.useRef<
    'pending' | 'reviewed' | 'lookup' | 'drawer' | 'done'
  >('pending');
  const receiptsSelectTransactionLookupRef = React.useRef(false);

  React.useEffect(() => {
    setPinnedExcelSelectTransactionFilterId(undefined);
    setActiveSelectTransactionDeepLinkId(undefined);
    selectTransactionDeepLinkConsumedRef.current = false;
  }, [organisationId]);

  React.useEffect(() => {
    if (!selectTransactionId) return;
    selectTransactionDeepLinkConsumedRef.current = false;
    receiptsSelectTransactionDeepLinkRef.current = 'pending';
    receiptsSelectTransactionLookupRef.current = false;
    receiptsDeepLinkDrawerOpeningRef.current = false;
  }, [selectTransactionId, organisationId]);

  React.useEffect(() => {
    if (!isExcelProvider || !selectTransactionId || !organisationId) return;
    if (selectTransactionDeepLinkConsumedRef.current) return;
    consumeSelectTransactionDeepLink(selectTransactionId);
  }, [
    isExcelProvider,
    selectTransactionId,
    organisationId,
    consumeSelectTransactionDeepLink,
  ]);

  React.useEffect(() => {
    if (!isReceiptsOnlyProvider || !organisationId) {
      return;
    }
    if (!receiptsDeepLinkTransactionId) return;
    if (
      selectTransactionId &&
      !selectTransactionDeepLinkConsumedRef.current
    ) {
      consumeSelectTransactionDeepLink(selectTransactionId);
    }    if (receiptsSelectTransactionDeepLinkRef.current === 'done') return;
    if (organisation?.excelFilesMetadata == null) return;

    if (receiptsSelectTransactionDeepLinkRef.current === 'pending') {
      if (rightPanelTransactionTab !== 'REVIEWED') {
        receiptsSelectTransactionDeepLinkRef.current = 'reviewed';
        handleFileStateTabChange('REVIEWED');
        return;
      }
      receiptsSelectTransactionDeepLinkRef.current = 'lookup';
    }

    if (receiptsSelectTransactionDeepLinkRef.current === 'reviewed') {
      if (rightPanelTransactionTab !== 'REVIEWED') return;
      receiptsSelectTransactionDeepLinkRef.current = 'lookup';
    }

    const pageMatchesOrg =
      allExcelTransactionsPage?.organisationId === organisationId;
    const loadedTransactions = pageMatchesOrg
      ? allExcelTransactionsPage?.transactions ?? []
      : [];

    const matchedTransaction = loadedTransactions.find(
      (row) => String(row.uuid) === String(receiptsDeepLinkTransactionId)
    );

    if (!matchedTransaction && !receiptsSelectTransactionLookupRef.current) {
      receiptsSelectTransactionLookupRef.current = true;
      skipNextFetchRef.current = true;
      dispatch(
        fetchAllExcelTransactionsRequest(organisationId, 1, 1, {
          uuid: { value: [receiptsDeepLinkTransactionId] },
        })
      );
      return;
    }

    if (!matchedTransaction) {
      if (allExcelTransactionsLoading) return;
      setActiveSelectTransactionDeepLinkId(undefined);
      receiptsSelectTransactionDeepLinkRef.current = 'done';
      return;
    }

    if (receiptsSelectTransactionDeepLinkRef.current !== 'lookup') return;
    if (receiptsDeepLinkDrawerOpeningRef.current) return;

    receiptsDeepLinkDrawerOpeningRef.current = true;
    receiptsSelectTransactionDeepLinkRef.current = 'drawer';
    void openReceiptsDeepLinkReviewDrawer(matchedTransaction).finally(() => {
      setActiveSelectTransactionDeepLinkId(undefined);
      receiptsSelectTransactionDeepLinkRef.current = 'done';
      receiptsDeepLinkDrawerOpeningRef.current = false;
    });
  }, [
    isReceiptsOnlyProvider,
    receiptsDeepLinkTransactionId,
    selectTransactionId,
    consumeSelectTransactionDeepLink,
    organisationId,
    organisation?.excelFilesMetadata,
    rightPanelTransactionTab,
    handleFileStateTabChange,
    allExcelTransactionsPage,
    allExcelTransactionsLoading,
    dispatch,
    openReceiptsDeepLinkReviewDrawer,
  ]);

  /**
   * Post-save refresh: keep the current file-panel selection when set;
   * otherwise all files in the active Captured / Reviewed / Posted tab.
   */
  const getSaveRefreshParams = React.useCallback(
    (page: number, pageSize: number) => ({
      page,
      pageSize,
      filters: sanitizeTableManagedFilters(currentFiltersState),
      tabFileImportIds: requestTabFileImportIds,
    }),
    [
      sanitizeTableManagedFilters,
      currentFiltersState,
      requestTabFileImportIds,
    ]
  );

  React.useEffect(() => {
    if (!onRegisterRefetchExcelTransactions) return;

    onRegisterRefetchExcelTransactions(() => {
      if (!organisationId) return;
      setCurrentPage(1);
      skipNextFetchRef.current = true;
      const pageSize = activeTablePageSize;
      const mergedFilters =
        buildTransactionsRequestFilters(currentFiltersState);
      dispatch(
        fetchAllExcelTransactionsRequest(
          organisationId,
          1,
          pageSize,
          mergedFilters
        )
      );
    });

    return () => onRegisterRefetchExcelTransactions(null);
  }, [
    onRegisterRefetchExcelTransactions,
    organisationId,
    activeTablePageSize,
    buildTransactionsRequestFilters,
    currentFiltersState,
    dispatch,
  ]);

  // Open the edit drawer when: (1) user prefers edit-in-drawer and this transaction has attachments, or (2) pending has forceOpenDrawer (e.g. other entry points).
  React.useEffect(() => {
    if (attachmentsTransactionId === NOT_CLICKED_ATTACHMENT) {
      return;
    }
    const pending = pendingAttachmentEditDrawerRef.current;
    if (!pending || pending.transactionId !== attachmentsTransactionId) {
      return;
    }
    const useDrawer = editInDrawer || pending.forceOpenDrawer === true;
    if (!useDrawer) {
      pendingAttachmentEditDrawerRef.current = null;
      return;
    }
    // Normal attachment flow: wait for at least one attachment (or user upload). Review-mode from table: open immediately.
    if (!pending.forceOpenDrawer && entityTransactionAttachments.length === 0) {
      return;
    }
    const tableRecords = currentPageTableDataRef.current;
    const hasPreloadedDrawerTransactions =
      (pending.preloadedDrawerTransactions?.length ?? 0) > 0;
    if (pending.targetFileId && !hasPreloadedDrawerTransactions) {
      const isTargetFileSelected =
        selectedImportIds.length === 1 &&
        selectedImportIds[0] === pending.targetFileId;
      const loadedCount = collectTransactionsSameDocument(
        tableRecords as Record<string, unknown>[],
        pending.original
      ).length;
      const fetchedAtFullFilePageSize =
        pending.targetFileRowCount != null &&
        inlineEditFilePageSizeOverride != null &&
        inlineEditFilePageSizeOverride >= pending.targetFileRowCount;
      const fullFileLoaded = isDocumentFileFullyLoadedForEdit(
        loadedCount,
        pending.targetFileRowCount,
        { fetchedAtFullFilePageSize }
      );
      if (!isTargetFileSelected || !fullFileLoaded) {
        return;
      }
    }
    const { original } = pending;
    pendingAttachmentEditDrawerRef.current = null;
    /** Legacy Excel rows: one transaction per drawer; prev/next moves row-by-row on the current page. */
    const isExcelTransactionRow = original.isExcelTransaction === true;

    const currentDrawerFileNavigationId = getTransactionFileFilterId(
      original,
      excelFiles
    );
    const drawerFileNavigationIds = filteredExcelFiles
      .map((file) => String(file.excelMetadataId ?? '').trim())
      .filter(Boolean);

    let drawerTransactions: ValidatedExcelTransaction[];
    let drawerNavigationMode: 'transaction' | 'document' = 'document';

    if (hasPreloadedDrawerTransactions) {
      drawerTransactions = pending.preloadedDrawerTransactions!;
      drawerNavigationMode = 'document';
    } else if (isExcelTransactionRow) {
      drawerTransactions = [original];
      drawerNavigationMode = 'transaction';
    } else {
      drawerTransactions = collectTransactionsSameDocument(
        tableRecords as Record<string, unknown>[],
        original as ValidatedExcelTransaction | undefined
      );
    }

    const drawerPayload: DrawerData = {
      title: t('organisation.review-document-drawer-title'),
      type: DrawerType.EDIT,
      width: 1800,
      // PWA: tap the dimmed area above the sheet to dismiss. Desktop keeps
      // mask locked so an accidental click does not abandon an in-progress edit.
      maskClosable: pwaMobileNav,
      integratedHeader: true,
      bodyProvidesBottomSheetHandle: pwaMobileNav,
      contentKey: attachmentsTransactionId,
      hideCloseButton: true,
      component: (
        <TransactionEditDrawerWithAttachments
          transactionId={attachmentsTransactionId}
          transactionRef={attachmentDrawerTransactionRef}
          transactions={drawerTransactions}
          navigationMode={drawerNavigationMode}
          currencyOptions={currencyOptions}
          countryOptions={countryOptions}
          sourceTypeOptions={sourceTypeOptionsWithExtras}
          accountNameOptions={accountNameOptions}
          accountNameOptionsGrouped={accountNameOptionsGrouped}
          transactionItemOptions={transactionItemOptions}
          taxNameOptions={taxNameOptions}
          contactOptions={contactOptions}
          contactTypeByContactId={contactTypeByContactId}
          organisationId={organisationId}
          connectionId={connectionId}
          organisation={organisation ?? undefined}
          showTrackingCategoryColumns={showTrackingCategoryColumns}
          trackingCategories={organisation?.trackingCategories ?? []}
          attachmentSourceType={attachmentSourceType}
          currentPage={currentPage || 1}
          currentPageSize={activeTablePageSize}
          effectivePageSize={effectivePageSize}
          currentFiltersState={currentFiltersState ?? undefined}
          selectedImportIds={selectedImportIds}
          documentNavigateRef={drawerDocumentNavigateRef}
          currentFileNavigationId={currentDrawerFileNavigationId}
          fileNavigationIds={drawerFileNavigationIds}
          onNavigateToFile={navigateDrawerToFile}
          onBulkSave={(payloads, postAfterSave) => {
            if (!organisationId) {
              dispatch(turnDrawerOff());
              onCloseAttachments();
              return { ok: true };
            }
            const page = currentPage || 1;
            const pageSize = activeTablePageSize;
            const saveRefresh = getSaveRefreshParams(page, pageSize);
            const isReviewableDrawerProvider =
              organisation?.providerName?.toLowerCase() !==
              Provider.EXCEL.toLowerCase();

            const prepared = prepareDrawerTransactionSaveBatch({
              payloads,
              bodyParams: {
                showTrackingCategoryColumns,
                trackingCategories: organisationTrackingCategories,
                isAccountIdProvider,
                accountNameByIdMap,
                accountNameOptions,
                accountNameOptionsGrouped,
                itemNameByIdMap,
                transactionItemOptions,
                taxRateByIdMap,
                deriveGrossNetFromQuantityUnitPrice:
                  showQuantityUnitPriceColumns,
                preserveOriginalKeys: Array.from(
                  DRAWER_EXCLUDED_FIELD_KEYS
                ) as (keyof ExcelTransaction)[],
              },
              validationOptions: transactionEditSaveValidation,
              enhanceBody: (body, origRow) =>
                origRow.reviewStatus === ReviewStatus.NOT_REVIEWED ||
                origRow.reviewStatus === ReviewStatus.NONE
                  ? { ...body, reviewStatus: ReviewStatus.REVIEWED }
                  : body,
              resolveMarkAsReviewed: (p) =>
                isReviewableDrawerProvider &&
                p.markAsReviewed === true &&
                transactionQualifiesForMarkAsReviewed(p.original),
            });

            if (!prepared.ok) {
              showDrawerBulkSaveValidationError(
                prepared.errorMessage,
                payloads.length
              );
              return {
                ok: false,
                fieldErrorsByTransactionId: prepared.fieldErrorsByTransactionId,
              };
            }

            const reviewIds = payloads
              .filter((payload) => payload.markAsReviewed === true)
              .map((payload) => payload.transactionId)
              .filter(Boolean);
            const changedTransactionIds = new Set(
              payloads
                .filter((payload) =>
                  hasRowChanges(payload.draft, payload.original)
                )
                .map((payload) => payload.transactionId)
            );
            const itemsToSave = prepared.items.filter((item) =>
              changedTransactionIds.has(item.transactionUuid)
            );
            if (reviewIds.length > 0) {
              queueActiveTabRefreshAfterReview();
            }
            if (postAfterSave && !organisation?.connectedProperly) {
              const name = organisation?.friendlyName || t('common.entity');
              dispatch(
                turnMessageOn({
                  title: t('organisation.entity-not-connected-title'),
                  description: t(
                    'organisation.entity-not-connected-to-provider',
                    { entityName: name }
                  ) as string,
                  type: MessageStates.ERROR,
                } as MessageData)
              );
            }
            skipNextFetchRef.current = true;
            const postFileId = String(
              currentDrawerFileNavigationId ?? ''
            ).trim();
            dispatch(
              saveExcelDrawerTransactionsBatchRequest(
                organisationId,
                itemsToSave,
                {
                  ...saveRefresh,
                  ...(reviewIds.length > 0
                    ? { markAsReviewedIds: reviewIds }
                    : {}),
                  ...(postAfterSave &&
                  postFileId &&
                  organisation?.connectedProperly
                    ? {
                        postAfterSave: {
                          fileId: postFileId,
                          status: postAfterSave.status,
                          bankAccountId: ledgerPostAccountId,
                        },
                      }
                    : {}),
                }
              )
            );
            return { ok: true };
          }}
          onCancel={() => {
            clearTemporaryFileEditSelection();
            dispatch(turnDrawerOff());
            onCloseAttachments();
          }}
          onCloseAttachments={onCloseAttachments}
          onUploadAttachment={handleUploadAttachment}
          showEditInDrawerSetting={showEditInDrawerSetting}
          onEditModeChange={(mode) => {
            if (mode === 'table') {
              handlePersistedEditInDrawerChangeRef.current(false);
            }
          }}
          handlePreferences={handlePreferences}
          onActivity={pwaMobileNav ? openActivityLogs : undefined}
          onRequestRescan={(excelMetadataId) => {
            rescanFromDrawerFileIdRef.current = excelMetadataId;
            setRescanFileImportId(excelMetadataId);
          }}
          onCreateInterpretationRule={
            reviewCaptureRuleCreationEnabled
              ? handleCreateInterpretationRule
              : undefined
          }
          onAfterSuccessfulSave={
            reviewCaptureRuleCreationEnabled
              ? handleAfterSuccessfulDrawerSave
              : undefined
          }
          onDeleteLine={(transactionUuid) => {
            if (!organisationId || !transactionUuid?.trim()) return;
            const page = currentPage || 1;
            const pageSize = activeTablePageSize;
            const saveRefresh = getSaveRefreshParams(page, pageSize);
            dispatch(
              deleteExcelTransactionRequest(organisationId, transactionUuid, {
                page: saveRefresh.page,
                pageSize: saveRefresh.pageSize,
                filters: saveRefresh.filters,
              })
            );
          }}
        />
      ),
    };
    editDrawerOpenFromAttachmentRef.current = true;
    dispatch(clearDrawerTransactionIdMap());
    dispatch(turnDrawerOn(drawerPayload));
  }, [
    entityTransactionAttachments,
    attachmentsTransactionId,
    attachmentDrawerTransactionRef,
    editInDrawer,
    t,
    dispatch,
    currencyOptions,
    countryOptions,
    accountNameOptions,
    accountNameOptionsGrouped,
    taxNameOptions,
    taxRateByIdMap,
    organisationId,
    organisation?.providerName,
    organisation?.excelFilesMetadata,
    organisation?.groupedConnections,
    connectionId,
    currentPage,
    activeTablePageSize,
    effectivePageSize,
    currentFiltersState,
    selectedImportIds,
    excelFiles,
    filteredExcelFiles,
    isAccountIdProvider,
    accountNameByIdMap,
    showTrackingCategoryColumns,
    transactionEditSaveValidation,
    showTransactionEditValidationError,
    showDrawerBulkSaveValidationError,
    clearTemporaryFileEditSelection,
    reviewDrawerRelaunchKey,
    getSaveRefreshParams,
    preparePendingAttachmentEditDrawer,
    rightPanelTransactionTab,
    navigateDrawerToFile,
    handleCreateInterpretationRule,
    handleAfterSuccessfulDrawerSave,
    reviewCaptureRuleCreationEnabled,
    inlineEditFilePageSizeOverride,
  ]);

  // After save: keep file-panel selection and refresh with the same filter scope.
  React.useEffect(() => {
    if (
      bulkUpdateExcelTransactionsSuccessCount <=
      bulkUpdateSuccessCountRef.current
    ) {
      return;
    }
    bulkUpdateSuccessCountRef.current = bulkUpdateExcelTransactionsSuccessCount;
    const keepDrawerTableContext =
      drawerOpen &&
      drawerData?.type === DrawerType.EDIT &&
      editDrawerOpenFromAttachmentRef.current;
    if (keepDrawerTableContext) {
      return;
    }
    setMassUpdateApplying(false);
    setMassUpdateMode(false);
    setMassUpdateSelectedRowKeys([]);
    setMassUpdateField('');
    setMassUpdateValue(undefined);
    resetInlineEditFilePageSizeOverride();
    // Do not set skipNextFetchRef here: selection is preserved so the
    // selection-driven fetch effect will not re-run, and a stuck skip flag
    // would block the next Pending review / tab click refresh.
    if (!organisationId) return;
    const page = currentPage || 1;
    const pageSize = currentPageSize ?? effectivePageSize;
    const refreshParams = getSaveRefreshParams(page, pageSize);
    const postSaveFilters = buildTransactionsRequestFilters(
      refreshParams.filters,
      { tabFileImportIds: refreshParams.tabFileImportIds }
    );
    dispatch(
      fetchAllExcelTransactionsRequest(
        organisationId,
        page,
        pageSize,
        postSaveFilters
      )
    );
  }, [
    bulkUpdateExcelTransactionsSuccessCount,
    organisationId,
    currentPage,
    currentPageSize,
    effectivePageSize,
    drawerOpen,
    drawerData?.type,
    dispatch,
    getSaveRefreshParams,
    buildTransactionsRequestFilters,
  ]);

  React.useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    // For providers that use file-state tabs, wait until excelFilesMetadata is
    // initialized. Otherwise we dispatch once with no file filter ({}) and then
    // immediately again when metadata arrives (with excelMetadataId filter).
    if (showFileStateTabs && organisation?.excelFilesMetadata == null) {
      return;
    }
    // When file selection changes, reset to the first page so the user sees
    // results from the beginning with the new filter applied.
    const page = 1;
    const pageSize = activeTablePageSize;

    const mergedFilters = buildTransactionsRequestFilters(currentFiltersState);
    const requestKey = JSON.stringify({
      organisationId,
      page,
      pageSize,
      mergedFilters: mergedFilters ?? {},
    });
    if (shouldSkipTxFetchBurst(requestKey, 1500)) {
      return;
    }

    dispatch(
      fetchAllExcelTransactionsRequest(
        organisationId,
        page,
        pageSize,
        mergedFilters
      )
    );
  }, [
    dispatch,
    organisationId,
    currentFiltersState,
    activeTablePageSize,
    buildTransactionsRequestFilters,
    showFileStateTabs,
    organisation?.excelFilesMetadata,
    effectiveSelectedImportIds,
    selectTransactionFilterId,
  ]);

  // After a successful attachment upload, refetch transactions so the row (e.g. hasAttachment) updates
  React.useEffect(() => {
    if (!organisationId) return;
    if (
      !attachmentLoading &&
      didUploadAttachmentRef.current &&
      attachmentsTransactionId !== NOT_CLICKED_ATTACHMENT &&
      lastFetchedAttachments?.transactionId === attachmentsTransactionId
    ) {
      didUploadAttachmentRef.current = false;
      const page = currentPage || 1;
      const pageSize = activeTablePageSize;
      const mergedFilters =
        buildTransactionsRequestFilters(currentFiltersState);
      dispatch(
        fetchAllExcelTransactionsRequest(
          organisationId,
          page,
          pageSize,
          mergedFilters
        )
      );
    }
  }, [
    attachmentLoading,
    attachmentsTransactionId,
    lastFetchedAttachments?.transactionId,
    organisationId,
    currentPage,
    activeTablePageSize,
    currentFiltersState,
    selectedImportIds,
    excelFiles,
    dispatch,
    buildTransactionsRequestFilters,
  ]);

  // Column keys (dataIndex) in display order; tax rate, account type and contact type (Type) columns are hidden.
  const entityTransactionColumnKeys = React.useMemo(() => {
    const fromExcel = stripProviderExcludedColumns(
      excelColumnNames
        .filter(
          (name) =>
            VALIDATION_COLUMN_KEY_MAP[name] != null &&
            VALIDATION_COLUMN_KEY_MAP[name] !== 'taxRate' &&
            VALIDATION_COLUMN_KEY_MAP[name] !== 'accountType' &&
            VALIDATION_COLUMN_KEY_MAP[name] !== 'contactType'
        )
        .map((name) => VALIDATION_COLUMN_KEY_MAP[name] as string)
    );
    if (!showTrackingCategoryColumns) {
      return injectDefaultTransactionTabColumns(fromExcel);
    }
    return injectTrackingCategoryColumns(
      injectDefaultTransactionTabColumns(fromExcel)
    );
  }, [showTrackingCategoryColumns, stripProviderExcludedColumns]);

  const allTransactionsTableData = React.useMemo(() => {
    if (allExcelTransactionsPage?.organisationId !== organisationId) return [];
    if (showFileStateTabs && activeTabFileIds.length === 0) {
      return [];
    }
    const rows = allExcelTransactionsPage.transactions;
    if (!rows?.length) return [];
    const trackingCategories = organisation?.trackingCategories ?? [];
    const orgTrackingCategory1Id =
      trackingCategories[0]?.id?.toString?.().trim?.() ?? '';
    const orgTrackingCategory2Id =
      trackingCategories[1]?.id?.toString?.().trim?.() ?? '';
    const displayNames = excelColumnNames.filter(
      (name) => VALIDATION_COLUMN_KEY_MAP[name] != null
    );
    return rows.map((row: ValidatedExcelTransaction, idx: number) => {
      let normalizedRow = row;
      if (
        showTrackingCategoryColumns &&
        orgTrackingCategory1Id &&
        orgTrackingCategory2Id
      ) {
        const rowTrackingCategory1Id = (row.trackingCategory1Id ?? '')
          .toString()
          .trim();
        const rowTrackingCategory2Id = (row.trackingCategory2Id ?? '')
          .toString()
          .trim();
        const isSwappedByCategoryId =
          rowTrackingCategory1Id === orgTrackingCategory2Id &&
          rowTrackingCategory2Id === orgTrackingCategory1Id;
        if (isSwappedByCategoryId) {
          normalizedRow = {
            ...row,
            trackingCategory1Id: row.trackingCategory2Id ?? null,
            trackingCategory1Name: row.trackingCategory2Name ?? null,
            trackingOption1Id: row.trackingOption2Id ?? null,
            trackingOption1Name: row.trackingOption2Name ?? null,
            trackingCategory2Id: row.trackingCategory1Id ?? null,
            trackingCategory2Name: row.trackingCategory1Name ?? null,
            trackingOption2Id: row.trackingOption1Id ?? null,
            trackingOption2Name: row.trackingOption1Name ?? null,
          };
        }
      }
      const record: Record<string, unknown> = {
        key: normalizedRow.uuid ?? idx,
        __transactionUuid: normalizedRow.uuid,
        __transactionIndex: idx,
        __transactionRow: normalizedRow,
      };
      displayNames.forEach((name) => {
        const dataKey = VALIDATION_COLUMN_KEY_MAP[name];
        if (dataKey && dataKey in normalizedRow) {
          record[dataKey] =
            normalizedRow[dataKey as keyof ValidatedExcelTransaction];
        }
      });
      // Account Name column uses dataKey accountCode ? keep cell value in sync with
      // COA accountId when present, else accountName.
      const rowAccountId =
        normalizedRow.accountId != null &&
        String(normalizedRow.accountId).trim() !== ''
          ? String(normalizedRow.accountId).trim()
          : '';
      if (rowAccountId) {
        record.accountCode = rowAccountId;
      } else if (
        (!record.accountCode || String(record.accountCode).trim() === '') &&
        normalizedRow.accountName != null &&
        String(normalizedRow.accountName).trim() !== ''
      ) {
        record.accountCode = String(normalizedRow.accountName).trim();
      }
      if (showTrackingCategoryColumns) {
        const id1 =
          (normalizedRow.trackingOption1Id ?? '').toString().trim() || '';
        const id2 =
          (normalizedRow.trackingOption2Id ?? '').toString().trim() || '';
        record[TRACKING_CATEGORY_1_COLUMN_KEY] = id1;
        record[TRACKING_CATEGORY_2_COLUMN_KEY] = id2;
      }
      if (showQuantityUnitPriceColumns) {
        const { gross, net } = resolveCalculatedGrossAndNet(normalizedRow);
        record.gross = gross;
        record.net = net;
        record.taxInclusive =
          normalizedRow.taxInclusive === undefined
            ? true
            : Boolean(normalizedRow.taxInclusive);
      }
      return record;
    });
  }, [
    allExcelTransactionsPage?.organisationId,
    allExcelTransactionsPage?.transactions,
    organisationId,
    organisation?.trackingCategories,
    showTrackingCategoryColumns,
    showQuantityUnitPriceColumns,
    showFileStateTabs,
    activeTabFileIds,
  ]);

  const displayedTransactionsTableData = React.useMemo(() => {
    const rows = allTransactionsTableData ?? [];
    const filtered = rows.filter((record) => {
      const row = record?.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      const reversalType = getTransactionReversalType(
        row ?? { reversalType: record?.reversalType }
      );
      // API may return live + deleted; filter client-side for all entity types.
      // Excel/Receipts "Show deleted" toggle: deleted only. Otherwise never show DELETED.
      if (supportsDeletedAndHistory && showDeletedTransactions) {
        return reversalType === 'DELETED';
      }
      if (supportsDeletedAndHistory) {
        return reversalType === 'NONE';
      }
      return reversalType !== 'DELETED';
    });

    if (!pendingManualEntry?.uuid || showDeletedTransactions) {
      return filtered;
    }

    const displayNames = excelColumnNames.filter(
      (name) => VALIDATION_COLUMN_KEY_MAP[name] != null
    );
    const pendingRecord: Record<string, unknown> = {
      key: pendingManualEntry.uuid,
      __transactionUuid: pendingManualEntry.uuid,
      __transactionIndex: -1,
      __transactionRow: pendingManualEntry,
      __isPendingManualEntry: true,
    };
    displayNames.forEach((name) => {
      const dataKey = VALIDATION_COLUMN_KEY_MAP[name];
      if (dataKey && dataKey in pendingManualEntry) {
        pendingRecord[dataKey] =
          pendingManualEntry[dataKey as keyof ValidatedExcelTransaction];
      }
    });
    if (showTrackingCategoryColumns) {
      pendingRecord[TRACKING_CATEGORY_1_COLUMN_KEY] = '';
      pendingRecord[TRACKING_CATEGORY_2_COLUMN_KEY] = '';
    }
    if (showQuantityUnitPriceColumns) {
      pendingRecord.gross = pendingManualEntry.gross;
      pendingRecord.net = pendingManualEntry.net;
      pendingRecord.taxInclusive =
        pendingManualEntry.taxInclusive === undefined
          ? true
          : Boolean(pendingManualEntry.taxInclusive);
    }

    return [pendingRecord, ...filtered];
  }, [
    allTransactionsTableData,
    showDeletedTransactions,
    supportsDeletedAndHistory,
    pendingManualEntry,
    showTrackingCategoryColumns,
    showQuantityUnitPriceColumns,
  ]);

  const handleShowDeletedTransactionsChange = React.useCallback(
    (checked: boolean) => {
      if (!supportsDeletedAndHistory) return;
      setShowDeletedTransactions(checked);
      // Deleted view: Status column on, Actions off. Live view: reverse.
      setVisibleColumns((prev) => {
        if (!prev) return prev;
        if (!checked) {
          const withoutStatus = prev.filter(
            (k) => k !== ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY
          );
          return withoutStatus.includes(ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY)
            ? withoutStatus
            : [...withoutStatus, ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY];
        }
        const withoutActions = prev.filter(
          (k) => k !== ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY
        );
        if (withoutActions.includes(ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY)) {
          return withoutActions;
        }
        return [...withoutActions, ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY];
      });
      if (!organisationId) return;

      skipNextFetchRef.current = true;
      setCurrentPage(1);

      const baseFilters =
        buildTransactionsRefreshFilters({
          tableFilters: sanitizeTableManagedFilters(currentFiltersState),
          tabFileImportIds: requestTabFileImportIds,
        }) ?? {};
      const withSelect =
        applySelectTransactionUuidApiFilter && selectTransactionFilterId
          ? appendSelectTransactionUuidFilter(
              baseFilters,
              selectTransactionFilterId
            )
          : baseFilters;
      const mergedFilters = checked
        ? { ...withSelect, reversalType: { value: ['DELETED'] } }
        : withSelect;

      dispatch(
        fetchAllExcelTransactionsRequest(
          organisationId,
          1,
          activeTablePageSize,
          mergedFilters
        )
      );
    },
    [
      organisationId,
      sanitizeTableManagedFilters,
      currentFiltersState,
      requestTabFileImportIds,
      applySelectTransactionUuidApiFilter,
      selectTransactionFilterId,
      dispatch,
      activeTablePageSize,
      supportsDeletedAndHistory,
    ]
  );

  const postedFileIds = React.useMemo(() => {
    const posted = new Set<string>();
    (excelFiles ?? []).forEach((file) => {
      if (!file?.isPosted) return;
      const excelMetadataId = String(file.excelMetadataId ?? '').trim();
      const importId = String(file.importId ?? '').trim();
      if (excelMetadataId) posted.add(excelMetadataId);
      if (importId) posted.add(importId);
    });
    return posted;
  }, [excelFiles]);

  const isTransactionLockedByPostedFile = React.useCallback(
    (record: Record<string, unknown>) => {
      if (!postedFileIds.size) return false;
      const row = record.__transactionRow as
        | {
            excelMetadataId?: string | null;
            importId?: string | null;
          }
        | undefined;

      const refs = [
        String(row?.excelMetadataId ?? '').trim(),
        String(row?.importId ?? '').trim(),
        String(record.excelMetadataId ?? '').trim(),
        String(record.importId ?? '').trim(),
      ].filter(Boolean);

      return refs.some((id) => postedFileIds.has(id));
    },
    [postedFileIds]
  );

  const isTransactionDeleted = React.useCallback(
    (record: Record<string, unknown>) => {
      if (!supportsDeletedAndHistory) return false;
      const row = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      return isTransactionDeletedRow(
        row ?? { reversalType: record?.reversalType }
      );
    },
    [supportsDeletedAndHistory]
  );

  const isTransactionEdited = React.useCallback(
    (record: Record<string, unknown>) => {
      if (!supportsDeletedAndHistory) return false;
      const row = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      return isTransactionEditedRow(
        row ?? {
          reversalType: record?.reversalType,
          journalModifiedDate: record?.journalModifiedDate,
          linkedTransactionId: record?.linkedTransactionId,
        }
      );
    },
    [supportsDeletedAndHistory]
  );

  const fileIdsWithPendingReview = React.useMemo(() => {
    const pending = new Set<string>();
    const rows = allExcelTransactionsPage?.transactions ?? [];
    rows.forEach((row) => {
      const reviewStatus = row?.reviewStatus;
      if (reviewStatus === ReviewStatus.REVIEWED) return;

      const rowWithFileRefs = row as {
        excelMetadataId?: string | null;
        importId?: string | null;
      };
      const excelMetadataId = rowWithFileRefs.excelMetadataId?.trim?.();
      const importId = rowWithFileRefs.importId?.trim?.();

      if (excelMetadataId) pending.add(excelMetadataId);
      if (importId) pending.add(importId);
    });
    return pending;
  }, [allExcelTransactionsPage?.transactions]);

  currentPageTableDataRef.current = displayedTransactionsTableData ?? [];

  /** Source type options: primary 4 + any other values from current data (for "allow user to use others"). */
  const sourceTypeOptionsWithExtras = React.useMemo(() => {
    const primaryOptions = getSourceTypeOptions(t);
    const primaryValues = new Set(primaryOptions.map((o) => o.value));
    const seen = new Set<string>(primaryValues);
    const extras: { label: string; value: string }[] = [];
    for (const r of allTransactionsTableData ?? []) {
      const st = r.sourceType;
      if (st != null && typeof st === 'string' && st.trim() && !seen.has(st)) {
        seen.add(st);
        extras.push({
          label: getSourceTypeDisplayLabel(st, t),
          value: st,
        });
      }
    }
    return [...primaryOptions, ...extras];
  }, [allTransactionsTableData, t]);

  const contactTypeOptions = React.useMemo(
    () => getContactTypeOptions(t),
    [t]
  );

  const isUuidInFileGroupEdit = React.useCallback(
    (rowUuid: string | undefined) => {
      if (!rowUuid || !fileGroupEditSession) return false;
      const normalized = String(rowUuid);
      return fileGroupEditSession.uuids.some((id) => String(id) === normalized);
    },
    [fileGroupEditSession]
  );

  const isRowInInlineEdit = React.useCallback(
    (rowUuid: string | undefined) => {
      if (!rowUuid) return false;
      if (editingTransactionId === rowUuid) {
        return true;
      }
      if (isExcelProvider) {
        return false;
      }
      return isUuidInFileGroupEdit(rowUuid);
    },
    [isExcelProvider, editingTransactionId, isUuidInFileGroupEdit]
  );

  const isFileGroupFullRowCellEdit = React.useCallback(
    (rowUuid: string | undefined) =>
      !isExcelProvider &&
      isUuidInFileGroupEdit(rowUuid) &&
      Boolean(editingColumnKeys?.has('__all__')),
    [isExcelProvider, isUuidInFileGroupEdit, editingColumnKeys]
  );

  const getInlineRowDraft = React.useCallback(
    (
      rowUuid: string | undefined,
      original?: ValidatedExcelTransaction
    ): Partial<ValidatedExcelTransaction> | null => {
      if (!rowUuid) return null;
      if (!isExcelProvider && isUuidInFileGroupEdit(rowUuid)) {
        const key =
          fileGroupEditSession!.uuids.find(
            (id) => String(id) === String(rowUuid)
          ) ?? rowUuid;
        const draft = fileGroupDraftMap[key];
        if (draft) {
          return { ...(original ?? {}), ...draft };
        }
        return original ?? null;
      }
      if (editingTransactionId === rowUuid && editingDraft) {
        return editingDraft;
      }
      if (isExcelProvider) {
        return editingTransactionId === rowUuid ? editingDraft : null;
      }
      return null;
    },
    [
      isExcelProvider,
      editingTransactionId,
      editingDraft,
      fileGroupEditSession,
      fileGroupDraftMap,
      isUuidInFileGroupEdit,
    ]
  );

  const patchInlineRowDraft = React.useCallback(
    (
      rowUuid: string | undefined,
      updater: (
        prev: Partial<ValidatedExcelTransaction>
      ) => Partial<ValidatedExcelTransaction> | null
    ) => {
      if (!rowUuid) return;
      if (!isExcelProvider && isUuidInFileGroupEdit(rowUuid)) {
        const draftKey =
          fileGroupEditSession!.uuids.find(
            (id) => String(id) === String(rowUuid)
          ) ?? rowUuid;
        setFileGroupDraftMap((prev) => {
          const original = allTransactionsTableData?.find(
            (r) => String(r.__transactionUuid) === String(draftKey)
          )?.__transactionRow as ValidatedExcelTransaction | undefined;
          const current = { ...(original ?? {}), ...(prev[draftKey] ?? {}) };
          const next = updater(current);
          if (next == null) return prev;

          const result: Record<
            string,
            Partial<ValidatedExcelTransaction>
          > = { ...prev, [draftKey]: next };

          // Mirror drawer Document details: shared fields apply to every line
          // in the same-document edit session.
          const sharedPatch = extractDocumentSharedFieldPatch(current, next);
          if (sharedPatch && fileGroupEditSession) {
            const deriveTaxInclusive =
              showQuantityUnitPriceColumns &&
              Object.prototype.hasOwnProperty.call(sharedPatch, 'taxInclusive');
            for (const id of fileGroupEditSession.uuids) {
              const siblingId = String(id);
              if (siblingId === String(draftKey)) continue;
              const siblingOriginal = allTransactionsTableData?.find(
                (r) => String(r.__transactionUuid) === siblingId
              )?.__transactionRow as ValidatedExcelTransaction | undefined;
              const siblingCurrent = {
                ...(siblingOriginal ?? {}),
                ...(prev[siblingId] ?? {}),
              };
              let siblingNext: Partial<ValidatedExcelTransaction> = {
                ...siblingCurrent,
                ...sharedPatch,
              };
              if (deriveTaxInclusive) {
                siblingNext = applyCalculatedGrossAndNet(siblingNext);
              }
              result[siblingId] = siblingNext;
            }
          }

          return result;
        });
        return;
      }
      if (editingTransactionId === rowUuid && editingDraft) {
        setEditingDraft((prev) => {
          if (!prev) return prev;
          const next = updater(prev);
          return next ?? prev;
        });
        return;
      }
      if (isExcelProvider) {
        if (editingTransactionId !== rowUuid) return;
        setEditingDraft((prev) => {
          if (!prev) return prev;
          const next = updater(prev);
          return next ?? prev;
        });
      }
    },
    [
      isExcelProvider,
      editingTransactionId,
      editingDraft,
      fileGroupEditSession,
      isUuidInFileGroupEdit,
      allTransactionsTableData,
      showQuantityUnitPriceColumns,
    ]
  );

  const hasFileGroupChanges = React.useCallback(() => {
    if (!fileGroupEditSession) return false;
    for (const id of fileGroupEditSession.uuids) {
      const original = allTransactionsTableData?.find(
        (r) => String(r.__transactionUuid) === String(id)
      )?.__transactionRow as ValidatedExcelTransaction | undefined;
      const draft = {
        ...(original ?? {}),
        ...(fileGroupDraftMap[id] ?? {}),
      };
      if (hasRowChanges(draft, original)) return true;
    }
    return false;
  }, [fileGroupEditSession, fileGroupDraftMap, allTransactionsTableData]);

  const syncFileGroupEditFromTableData = React.useCallback(
    (seed: ValidatedExcelTransaction, preserveDraftChanges: boolean) => {
      const txs = collectTransactionsSameDocument(
        allTransactionsTableData ?? [],
        seed
      );
      if (txs.length === 0) return;

      const uuids = txs
        .map((t) => (t.uuid != null ? String(t.uuid) : ''))
        .filter(Boolean);

      setFileGroupEditSession({
        seedUuid: String(seed.uuid),
        uuids,
      });
      setFileGroupDraftMap((prev) => {
        const next: Record<string, Partial<ValidatedExcelTransaction>> = {};
        for (const t of txs) {
          if (!t.uuid) continue;
          const id = String(t.uuid);
          next[id] =
            preserveDraftChanges && prev[id] ? { ...prev[id] } : { ...t };
        }
        return next;
      });
      setEditingColumnKeys(new Set(fileGroupEditColumnKeysRef.current));
      pendingFileGroupEditRef.current = false;
    },
    [allTransactionsTableData]
  );

  React.useEffect(() => {
    const seed = fileGroupEditSeedRef.current;
    if (!seed) return;

    if (pendingFileGroupEditRef.current) {
      syncFileGroupEditFromTableData(seed, false);
      return;
    }

    if (!fileGroupEditSession) return;

    const txs = collectTransactionsSameDocument(
      allTransactionsTableData ?? [],
      seed
    );
    if (txs.length === 0) return;

    const nextUuids = txs
      .map((t) => (t.uuid != null ? String(t.uuid) : ''))
      .filter(Boolean);
    const currentUuids = fileGroupEditSession.uuids;
    const sameUuids =
      nextUuids.length === currentUuids.length &&
      nextUuids.every((id) =>
        currentUuids.some((cur) => String(cur) === String(id))
      );
    if (sameUuids) return;

    syncFileGroupEditFromTableData(seed, true);
  }, [
    allTransactionsTableData,
    selectedImportIds,
    fileGroupEditSession,
    syncFileGroupEditFromTableData,
  ]);

  const startNonExcelFileGroupEdit = React.useCallback(
    (
      record: Record<string, unknown>,
      uuid: string,
      original: ValidatedExcelTransaction,
      options?: { columnKeys?: string[]; openAttachment?: boolean }
    ) => {
      const nextColumnKeys =
        options?.columnKeys && options.columnKeys.length > 0
          ? new Set(options.columnKeys)
          : new Set(['__all__']);
      const shouldOpenAttachment = options?.openAttachment ?? true;
      fileGroupEditColumnKeysRef.current = nextColumnKeys;

      if (isInlineFileGroupEditing && isUuidInFileGroupEdit(uuid)) {
        fileGroupEditSeedRef.current = original;
        setEditingTransactionId(uuid);
        setEditingColumnKeys(new Set(nextColumnKeys));
        if (shouldOpenAttachment) {
          onAttachmentClick(record);
        }
        return;
      }

      if (isInlineFileGroupEditing && hasFileGroupChanges()) {
        setPendingRowSwitch({
          uuid,
          original,
          dataKey: options?.columnKeys?.[0],
          mode: options?.columnKeys?.length === 1 ? 'cell' : 'fullRow',
        });
        return;
      }

      clearFileGroupEdit();
      fileGroupEditSeedRef.current = original;
      setEditingTransactionId(uuid);
      setEditingDraft(null);
      setEditingColumnKeys(new Set(['__all__']));

      const fileId = getTransactionFileFilterId(original, excelFiles);
      const fileRowCount = resolveInlineEditFileRowCount(fileId);
      const alreadyOnlyThisFile =
        !!fileId &&
        selectedImportIds.length === 1 &&
        selectedImportIds[0] === fileId;
      const loadedCount = collectTransactionsSameDocument(
        currentPageTableDataRef.current,
        original
      ).length;
      const fullFileAlreadyLoaded = isDocumentFileFullyLoadedForEdit(
        loadedCount,
        fileRowCount,
        {
          fetchedAtFullFilePageSize:
            fileRowCount != null &&
            inlineEditFilePageSizeOverride != null &&
            inlineEditFilePageSizeOverride >= fileRowCount,
        }
      );

      if (!fileId) {
        resetInlineEditFilePageSizeOverride();
        setFileGroupEditSession({ seedUuid: uuid, uuids: [uuid] });
        setFileGroupDraftMap({ [uuid]: { ...original } });
        if (shouldOpenAttachment) {
          onAttachmentClick(record);
        }
        return;
      }

      if (fileRowCount != null) {
        setInlineEditFilePageSizeOverride(fileRowCount);
      } else {
        resetInlineEditFilePageSizeOverride();
      }

      if (alreadyOnlyThisFile && fullFileAlreadyLoaded) {
        syncFileGroupEditFromTableData(original, false);
        setEditingTransactionId(uuid);
        setEditingColumnKeys(new Set(nextColumnKeys));
      } else {
        pendingFileGroupEditRef.current = true;
        if (fileRowCount != null && organisationId) {
          skipNextFetchRef.current = true;
          dispatch(
            fetchAllExcelTransactionsRequest(organisationId, 1, fileRowCount, {
              ...(applySelectTransactionUuidApiFilter
                ? appendSelectTransactionUuidFilter(
                    buildTransactionsRefreshFilters({
                      tableFilters: currentFiltersState,
                      tabFileImportIds: [fileId],
                    }),
                    selectTransactionFilterId!
                  )
                : buildTransactionsRefreshFilters({
                    tableFilters: currentFiltersState,
                    tabFileImportIds: [fileId],
                  })),
            })
          );
        }
        setSelectedImportIds([fileId]);
        setCurrentPage(1);
        if (fileRowCount != null && organisationId) {
          skipNextFetchRef.current = true;
          dispatch(
            fetchAllExcelTransactionsRequest(organisationId, 1, fileRowCount, {
              ...(applySelectTransactionUuidApiFilter
                ? appendSelectTransactionUuidFilter(
                    buildTransactionsRefreshFilters({
                      tableFilters: currentFiltersState,
                      tabFileImportIds: [fileId],
                    }),
                    selectTransactionFilterId!
                  )
                : buildTransactionsRefreshFilters({
                    tableFilters: currentFiltersState,
                    tabFileImportIds: [fileId],
                  })),
            })
          );
        }
      }

      if (shouldOpenAttachment) {
        onAttachmentClick(record);
      }
    },
    [
      isInlineFileGroupEditing,
      isUuidInFileGroupEdit,
      hasFileGroupChanges,
      clearFileGroupEdit,
      excelFiles,
      resolveInlineEditFileRowCount,
      resetInlineEditFilePageSizeOverride,
      selectedImportIds,
      inlineEditFilePageSizeOverride,
      syncFileGroupEditFromTableData,
      onAttachmentClick,
      organisationId,
      dispatch,
      currentFiltersState,
    ]
  );

  const handlePersistedEditInDrawerChange = React.useCallback(
    (nextEditInDrawer: boolean) => {
      editInDrawerRef.current = nextEditInDrawer;
      if (resolvedOrganisationId?.trim()) {
        handlePreferences(PreferenceActions.SET, {
          name: `${ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF}-${resolvedOrganisationId}`,
          value: nextEditInDrawer,
        });
      }
      setEditInDrawer(nextEditInDrawer);

      if (nextEditInDrawer) {
        clearInlineTableEditState();

        if (attachmentsTransactionId === NOT_CLICKED_ATTACHMENT) return;

        const reviewDrawerOpen =
          drawerOpen && drawerData?.type === DrawerType.EDIT;
        if (reviewDrawerOpen) return;

        const txId = attachmentsTransactionId;
        const record = currentPageTableDataRef.current.find(
          (r) => String(r.__transactionUuid ?? '') === String(txId)
        );
        if (!record) return;

        const original = record.__transactionRow as
          | ValidatedExcelTransaction
          | undefined;
        if (!original) return;

        void openEditDrawerForTransaction(original).then(() => {
          setReviewDrawerRelaunchKey((key) => key + 1);
        });
        return;
      }

      if (attachmentsTransactionId === NOT_CLICKED_ATTACHMENT) return;

      // Drawer ? table: close drawer first and block pending reopen before table edit runs.
      pendingAttachmentEditDrawerRef.current = null;
      dispatch(turnDrawerOff());
      editDrawerOpenFromAttachmentRef.current = false;

      const txId = attachmentsTransactionId;
      const record = currentPageTableDataRef.current.find(
        (r) => String(r.__transactionUuid ?? '') === String(txId)
      );
      if (!record) return;

      const uuid = String(record.__transactionUuid ?? record.uuid ?? '');
      const original = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (!uuid || !original) return;

      if (!isExcelProvider) {
        startNonExcelFileGroupEdit(record, uuid, original);
        return;
      }

      setEditingTransactionId(uuid);
      setEditingDraft({ ...original });
      setEditingColumnKeys(new Set(['__all__']));
    },
    [
      resolvedOrganisationId,
      handlePreferences,
      attachmentsTransactionId,
      dispatch,
      drawerOpen,
      drawerData?.type,
      isExcelProvider,
      startNonExcelFileGroupEdit,
      clearInlineTableEditState,
      openEditDrawerForTransaction,
    ]
  );

  React.useLayoutEffect(() => {
    handlePersistedEditInDrawerChangeRef.current =
      handlePersistedEditInDrawerChange;
  }, [handlePersistedEditInDrawerChange]);

  const handleEditTransaction = React.useCallback(
    (record: Record<string, unknown>) => {
      if (isTransactionLockedByPostedFile(record)) return;
      if (isTransactionDeleted(record)) return;
      const uuid = (record.__transactionUuid ?? record.uuid) as
        | string
        | undefined;
      if (!uuid) return;

      const original = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (!original) return;

      // Unsaved manual row: always inline-edit (no drawer / attachments yet).
      if (isClientOnlyManualTransactionId(uuid)) {
        setEditingTransactionId(uuid);
        setEditingDraft(original);
        setEditingColumnKeys(new Set(['__all__']));
        return;
      }

      // Respect "edit in drawer" switch (all providers except Group).
      // If switch is on, open drawer (with attachment flow). If off, use inline table edit.
      const isReviewMode =
        isReceiptsProvider && transactionQualifiesForMarkAsReviewed(original);
      if (showEditInDrawerSetting && connectionId && editInDrawer) {
        void openEditDrawerForTransaction(original);
        return;
      }

      if (!isExcelProvider) {
        startNonExcelFileGroupEdit(record, uuid, original);
        return;
      }

      // Excel: single-row inline edit
      if (editingTransactionId != null && uuid !== editingTransactionId) {
        const currentOriginal = allTransactionsTableData?.find(
          (r) => r.__transactionUuid === editingTransactionId
        )?.__transactionRow as ValidatedExcelTransaction | undefined;
        if (!hasRowChanges(editingDraft, currentOriginal)) {
          setEditingTransactionId(uuid);
          setEditingDraft(original);
          setEditingColumnKeys(new Set(['__all__']));
          if (isReviewMode) onAttachmentClick(record);
          return;
        }
        setPendingRowSwitch({ uuid, original, mode: 'fullRow' });
        return;
      }

      setEditingTransactionId(uuid);
      setEditingDraft(original);
      setEditingColumnKeys(new Set(['__all__']));
      if (isReviewMode) onAttachmentClick(record);
    },
    [
      connectionId,
      editInDrawer,
      isExcelProvider,
      isReceiptsProvider,
      showEditInDrawerSetting,
      organisation?.providerName,
      dispatch,
      editingTransactionId,
      editingDraft,
      allTransactionsTableData,
      onAttachmentClick,
      isTransactionLockedByPostedFile,
      isTransactionDeleted,
      startNonExcelFileGroupEdit,
      openEditDrawerForTransaction,
    ]
  );

  const handleCellDoubleClick = React.useCallback(
    (record: Record<string, unknown>, dataKey: string) => {
      if (isTransactionLockedByPostedFile(record)) return;
      if (isTransactionDeleted(record)) return;
      // Tax rate is derived from tax name and cannot be edited directly
      if (dataKey === 'taxRate') return;
      if (
        isEntityTransactionColumnReadOnly(
          dataKey,
          showQuantityUnitPriceColumns
        )
      )
        return;

      const uuid = (record.__transactionUuid ?? record.uuid) as
        | string
        | undefined;
      if (!uuid) return;

      const original = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (!original) return;

      if (!isExcelProvider) {
        if (uuid === editingTransactionId) {
          setEditingColumnKeys((prev) => {
            if (!prev || prev.has('__all__')) return prev;
            const next = new Set(prev);
            next.add(dataKey);
            return next;
          });
          return;
        }

        if (editingTransactionId != null) {
          const currentOriginal = allTransactionsTableData?.find(
            (r) => r.__transactionUuid === editingTransactionId
          )?.__transactionRow as ValidatedExcelTransaction | undefined;
          if (!hasRowChanges(editingDraft, currentOriginal)) {
            setEditingTransactionId(uuid);
            setEditingDraft(original);
            setEditingColumnKeys(new Set([dataKey]));
            return;
          }
          setPendingRowSwitch({ uuid, original, dataKey, mode: 'cell' });
          return;
        }

        setEditingTransactionId(uuid);
        setEditingDraft(original);
        setEditingColumnKeys(new Set([dataKey]));
        return;
      }

      // Same row: add the column to the editable set
      if (uuid === editingTransactionId) {
        setEditingColumnKeys((prev) => {
          if (!prev || prev.has('__all__')) return prev; // already fully editing
          const next = new Set(prev);
          next.add(dataKey);
          return next;
        });
        return;
      }

      // Different row while editing: prompt to save/discard first only if there are changes
      if (editingTransactionId != null) {
        const currentOriginal = allTransactionsTableData?.find(
          (r) => r.__transactionUuid === editingTransactionId
        )?.__transactionRow as ValidatedExcelTransaction | undefined;
        if (!hasRowChanges(editingDraft, currentOriginal)) {
          setEditingTransactionId(uuid);
          setEditingDraft(original);
          setEditingColumnKeys(new Set([dataKey]));
          return;
        }
        setPendingRowSwitch({ uuid, original, dataKey, mode: 'cell' });
        return;
      }

      // No row editing yet: start fresh for this cell
      setEditingTransactionId(uuid);
      setEditingDraft(original);
      setEditingColumnKeys(new Set([dataKey]));
    },
    [
      isExcelProvider,
      editingTransactionId,
      editingDraft,
      allTransactionsTableData,
      isTransactionLockedByPostedFile,
      isTransactionDeleted,
      showQuantityUnitPriceColumns,
    ]
  );

  const handleCancelEditTransaction = React.useCallback(() => {
    if (isInlineFileGroupEditing) {
      const attachmentInGroup =
        attachmentsTransactionId !== NOT_CLICKED_ATTACHMENT &&
        isUuidInFileGroupEdit(attachmentsTransactionId);
      if (attachmentInGroup) {
        onCloseAttachments();
      }
      resetInlineEditFilePageSizeOverride();
      clearFileGroupEdit();
      resetInlineEditFilePageSizeOverride();
      setEditingTransactionId(null);
      setEditingDraft(null);
      setEditingColumnKeys(null);
      return;
    }
    if (attachmentsTransactionId === editingTransactionId) {
      onCloseAttachments();
    }
    if (isClientOnlyManualTransactionId(editingTransactionId)) {
      setPendingManualEntry(null);
    }
    resetInlineEditFilePageSizeOverride();
    setEditingTransactionId(null);
    setEditingDraft(null);
    setEditingColumnKeys(null);
  }, [
    attachmentsTransactionId,
    editingTransactionId,
    onCloseAttachments,
    isInlineFileGroupEditing,
    isUuidInFileGroupEdit,
    clearFileGroupEdit,
    resetInlineEditFilePageSizeOverride,
  ]);

  const handleAddManualTransactionRow = React.useCallback(() => {
    if (!organisationId || showDeletedTransactions) return;
    if (!isExcelProvider && !isReceiptsOnlyProvider) return;
    if (editingTransactionId != null || isInlineFileGroupEditing) return;

    if (pendingManualEntry?.uuid) {
      setEditingTransactionId(String(pendingManualEntry.uuid));
      setEditingDraft({ ...pendingManualEntry });
      setEditingColumnKeys(new Set(['__all__']));
      return;
    }

    const blank = createBlankManualExcelTransaction({
      applicationConnectionId: organisationId,
      currency: organisation?.currency ?? '',
      country: organisation?.country ?? '',
      isExcelTransaction: isExcelProvider,
    });
    setPendingManualEntry(blank);
    setEditingTransactionId(String(blank.uuid));
    setEditingDraft({ ...blank });
    setEditingColumnKeys(new Set(['__all__']));
  }, [
    organisationId,
    showDeletedTransactions,
    editingTransactionId,
    isInlineFileGroupEditing,
    pendingManualEntry,
    organisation?.currency,
    organisation?.country,
    isExcelProvider,
    isReceiptsOnlyProvider,
  ]);

  const handleSaveTransaction = React.useCallback(
    async (record: Record<string, unknown>) => {
      if (!organisationId) return;

      const page = currentPage || 1;
      const pageSize = activeTablePageSize;
      const saveRefresh = getSaveRefreshParams(page, pageSize);

      const closeAttachmentPanelIfInGroup = () => {
        const attachmentInGroup =
          attachmentsTransactionId !== NOT_CLICKED_ATTACHMENT &&
          isUuidInFileGroupEdit(attachmentsTransactionId);
        if (attachmentInGroup) {
          onCloseAttachments();
        }
      };

      if (!isExcelProvider && fileGroupEditSession) {
        const payloads = fileGroupEditSession.uuids
          .map((id) => {
            const rowRecord = allTransactionsTableData?.find(
              (r) => String(r.__transactionUuid) === String(id)
            );
            const original = rowRecord?.__transactionRow as
              | ValidatedExcelTransaction
              | undefined;
            if (!original) return null;
            const draft = {
              ...(original as ExcelTransaction),
              ...(fileGroupDraftMap[id] ?? {}),
            } as ValidatedExcelTransaction;
            const isReview =
              isReceiptsProvider &&
              transactionQualifiesForMarkAsReviewed(original);
            return {
              transactionId: id,
              original,
              draft,
              markAsReviewed: isReview,
            };
          })
          .filter((p): p is NonNullable<typeof p> => p != null);

        if (payloads.length === 0) return;

        const anyChanges = payloads.some((p) =>
          hasRowChanges(p.draft, p.original)
        );
        const anyReviewOnly = payloads.some(
          (p) => p.markAsReviewed && !hasRowChanges(p.draft, p.original)
        );

        if (!anyChanges && !anyReviewOnly) {
          closeAttachmentPanelIfInGroup();
          resetInlineEditFilePageSizeOverride();
          clearFileGroupEdit();
          resetInlineEditFilePageSizeOverride();
          setEditingTransactionId(null);
          setEditingDraft(null);
          setEditingColumnKeys(null);
          return;
        }

        const prepared = prepareDrawerTransactionSaveBatch({
          payloads,
          bodyParams: {
            showTrackingCategoryColumns,
            trackingCategories: organisationTrackingCategories,
            isAccountIdProvider,
            accountNameByIdMap,
            accountNameOptions,
            accountNameOptionsGrouped,
            itemNameByIdMap,
            transactionItemOptions,
            taxRateByIdMap,
            deriveGrossNetFromQuantityUnitPrice: showQuantityUnitPriceColumns,
            preserveOriginalKeys: Array.from(
              DRAWER_EXCLUDED_FIELD_KEYS
            ) as (keyof ExcelTransaction)[],
          },
          validationOptions: transactionEditSaveValidation,
          enhanceBody: (body, origRow) =>
            origRow.reviewStatus === ReviewStatus.NOT_REVIEWED ||
            origRow.reviewStatus === ReviewStatus.NONE
              ? { ...body, reviewStatus: ReviewStatus.REVIEWED }
              : body,
          resolveMarkAsReviewed: (p) =>
            isReceiptsProvider &&
            p.markAsReviewed === true &&
            transactionQualifiesForMarkAsReviewed(p.original),
        });

        if (!prepared.ok) {
          showDrawerBulkSaveValidationError(
            prepared.errorMessage,
            payloads.length
          );
          return;
        }

        const reviewIds = payloads
          .filter(
            (payload) =>
              isReceiptsProvider &&
              transactionQualifiesForMarkAsReviewed(payload.original)
          )
          .map((payload) => payload.transactionId)
          .filter(Boolean);
        const changedTransactionIds = new Set(
          payloads
            .filter((payload) => hasRowChanges(payload.draft, payload.original))
            .map((payload) => payload.transactionId)
        );
        const itemsToSave = prepared.items.filter((item) =>
          changedTransactionIds.has(item.transactionUuid)
        );
        if (reviewIds.length > 0) {
          queueActiveTabRefreshAfterReview();
        }
        skipNextFetchRef.current = true;
        dispatch(
          saveExcelDrawerTransactionsBatchRequest(organisationId, itemsToSave, {
            ...saveRefresh,
            ...(reviewIds.length > 0 ? { markAsReviewedIds: reviewIds } : {}),
          })
        );

        closeAttachmentPanelIfInGroup();
        resetInlineEditFilePageSizeOverride();
        clearFileGroupEdit();
        resetInlineEditFilePageSizeOverride();
        setEditingTransactionId(null);
        setEditingDraft(null);
        setEditingColumnKeys(null);
        return;
      }

      if (!editingDraft || !editingTransactionId) return;

      const original = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (!original) return;

      const closeAttachmentPanelIfThisRow = () => {
        if (attachmentsTransactionId === editingTransactionId) {
          onCloseAttachments();
        }
      };

      const isReviewModeRow =
        isReceiptsProvider && transactionQualifiesForMarkAsReviewed(original);

      // New manual row: POST create (API expects List<ExcelJournalDTO>).
      if (isClientOnlyManualTransactionId(editingTransactionId)) {
        const body = buildTransactionSaveBody({
          original: original as ExcelTransaction,
          draft: editingDraft as ExcelTransaction,
          showTrackingCategoryColumns,
          trackingCategories: organisationTrackingCategories,
          isAccountIdProvider,
          accountNameByIdMap,
          accountNameOptions,
          accountNameOptionsGrouped,
          itemNameByIdMap,
          transactionItemOptions,
          taxRateByIdMap,
          deriveGrossNetFromQuantityUnitPrice: showQuantityUnitPriceColumns,
        });
        const createBody: ExcelTransaction = { ...body, uuid: null };
        // FE-only field from ValidatedExcelTransaction — not part of ExcelJournalDTO.
        delete (createBody as { errorReason?: string }).errorReason;
        const missingRequired = validateTransactionEditSave(
          createBody,
          transactionEditSaveValidation
        );
        if (missingRequired.length > 0) {
          showTransactionEditValidationError(missingRequired);
          return;
        }
        skipNextFetchRef.current = true;
        dispatch(
          addExcelTransactionsRequest(organisationId, [createBody], {
            page: saveRefresh.page,
            pageSize: saveRefresh.pageSize,
            filters: saveRefresh.filters,
            tabFileImportIds: saveRefresh.tabFileImportIds,
          })
        );
        setPendingManualEntry(null);
        closeAttachmentPanelIfThisRow();
        setEditingTransactionId(null);
        setEditingDraft(null);
        setEditingColumnKeys(null);
        return;
      }

      if (!hasRowChanges(editingDraft, original)) {
        if (isReviewModeRow) {
          const body = buildTransactionSaveBody({
            original: original as ExcelTransaction,
            draft: editingDraft as ExcelTransaction,
            showTrackingCategoryColumns,
            trackingCategories: organisationTrackingCategories,
            isAccountIdProvider,
            accountNameByIdMap,
            accountNameOptions,
            accountNameOptionsGrouped,
            itemNameByIdMap,
            transactionItemOptions,
            taxRateByIdMap,
            deriveGrossNetFromQuantityUnitPrice: showQuantityUnitPriceColumns,
          });
          const missingRequired = validateTransactionEditSave(
            body,
            transactionEditSaveValidation
          );
          if (missingRequired.length > 0) {
            showTransactionEditValidationError(
              missingRequired,
              'Review status was not changed.'
            );
            return;
          }
          queueActiveTabRefreshAfterReview();
          skipNextFetchRef.current = true;
          dispatch(
            setTransactionsReviewedRequest(
              organisationId,
              [editingTransactionId],
              page,
              pageSize,
              saveRefresh.filters,
              saveRefresh.tabFileImportIds
            )
          );
        }
        closeAttachmentPanelIfThisRow();
        setEditingTransactionId(null);
        setEditingDraft(null);
        setEditingColumnKeys(null);
        return;
      }

      const clearSingleRowEdit = () => {
        closeAttachmentPanelIfThisRow();
        setEditingTransactionId(null);
        setEditingDraft(null);
        setEditingColumnKeys(null);
      };

      const saveBodyParams = {
        showTrackingCategoryColumns,
        trackingCategories: organisationTrackingCategories,
        isAccountIdProvider,
        accountNameByIdMap,
        accountNameOptions,
        accountNameOptionsGrouped,
        itemNameByIdMap,
        transactionItemOptions,
        taxRateByIdMap,
        deriveGrossNetFromQuantityUnitPrice: showQuantityUnitPriceColumns,
      };

      /**
       * Non-Excel single-cell edit: keep one-row UI, but fan out document-shared
       * fields on save (mass update; taxInclusive uses full-body batch + totals).
       */
      if (!isExcelProvider) {
        const sharedPatch = extractDocumentSharedFieldPatch(
          original,
          editingDraft
        );
        const perLineChanged = hasPerLineRowChanges(editingDraft, original, {
          treatGrossNetAsDerived: showQuantityUnitPriceColumns,
        });
        const taxInclusiveChanged =
          sharedPatch != null &&
          Object.prototype.hasOwnProperty.call(sharedPatch, 'taxInclusive');

        if (sharedPatch) {
          let siblings = collectTransactionsSameDocument(
            allTransactionsTableData ?? [],
            original
          );
          const fileId = getTransactionFileFilterId(original, excelFiles);
          const fileRowCount = resolveInlineEditFileRowCount(fileId);
          if (
            fileId &&
            fileRowCount != null &&
            siblings.length < fileRowCount
          ) {
            try {
              const refreshFilters = buildTransactionsRefreshFilters({
                tableFilters: {},
                tabFileImportIds: [fileId],
              });
              const { getAllExcelTransactions } = await import(
                'store/organisation/api'
              );
              const response = await getAllExcelTransactions(
                organisationId,
                1,
                fileRowCount,
                convertScopeTableFiltersToApiBody(refreshFilters)
              );
              const content = response?.data?.transactions?.content ?? [];
              const fetched: ValidatedExcelTransaction[] = content.map(
                (row) => ({
                  ...normalizeExcelTransactionFromApi(row),
                  errorReason: '',
                })
              );
              if (fetched.length > 0) {
                siblings = collectTransactionsSameDocument(
                  fetched.map((row) => ({ __transactionRow: row })),
                  original
                );
              }
            } catch {
              // Fall back to loaded same-document rows.
            }
          }
          if (siblings.length === 0) {
            siblings = [original];
          }
          const siblingIds = siblings
            .map((row) => String(row.uuid ?? '').trim())
            .filter(Boolean);

          if (taxInclusiveChanged) {
            const payloads = siblings.map((sibling) => {
              const id = String(sibling.uuid ?? '');
              let draft: Partial<ValidatedExcelTransaction> =
                id === String(editingTransactionId)
                  ? { ...sibling, ...editingDraft }
                  : { ...sibling, ...sharedPatch };
              if (showQuantityUnitPriceColumns) {
                draft = applyCalculatedGrossAndNet(draft);
              }
              return {
                transactionId: id,
                original: sibling,
                draft,
                markAsReviewed:
                  isReviewModeRow && id === String(editingTransactionId),
              };
            });

            const prepared = prepareDrawerTransactionSaveBatch({
              payloads,
              bodyParams: {
                ...saveBodyParams,
                preserveOriginalKeys: Array.from(
                  DRAWER_EXCLUDED_FIELD_KEYS
                ) as (keyof ExcelTransaction)[],
              },
              validationOptions: transactionEditSaveValidation,
              enhanceBody: (body, origRow) =>
                isReviewModeRow &&
                String(origRow.uuid) === String(editingTransactionId) &&
                (origRow.reviewStatus === ReviewStatus.NOT_REVIEWED ||
                  origRow.reviewStatus === ReviewStatus.NONE)
                  ? { ...body, reviewStatus: ReviewStatus.REVIEWED }
                  : body,
              resolveMarkAsReviewed: (p) =>
                isReceiptsProvider &&
                p.markAsReviewed === true &&
                transactionQualifiesForMarkAsReviewed(p.original),
            });

            if (!prepared.ok) {
              showDrawerBulkSaveValidationError(
                prepared.errorMessage,
                payloads.length
              );
              return;
            }

            const reviewIds = payloads
              .filter(
                (payload) =>
                  isReceiptsProvider &&
                  payload.markAsReviewed === true &&
                  transactionQualifiesForMarkAsReviewed(payload.original)
              )
              .map((payload) => payload.transactionId)
              .filter(Boolean);
            const changedTransactionIds = new Set(
              payloads
                .filter((payload) =>
                  hasRowChanges(payload.draft, payload.original)
                )
                .map((payload) => payload.transactionId)
            );
            const itemsToSave = prepared.items.filter((item) =>
              changedTransactionIds.has(item.transactionUuid)
            );
            if (itemsToSave.length === 0) {
              clearSingleRowEdit();
              return;
            }
            if (reviewIds.length > 0) {
              queueActiveTabRefreshAfterReview();
            }
            skipNextFetchRef.current = true;
            dispatch(
              saveExcelDrawerTransactionsBatchRequest(
                organisationId,
                itemsToSave,
                {
                  ...saveRefresh,
                  ...(reviewIds.length > 0
                    ? { markAsReviewedIds: reviewIds }
                    : {}),
                }
              )
            );
            clearSingleRowEdit();
            return;
          }

          const massUpdates =
            buildDocumentSharedMassUpdatePayload(sharedPatch);
          if (massUpdates && siblingIds.length > 0) {
            skipNextFetchRef.current = true;
            if (isReviewModeRow && !perLineChanged) {
              queueActiveTabRefreshAfterReview();
            }
            dispatch(
              bulkUpdateExcelTransactionsRequest(
                organisationId,
                siblingIds,
                massUpdates,
                {
                  page,
                  pageSize,
                  filters: saveRefresh.filters,
                  tabFileImportIds: saveRefresh.tabFileImportIds,
                }
              )
            );
            if (isReviewModeRow && !perLineChanged) {
              dispatch(
                setTransactionsReviewedRequest(
                  organisationId,
                  [editingTransactionId],
                  page,
                  pageSize,
                  saveRefresh.filters,
                  saveRefresh.tabFileImportIds
                )
              );
            }
            if (!perLineChanged) {
              clearSingleRowEdit();
              return;
            }
            // Mixed: shared mass-update already dispatched; continue with
            // per-line single-row save below.
          } else if (!perLineChanged) {
            clearSingleRowEdit();
            return;
          }
        }
      }

      const body = buildTransactionSaveBody({
        original: original as ExcelTransaction,
        draft: editingDraft as ExcelTransaction,
        ...saveBodyParams,
      });
      const missingRequired = validateTransactionEditSave(
        body,
        transactionEditSaveValidation
      );
      if (missingRequired.length > 0) {
        showTransactionEditValidationError(missingRequired);
        return;
      }

      skipNextFetchRef.current = true;
      if (isReviewModeRow) {
        queueActiveTabRefreshAfterReview();
      }
      dispatch(
        updateExcelTransactionRequest(
          organisationId,
          editingTransactionId,
          body,
          {
            ...saveRefresh,
            ...(isReviewModeRow
              ? { markAsReviewedIds: [editingTransactionId] }
              : {}),
          }
        )
      );

      clearSingleRowEdit();
    },
    [
      isExcelProvider,
      fileGroupEditSession,
      fileGroupDraftMap,
      editingDraft,
      editingTransactionId,
      organisationId,
      attachmentsTransactionId,
      onCloseAttachments,
      currentPage,
      activeTablePageSize,
      currentFiltersState,
      getSaveRefreshParams,
      allTransactionsTableData,
      excelFiles,
      resolveInlineEditFileRowCount,
      dispatch,
      isReceiptsProvider,
      rightPanelTransactionTab,
      isAccountIdProvider,
      accountNameByIdMap,
      accountNameOptions,
      accountNameOptionsGrouped,
      itemNameByIdMap,
      transactionItemOptions,
      organisationTrackingCategories,
      taxRateByIdMap,
      showTrackingCategoryColumns,
      showQuantityUnitPriceColumns,
      transactionEditSaveValidation,
      showTransactionEditValidationError,
      showDrawerBulkSaveValidationError,
      queueActiveTabRefreshAfterReview,
      isUuidInFileGroupEdit,
      clearFileGroupEdit,
      resetInlineEditFilePageSizeOverride,
    ]
  );

  const transactionsTableCustomFilterOptions = React.useMemo(
    () => (taxNameOptions.length > 0 ? { taxName: taxNameOptions } : undefined),
    [taxNameOptions]
  );

  const handleDeleteTransaction = React.useCallback(
    (record: Record<string, unknown>) => {
      if (isTransactionLockedByPostedFile(record)) return;
      if (isTransactionDeleted(record)) return;
      const uuid = (record.__transactionUuid ?? record.uuid) as
        | string
        | undefined;
      if (!uuid || !organisationId) return;
      if (isClientOnlyManualTransactionId(uuid)) {
        setPendingManualEntry(null);
        if (editingTransactionId === uuid) {
          setEditingTransactionId(null);
          setEditingDraft(null);
          setEditingColumnKeys(null);
        }
        return;
      }

      const page = currentPage || 1;
      const pageSize = activeTablePageSize;

      const baseFilters: Record<string, unknown> = {
        ...(currentFiltersState ?? {}),
      };
      if (requestTabFileImportIds && requestTabFileImportIds.length > 0) {
        baseFilters.excelMetadataId = { value: requestTabFileImportIds };
      } else {
        delete (baseFilters as any).excelMetadataId;
      }
      const mergedFilters = baseFilters;

      dispatch(
        deleteExcelTransactionRequest(organisationId, uuid, {
          page,
          pageSize,
          filters: mergedFilters,
        })
      );
    },
    [
      organisationId,
      currentPage,
      activeTablePageSize,
      currentFiltersState,
      requestTabFileImportIds,
      dispatch,
      isTransactionLockedByPostedFile,
      isTransactionDeleted,
      editingTransactionId,
    ]
  );

  const handleMarkTransactionReviewed = React.useCallback(
    (record: Record<string, unknown>) => {
      if (isTransactionDeleted(record)) return;
      const uuid = (record.__transactionUuid ?? record.uuid) as
        | string
        | undefined;
      if (!uuid || !organisationId) return;
      const original = record.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (!original) return;

      const body = buildTransactionSaveBody({
        original: original as ExcelTransaction,
        draft: {},
        showTrackingCategoryColumns,
        trackingCategories: organisationTrackingCategories,
        isAccountIdProvider,
        accountNameByIdMap,
        accountNameOptions,
        accountNameOptionsGrouped,
        itemNameByIdMap,
        transactionItemOptions,
        taxRateByIdMap,
      deriveGrossNetFromQuantityUnitPrice: showQuantityUnitPriceColumns,
      });
      const missingRequired = validateTransactionEditSave(
        body,
        transactionEditSaveValidation
      );
      if (missingRequired.length > 0) {
        showTransactionEditValidationError(
          missingRequired,
          'Review status was not changed.'
        );
        return;
      }

      const page = currentPage || 1;
      const pageSize = activeTablePageSize;
      const saveRefresh = getSaveRefreshParams(page, pageSize);
      queueActiveTabRefreshAfterReview();
      skipNextFetchRef.current = true;

      dispatch(
        setTransactionsReviewedRequest(
          organisationId,
          [uuid],
          page,
          pageSize,
          saveRefresh.filters,
          saveRefresh.tabFileImportIds
        )
      );
    },
    [
      organisationId,
      currentPage,
      activeTablePageSize,
      getSaveRefreshParams,
      dispatch,
      showTrackingCategoryColumns,
      isAccountIdProvider,
      accountNameByIdMap,
      accountNameOptions,
      taxRateByIdMap,
      transactionEditSaveValidation,
      showTransactionEditValidationError,
      queueActiveTabRefreshAfterReview,
      isTransactionDeleted,
    ]
  );

  const allTransactionsTableColumns = React.useMemo(() => {
    const displayNames = excelColumnNames.filter((name) => {
      const dataKey = VALIDATION_COLUMN_KEY_MAP[name];
      return (
        dataKey != null &&
        dataKey !== 'taxRate' &&
        dataKey !== 'accountType' &&
        dataKey !== 'contactType' &&
        !ENTITY_TRANSACTIONS_TABLE_EXCLUDED_COLUMN_KEYS.has(dataKey) &&
        !excelProviderHiddenColumnKeys.has(dataKey) &&
        (showTransactionItemColumns || dataKey !== 'itemId') &&
        (showQuantityUnitPriceColumns ||
          (dataKey !== 'quantity' &&
            dataKey !== 'unitPrice' &&
            dataKey !== 'taxInclusive'))
      );
    });
    const dataColumns = displayNames.map((name) => {
      const dataKey = VALIDATION_COLUMN_KEY_MAP[name];
      const isFixedRight =
        dataKey === 'gross' || dataKey === 'net' || dataKey === 'tax';
      const isAmountColumn =
        dataKey === 'gross' ||
        dataKey === 'net' ||
        dataKey === 'tax' ||
        dataKey === 'exchangeRate' ||
        dataKey === 'discountAmount' ||
        dataKey === 'quantity' ||
        dataKey === 'unitPrice';
      const shortTitle = getEntityTransactionColumnTitle(dataKey, t);
      const width = ENTITY_TRANSACTION_COLUMN_MIN_WIDTHS[dataKey] ?? 80;
      const isDateLabelColumn =
        dataKey === 'uploadDate' ||
        dataKey === 'dueDate' ||
        dataKey === 'transactionDate';
      return {
        title: shortTitle,
        dataIndex: dataKey,
        key: dataKey,
        width,
        fixed: isFixedRight ? ('right' as const) : undefined,
        // Mark numeric columns so table CSS can right-align their headers.
        className: [
          isAmountColumn ? 'transactions-amount-column' : '',
          isDateLabelColumn ? 'transactions-date-column' : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined,
        align: isAmountColumn ? ('right' as const) : undefined,
        // Temporarily disable sorting for Account Name / Item until backend sort
        // field alignment is fixed.
        sorter:
          dataKey === 'accountCode' || dataKey === 'itemId' ? false : true,
        sortIcon:
          dataKey === 'accountCode' || dataKey === 'itemId'
            ? undefined
            : renderSortIcon,
        render: (val: any, record: Record<string, unknown>) => {
          const rowUuid = record.__transactionUuid as string | undefined;
          const rowOriginal = record.__transactionRow as
            | ValidatedExcelTransaction
            | undefined;
          const rowDraft = getInlineRowDraft(rowUuid, rowOriginal);
          const isRowEditing = isRowInInlineEdit(rowUuid);
          const isDocumentGroupCellEdit = isFileGroupFullRowCellEdit(rowUuid);
          const isCellEditing =
            isRowEditing &&
            rowDraft &&
            (isDocumentGroupCellEdit ||
              (editingColumnKeys &&
                (editingColumnKeys.has('__all__') ||
                  editingColumnKeys.has(dataKey))));

          // --- Read-only display (format value) ---
          const isEmpty = val == null || val === '';
          const formatDisplayValue = () => {
            if (
              dataKey === 'transactionDate' ||
              dataKey === 'dueDate' ||
              dataKey === 'uploadDate'
            ) {
              const dateRaw =
                val ??
                (rowOriginal as Record<string, unknown> | undefined)?.[dataKey];
              return formatTransactionTableDate(dateRaw, i18n.language);
            }
            if (dataKey === 'currency') {
              const raw =
                val ??
                (rowOriginal as Record<string, unknown> | undefined)?.currency;
              const code = raw != null ? String(raw).trim() : '';
              return code === '' ? '-' : code;
            }
            if (dataKey === 'taxRate') {
              return !isEmpty
                ? `${localizeDigitsForLocale(String(val), i18n.language)}%`
                : '-';
            }
            if (
              showQuantityUnitPriceColumns &&
              (dataKey === 'gross' || dataKey === 'net')
            ) {
              const source = {
                ...(rowOriginal ?? {}),
                ...(rowDraft ?? {}),
              };
              const { gross, net } = resolveCalculatedGrossAndNet(source);
              const amount = dataKey === 'gross' ? gross : net;
              return formatTransactionAmountDisplay(amount);
            }
            if (dataKey === 'quantity' && !isEmpty) {
              return formatTransactionQuantityDisplay(val);
            }
            if (isAmountColumn && !isEmpty) {
              return formatTransactionAmountDisplay(val);
            }
            if (dataKey === 'contactType' && !isEmpty) {
              return getContactTypeLabel(String(val), t);
            }
            if (dataKey === 'sourceType' && !isEmpty) {
              return getSourceTypeDisplayLabel(String(val), t);
            }
            if (dataKey === 'taxInclusive') {
              const raw =
                (rowDraft as { taxInclusive?: boolean } | null)?.taxInclusive ??
                (rowOriginal as { taxInclusive?: boolean } | undefined)
                  ?.taxInclusive ??
                val;
              // Missing value defaults to inclusive (legacy qty×unitPrice − discount).
              return raw !== false ? t('common.yes') : t('common.no');
            }
            if (dataKey === 'country' && !isEmpty) {
              const country = countries?.find((c) => c.iso === String(val));
              return country?.name ?? val;
            }
            if (dataKey === 'contactName') {
              const row = record.__transactionRow as {
                contactId?: string | null;
                contactName?: string | null;
              };
              const displayContactName = String(
                row?.contactName ?? val ?? ''
              ).trim();
              const matchingContact = findMatchingContactOption(
                row?.contactId,
                row?.contactName,
                val
              );

              if (matchingContact === undefined && displayContactName !== '') {
                return formatNewContactLabel(displayContactName);
              }

              return displayContactName === '' ? '-' : displayContactName;
            }
            if (dataKey === 'accountCode') {
              return resolveAccountDisplayLabel(
                record.__transactionRow as {
                  accountId?: string | null;
                  accountCode?: string | null;
                  accountName?: string | null;
                },
                val,
                accountNameByIdMap,
                accountNameOptions,
                `uuid ${String(record.__transactionUuid ?? '')}`
              );
            }
            if (dataKey === 'itemId') {
              return resolveItemDisplayLabel(
                record.__transactionRow as {
                  itemId?: string | null;
                },
                val,
                itemNameByIdMap,
                transactionItemOptions
              );
            }
            if (dataKey === 'taxName') {
              const row = record.__transactionRow as {
                taxRateId?: string | null;
                taxName?: string | null;
                taxRate?: number | null;
              };
              return resolveTaxDisplayLabel(
                row,
                val,
                taxNameByIdMap,
                taxNameOptions
              );
            }
            return isEmpty
              ? '-'
              : localizeDigitsForLocale(String(val), i18n.language);
          };

          if (!isCellEditing) {
            const displayVal = formatDisplayValue();
            if (
              dataKey === 'description' &&
              typeof displayVal === 'string' &&
              displayVal !== '-'
            ) {
              const truncated =
                displayVal.length <= DESCRIPTION_DISPLAY_MAX_LENGTH
                  ? displayVal
                  : `${displayVal.slice(0, DESCRIPTION_DISPLAY_MAX_LENGTH)}...`;
              return (
                <Tooltip title={displayVal}>
                  <span
                    className="cell-dblclick"
                    onDoubleClick={() => handleCellDoubleClick(record, dataKey)}
                    style={{
                      display: 'block',
                      width: '100%',
                      cursor: 'default',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncated}
                  </span>
                </Tooltip>
              );
            }
            if (
              dataKey === 'taxName' &&
              typeof displayVal === 'string' &&
              displayVal !== '-'
            ) {
              const truncated =
                displayVal.length <= TAX_NAME_DISPLAY_MAX_LENGTH
                  ? displayVal
                  : `${displayVal.slice(0, TAX_NAME_DISPLAY_MAX_LENGTH)}...`;
              return (
                <Tooltip title={displayVal}>
                  <span
                    className="cell-dblclick"
                    onDoubleClick={() => handleCellDoubleClick(record, dataKey)}
                    style={{
                      display: 'block',
                      width: '100%',
                      cursor: 'default',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncated}
                  </span>
                </Tooltip>
              );
            }
            if (
              dataKey === 'accountCode' &&
              typeof displayVal === 'string' &&
              displayVal !== '-'
            ) {
              const truncated =
                displayVal.length <= ACCOUNT_NAME_DISPLAY_MAX_LENGTH
                  ? displayVal
                  : `${displayVal.slice(
                      0,
                      ACCOUNT_NAME_DISPLAY_MAX_LENGTH
                    )}...`;
              return (
                <Tooltip title={displayVal}>
                  <span
                    className="cell-dblclick"
                    onDoubleClick={() => handleCellDoubleClick(record, dataKey)}
                    style={{
                      display: 'block',
                      width: '100%',
                      cursor: 'default',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncated}
                  </span>
                </Tooltip>
              );
            }
            if (
              dataKey === 'itemId' &&
              typeof displayVal === 'string' &&
              displayVal !== '-'
            ) {
              const truncated =
                displayVal.length <= ITEM_DISPLAY_MAX_LENGTH
                  ? displayVal
                  : `${displayVal.slice(0, ITEM_DISPLAY_MAX_LENGTH)}...`;
              return (
                <Tooltip title={displayVal}>
                  <span
                    className="cell-dblclick"
                    onDoubleClick={() => handleCellDoubleClick(record, dataKey)}
                    style={{
                      display: 'block',
                      width: '100%',
                      cursor: 'default',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {truncated}
                  </span>
                </Tooltip>
              );
            }
            if (
              isEntityTransactionColumnReadOnly(
                dataKey,
                showQuantityUnitPriceColumns
              )
            ) {
              const isDateColumn =
                dataKey === 'transactionDate' ||
                dataKey === 'dueDate' ||
                dataKey === 'uploadDate';
              return (
                <span
                  className={
                    isDateColumn ? 'transaction-table-date-cell' : undefined
                  }
                  style={{
                    display: 'block',
                    width: '100%',
                    cursor: 'default',
                    fontWeight: 600,
                  }}
                >
                  {formatDisplayValue()}
                </span>
              );
            }
            const isDateColumn =
              dataKey === 'transactionDate' ||
              dataKey === 'dueDate' ||
              dataKey === 'uploadDate';
            return (
              <span
                className={[
                  'cell-dblclick',
                  isDateColumn ? 'transaction-table-date-cell' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDoubleClick={() => handleCellDoubleClick(record, dataKey)}
                style={{ display: 'block', width: '100%', cursor: 'default' }}
              >
                {formatDisplayValue()}
              </span>
            );
          }

          // --- Editable controls ---
          let currentValue =
            dataKey === 'contactName'
              ? (rowDraft as any).contactId ??
                (rowDraft as any).contactName ??
                (record.__transactionRow as any)?.contactId ??
                (record.__transactionRow as any)?.contactName
              : (rowDraft as any)[dataKey];

          if (dataKey === 'transactionDate' || dataKey === 'dueDate') {
            const dateValue = currentValue ? dayjs(currentValue) : null;
            return (
              <DatePicker
                size="small"
                value={dateValue?.isValid() ? dateValue : null}
                format={getLocalizedDatePickerFormat('DD/MM/YYYY', i18n.language)}
                onChange={(date) =>
                  patchInlineRowDraft(rowUuid, (prev) => ({
                    ...prev,
                    [dataKey]: date ? date.format('YYYY-MM-DD') : '',
                  }))
                }
                style={{ width: '100%', minWidth: 105 }}
              />
            );
          }

          if (dataKey === 'taxInclusive') {
            return (
              <Select
                size="small"
                value={currentValue !== false ? 'yes' : 'no'}
                options={[
                  { label: t('common.yes'), value: 'yes' },
                  { label: t('common.no'), value: 'no' },
                ]}
                onChange={(val) =>
                  patchInlineRowDraft(rowUuid, (prev) => {
                    const next = {
                      ...prev,
                      taxInclusive: val === 'yes',
                    };
                    return showQuantityUnitPriceColumns
                      ? applyCalculatedGrossAndNet(next)
                      : next;
                  })
                }
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 100 }}
                popupMatchSelectWidth={false}
              />
            );
          }

          if (dataKey === 'currency') {
            return (
              <Select
                size="small"
                showSearch
                value={currentValue as string}
                options={currencyOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) =>
                  patchInlineRowDraft(rowUuid, (prev) => ({
                    ...prev,
                    currency: val,
                  }))
                }
                style={{ width: '100%' }}
              />
            );
          }

          if (dataKey === 'contactType') {
            return (
              <Select
                size="small"
                value={currentValue as string}
                options={contactTypeOptions}
                onChange={(val) =>
                  patchInlineRowDraft(rowUuid, (prev) => ({
                    ...prev,
                    contactType: val,
                  }))
                }
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 160 }}
                popupMatchSelectWidth={false}
              />
            );
          }

          if (dataKey === 'sourceType') {
            return (
              <Select
                size="small"
                showSearch
                value={currentValue as string}
                options={sourceTypeOptionsWithExtras}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) =>
                  patchInlineRowDraft(rowUuid, (prev) => ({
                    ...prev,
                    sourceType: val,
                  }))
                }
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 160 }}
                popupMatchSelectWidth={false}
              />
            );
          }

          if (dataKey === 'country') {
            return (
              <Select
                size="small"
                showSearch
                value={currentValue as string}
                options={countryOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) =>
                  patchInlineRowDraft(rowUuid, (prev) => ({
                    ...prev,
                    country: val,
                  }))
                }
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 180 }}
                popupMatchSelectWidth={false}
              />
            );
          }

          if (dataKey === 'contactName') {
            const rowContactName =
              (rowDraft as any)?.contactName ??
              (record.__transactionRow as any)?.contactName ??
              val;
            const {
              matchingContact,
              options: contactSelectOptions,
              syntheticValue,
            } = buildContactOptionsWithCurrent(
              (rowDraft as any)?.sourceType ??
                (record.__transactionRow as any)?.sourceType,
              (rowDraft as any)?.contactId ??
                (record.__transactionRow as any)?.contactId,
              rowContactName,
              currentValue
            );
            const selectValue =
              matchingContact?.value ??
              (syntheticValue ||
                String(currentValue ?? '').trim() ||
                undefined);

            return (
              <Select
                size="small"
                showSearch
                value={selectValue}
                options={contactSelectOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes((input ?? '').toLowerCase())
                }
                onChange={(val) =>
                  patchInlineRowDraft(rowUuid, (prev) => {
                    const selected = contactSelectOptions.find(
                      (o) => o.value === (val as string)
                    );
                    const nextContactName =
                      selected?.value === syntheticValue
                        ? String(rowContactName ?? syntheticValue ?? '')
                            .replace(CONTACT_NEW_SUFFIX_REGEX, '')
                            .trim() || prev.contactName
                        : selected?.label ?? prev.contactName;
                    return {
                      ...prev,
                      contactId: val as string,
                      contactName: nextContactName,
                    };
                  })
                }
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
              />
            );
          }

          if (dataKey === 'accountCode') {
            const orig = record.__transactionRow as {
              accountId?: string | null;
              accountCode?: string | null;
              accountName?: string | null;
            };
            const draftRow = rowDraft as ExcelTransaction | null;
            const row = {
              accountId: draftRow?.accountId ?? orig?.accountId,
              accountCode: draftRow?.accountCode ?? orig?.accountCode,
              accountName: draftRow?.accountName ?? orig?.accountName,
            };
            const cellForResolve =
              draftRow?.accountId ?? draftRow?.accountCode ?? currentValue;
            const {
              value: accountSelectValue,
              label: accountSelectLabel,
              options,
            } = resolveAccountSelectEditState({
              row,
              cellVal: cellForResolve,
              isAccountIdProvider:
                isAccountIdProvider || coaAccountOptionsUseIds,
              accountNameByIdMap,
              accountNameOptions,
              accountNameOptionsGrouped,
            });
            const filterAccountOption = (input: string, option: any) => {
              const label = (option?.label ?? '').toString().toLowerCase();
              if (label.includes((input ?? '').toLowerCase())) return true;
              const opts = option?.options;
              if (Array.isArray(opts))
                return opts.some((o: any) =>
                  (o?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes((input ?? '').toLowerCase())
                );
              return false;
            };
            return (
              <Select
                size="small"
                showSearch
                labelInValue
                value={
                  accountSelectValue && accountSelectLabel
                    ? {
                        value: accountSelectValue,
                        label: accountSelectLabel,
                      }
                    : undefined
                }
                options={options}
                filterOption={filterAccountOption}
                onChange={(item) => {
                  const nextVal =
                    item != null && typeof item === 'object' && 'value' in item
                      ? String((item as { value: string }).value)
                      : item != null
                      ? String(item)
                      : '';
                  const accountFields =
                    applyAccountSelectionToTransactionFields(
                      nextVal,
                      accountNameByIdMap,
                      accountNameOptions,
                      accountNameOptionsGrouped
                    );
                  patchInlineRowDraft(rowUuid, (prev) => ({
                    ...prev,
                    ...accountFields,
                  }));
                }}
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 200 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            );
          }

          if (dataKey === 'itemId') {
            const orig = record.__transactionRow as {
              itemId?: string | null;
            };
            const draftRow = rowDraft as ExcelTransaction | null;
            const row = {
              itemId: draftRow?.itemId ?? orig?.itemId,
            };
            const cellForResolve = draftRow?.itemId ?? currentValue;
            const {
              value: itemSelectValue,
              label: itemSelectLabel,
              options,
            } = resolveItemSelectEditState({
              row,
              cellVal: cellForResolve,
              itemNameByIdMap,
              transactionItemOptions,
            });
            return (
              <Select
                size="small"
                showSearch
                labelInValue
                value={
                  itemSelectValue && itemSelectLabel
                    ? {
                        value: itemSelectValue,
                        label: itemSelectLabel,
                      }
                    : undefined
                }
                options={options}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes((input ?? '').toLowerCase())
                }
                onChange={(item) => {
                  const nextVal =
                    item != null && typeof item === 'object' && 'value' in item
                      ? String((item as { value: string }).value)
                      : item != null
                      ? String(item)
                      : '';
                  const itemFields = applyItemSelectionToTransactionFields(
                    nextVal,
                    itemNameByIdMap,
                    transactionItemOptions
                  );
                  patchInlineRowDraft(rowUuid, (prev) => ({
                    ...prev,
                    ...itemFields,
                  }));
                }}
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 200 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            );
          }

          if (dataKey === 'taxName') {
            const row = record.__transactionRow as ExcelTransaction;
            const draftRow = rowDraft as ExcelTransaction | null;
            const {
              value: taxSelectValue,
              label: taxSelectLabel,
              options: taxSelectOptions,
            } = resolveTaxSelectEditState({
              row,
              draftRow: draftRow ?? undefined,
              cellVal: currentValue,
              taxNameByIdMap,
              taxNameOptions,
            });
            return (
              <Select
                size="small"
                showSearch
                labelInValue
                value={
                  taxSelectValue && taxSelectLabel
                    ? {
                        value: taxSelectValue,
                        label: taxSelectLabel,
                      }
                    : undefined
                }
                options={taxSelectOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes((input ?? '').toLowerCase())
                }
                onChange={(item) => {
                  const nextVal =
                    item != null && typeof item === 'object' && 'value' in item
                      ? String((item as { value: string }).value)
                      : item != null
                      ? String(item)
                      : '';
                  patchInlineRowDraft(rowUuid, (prev) => {
                    const next = {
                      ...prev,
                      taxRateId: nextVal || null,
                    } as ExcelTransaction;
                    return syncTaxFieldsFromRateId(next, taxRateByIdMap);
                  });
                }}
                style={{ width: '100%' }}
                dropdownStyle={{ minWidth: 200 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            );
          }

          // Tax rate is set automatically from tax name; not editable
          if (
            dataKey === 'taxRate' ||
            isEntityTransactionColumnReadOnly(
              dataKey,
              showQuantityUnitPriceColumns
            )
          ) {
            return (
              <span
                style={{
                  display: 'block',
                  width: '100%',
                  cursor: 'default',
                  fontWeight: 600,
                }}
              >
                {formatDisplayValue()}
              </span>
            );
          }

          if (NUMERIC_COLUMN_KEYS.has(dataKey)) {
            const displayValue = formatTransactionNumericInputValue(
              dataKey,
              currentValue,
              TRANSACTION_AMOUNT_FIELD_KEYS
            );
            return (
              <Input
                size="small"
                value={displayValue}
                onFocus={
                  dataKey === 'tax' && showQuantityUnitPriceColumns
                    ? handleReceiptTaxFieldFocus
                    : undefined
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  const sanitised =
                    dataKey === 'quantity'
                      ? sanitiseQuantityInput(raw)
                      : raw
                          .replace(/,/g, '.')
                          .replace(/[^0-9.\-]/g, '')
                          .replace(/(?!^)-/g, '')
                          .replace(/(\..*)\./g, '$1');
                  patchInlineRowDraft(rowUuid, (prev) => {
                    const next = { ...prev, [dataKey]: sanitised };
                    if (
                      showQuantityUnitPriceColumns &&
                      (dataKey === 'quantity' ||
                        dataKey === 'unitPrice' ||
                        dataKey === 'discountAmount' ||
                        dataKey === 'tax')
                    ) {
                      return applyCalculatedGrossAndNet(next);
                    }
                    return next;
                  });
                }}
                onBlur={() => {
                  patchInlineRowDraft(rowUuid, (prev) => {
                    const raw = (prev as any)[dataKey];
                    let next: Partial<ValidatedExcelTransaction>;
                    if (raw == null || raw === '' || raw === '-') {
                      next = {
                        ...prev,
                        [dataKey]: OPTIONAL_NUMERIC_COLUMN_KEYS.has(dataKey)
                          ? null
                          : 0,
                      };
                    } else {
                      const parsed = parseLocaleNumber(raw);
                      next = {
                        ...prev,
                        [dataKey]:
                          dataKey === 'quantity'
                            ? toApiQuantity(parsed)
                            : toApiNumber(parsed),
                      };
                    }
                    if (
                      showQuantityUnitPriceColumns &&
                      (dataKey === 'quantity' ||
                        dataKey === 'unitPrice' ||
                        dataKey === 'discountAmount' ||
                        dataKey === 'tax')
                    ) {
                      return applyCalculatedGrossAndNet(next);
                    }
                    return next;
                  });
                }}
                style={{ width: '100%' }}
              />
            );
          }

          return (
            <Input
              size="small"
              value={currentValue as any}
              onChange={(e) =>
                patchInlineRowDraft(rowUuid, (prev) => ({
                  ...prev,
                  [dataKey]: e.target.value,
                }))
              }
            />
          );
        },
      };
    });

    const embeddedTracking = organisation?.trackingCategories;
    const trackingCategoryColumns = showTrackingCategoryColumns
      ? ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS.map((dataKey, idx) => {
          const optionField =
            trackingTableColumnKeyToOptionField(dataKey) ?? 'trackingOption1Id';
          const fromApi = embeddedTracking?.[idx]?.name?.trim();
          const title =
            fromApi ||
            getEntityTransactionColumnTitle(dataKey, t);
          const dimensionIndex = (idx === 0 ? 0 : 1) as 0 | 1;
          const optionNameField =
            optionField === 'trackingOption1Id'
              ? 'trackingOption1Name'
              : 'trackingOption2Name';
          return {
            title,
            dataIndex: dataKey,
            key: dataKey,
            width: ENTITY_TRANSACTION_COLUMN_MIN_WIDTHS[dataKey] ?? 120,
            sorter: false,
            render: (_val: unknown, record: Record<string, unknown>) => {
              const rowUuid = record.__transactionUuid as string | undefined;
              const row = record.__transactionRow as
                | ValidatedExcelTransaction
                | undefined;
              const rowDraft = getInlineRowDraft(rowUuid, row);
              const isRowEditing = isRowInInlineEdit(rowUuid);
              const isDocumentGroupCellEdit =
                isFileGroupFullRowCellEdit(rowUuid);
              const isCellEditing =
                isRowEditing &&
                rowDraft &&
                (isDocumentGroupCellEdit ||
                  (editingColumnKeys &&
                    (editingColumnKeys.has('__all__') ||
                      editingColumnKeys.has(dataKey))));

              const draftRecord = rowDraft as
                | Record<string, unknown>
                | null
                | undefined;
              const fromDraft =
                isRowEditing && draftRecord
                  ? draftRecord[optionField]
                  : undefined;
              const fromOriginal =
                row == null
                  ? undefined
                  : (row as unknown as Record<string, unknown>)[optionField];
              const fallbackTable = record[dataKey];
              const currentIdRaw =
                fromDraft !== undefined && fromDraft !== null
                  ? fromDraft
                  : fromOriginal !== undefined && fromOriginal !== null
                  ? fromOriginal
                  : fallbackTable;
              const trimmedRaw =
                currentIdRaw == null || currentIdRaw === ''
                  ? ''
                  : String(currentIdRaw).trim();
              // Treat placeholder / empty display as unset (matches tax name Select behaviour).
              const currentId =
                trimmedRaw === '' ||
                trimmedRaw === '-' ||
                trimmedRaw === '—' ||
                trimmedRaw === '–' ||
                trimmedRaw === '?'
                  ? ''
                  : trimmedRaw;

              const optionNameRaw =
                isRowEditing && draftRecord
                  ? draftRecord[optionNameField]
                  : (row as unknown as Record<string, unknown> | undefined)?.[
                      optionNameField
                    ];
              const optionName =
                optionNameRaw == null ? null : String(optionNameRaw);
              const selectOptions = trackingSelectOptionsForValue(
                embeddedTracking,
                dimensionIndex,
                currentId,
                optionName
              );

              if (!isCellEditing) {
                const display = resolveTrackingOptionDisplayLabel(
                  currentId,
                  optionName,
                  embeddedTracking,
                  dimensionIndex
                );
                return (
                  <span
                    className="cell-dblclick"
                    onDoubleClick={() => handleCellDoubleClick(record, dataKey)}
                    style={{
                      display: 'block',
                      width: '100%',
                      cursor: 'default',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {display || '-'}
                  </span>
                );
              }

              const selectValue =
                currentId &&
                selectOptions.some((option) => option.value === currentId)
                  ? currentId
                  : undefined;

              return (
                <Select
                  size="small"
                  allowClear
                  showSearch
                  value={selectValue}
                  options={selectOptions}
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes((input ?? '').toLowerCase())
                  }
                  onChange={(val) =>
                    patchInlineRowDraft(rowUuid, (prev) => ({
                      ...prev,
                      ...applyTrackingOptionSelectionToTransactionFields(
                        optionField,
                        val === undefined || val === null || val === ''
                          ? ''
                          : String(val),
                        embeddedTracking ?? []
                      ),
                    }))
                  }
                  style={{ width: '100%' }}
                  dropdownStyle={{ minWidth: 200 }}
                  popupMatchSelectWidth={false}
                  notFoundContent={null}
                />
              );
            },
          };
        })
      : [];

    const attachmentColumn = {
      title: '',
      key: ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY,
      dataIndex: ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY,
      // Excel/Receipts: paperclip + history eye (edited & deleted); others: paperclip only
      width: supportsDeletedAndHistory ? 52 : 36,
      fixed: 'left' as const,
      sorter: false,
      className: 'transaction-attachment-cell',
      onCell: () => ({ className: 'transaction-attachment-cell' }),
      render: (_: unknown, record: Record<string, unknown>) => {
        const uuid = record.__transactionUuid ?? record.uuid;
        const uuidStr = typeof uuid === 'string' ? uuid : String(uuid ?? '');
        const isViewing = attachmentsTransactionId === uuidStr;
        const row = record.__transactionRow as
          | ValidatedExcelTransaction
          | undefined;
        const hasAttachments = isViewing
          ? entityTransactionAttachments.length > 0
          : !!row?.hasAttachment;
        const canClick = !!uuidStr && !!connectionId;
        /** Tooltips only for Excel; other entity types use their own attachment UX without this copy. */
        const showAttachmentColumnTooltip = isExcelProvider;
        const isDeletedRow = isTransactionDeleted(record);
        const isEdited = !isDeletedRow && isTransactionEdited(record);
        const showHistoryEye =
          supportsDeletedAndHistory && (isEdited || isDeletedRow);
        const historyTooltip = t(
          'organisation.transaction-edited-badge-tooltip'
        );

        const historyEyeButton = showHistoryEye ? (
          <Tooltip
            title={historyTooltip}
            placement="right"
            getPopupContainer={() => document.body}
          >
            <button
              type="button"
              aria-label={historyTooltip}
              onClick={(e) => {
                e.stopPropagation();
                openTransactionHistoryModal(record);
              }}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--color-surface, #F3F7F8)',
                border: 'none',
                padding: 0,
                margin: 0,
                appearance: 'none',
                color: 'inherit',
              }}
            >
              <EyeIcon
                className="transaction-history-eye-icon"
                style={{ width: 18, height: 18 }}
                aria-hidden
              />
            </button>
          </Tooltip>
        ) : null;

        const attachmentAriaLabel = hasAttachments
          ? t('organisation.attachment-tooltip-has-excel-receipt')
          : t('organisation.attachment-tooltip-check-excel-receipt');

        const attachmentIconButton = (
          <button
            type="button"
            aria-label={attachmentAriaLabel}
            onClick={() => onAttachmentClick(record)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              opacity: isViewing ? 0.9 : 1,
              border: 'none',
              padding: 0,
              margin: 0,
              appearance: 'none',
              background: 'transparent',
              color: 'inherit',
            }}
          >
            {hasAttachments ? (
              <IconTransactionHasAttachment
                className="transaction-action-icon"
                style={{ width: 22, height: 22 }}
                aria-hidden
              />
            ) : (
              <IconAttachment
                className="transaction-action-icon"
                style={{ width: 22, height: 22 }}
                aria-hidden
              />
            )}
          </button>
        );

                return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 4,
              width: '100%',
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {canClick ? (
                showAttachmentColumnTooltip ? (
                  <Tooltip
                    title={
                      hasAttachments
                        ? t('organisation.attachment-tooltip-has-excel-receipt')
                        : t(
                            'organisation.attachment-tooltip-check-excel-receipt'
                          )
                    }
                    placement="right"
                    getPopupContainer={() => document.body}
                  >
                    {attachmentIconButton}
                  </Tooltip>
                ) : (
                  attachmentIconButton
                )
              ) : null}
            </div>
            {historyEyeButton ? (
              <div
                style={{
                  width: 22,
                  height: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {historyEyeButton}
              </div>
            ) : null}
          </div>
        );
      },
    };

    const customiseColumnsHeader = (align: 'center' | 'flex-end') => (
      <div
        ref={align === 'center' ? editInDrawerTourTargetRef : undefined}
        style={{
          display: 'flex',
          justifyContent: align,
          alignItems: 'center',
          gap: 6,
          width: '100%',
          minHeight: 32,
        }}
      >
        {!showDeletedTransactions &&
        (isExcelProvider || isReceiptsOnlyProvider) ? (
          <CustomiseColumnsIconButtonWithTooltip
            type="default"
            tooltipTitle={t('organisation.add-transaction')}
            disabled={editingTransactionId != null || isInlineFileGroupEditing}
            icon={
              <PlusOutlined
                style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
              />
            }
            style={customiseColumnsIconButtonStyle(false)}
            onClick={handleAddManualTransactionRow}
          />
        ) : null}
        <CustomiseColumnsIconButtonWithTooltip
          type="default"
          disabled={editingTransactionId != null || isInlineFileGroupEditing}
          icon={
            <SettingOutlined
              style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
            />
          }
          style={customiseColumnsIconButtonStyle(
            transactionsSettingsDrawerOpen
          )}
          onClick={() => openTransactionsSettingsDrawerRef.current?.()}
        />
      </div>
    );

    const statusColumnPreferredVisible =
      showDeletedTransactions &&
      (visibleColumns == null ||
        visibleColumns.includes(ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY));

    const statusColumn =
      supportsDeletedAndHistory && showDeletedTransactions
      ? {
          // No "Status" title ? customise icon lives here while this column is visible
          title: statusColumnPreferredVisible
            ? customiseColumnsHeader('center')
            : '',
          key: ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY,
          dataIndex: ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY,
          width: 88,
          fixed: 'right' as const,
          align: 'center' as const,
          sorter: false,
          render: (_: unknown, record: Record<string, unknown>) => {
            if (!isTransactionDeleted(record)) return null;
            return (
              <Tooltip
                title={t('organisation.transaction-deleted-badge-tooltip')}
              >
                <span className="transaction-status-badge transaction-status-badge-deleted">
                  {t('organisation.transaction-deleted-badge')}
                </span>
              </Tooltip>
            );
          },
        }
      : null;

    const actionsColumn = {
      title: statusColumnPreferredVisible ? (
        ''
      ) : (
        <div
          ref={editInDrawerTourTargetRef}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 6,
            width: '100%',
            minHeight: 32,
          }}
        >
          {!showDeletedTransactions &&
          (isExcelProvider || isReceiptsOnlyProvider) ? (
            <CustomiseColumnsIconButtonWithTooltip
              type="default"
              tooltipTitle={t('organisation.add-transaction')}
              disabled={
                editingTransactionId != null || isInlineFileGroupEditing
              }
              icon={
                <PlusOutlined
                  style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
                />
              }
              style={customiseColumnsIconButtonStyle(false)}
              onClick={handleAddManualTransactionRow}
            />
          ) : null}
          <CustomiseColumnsIconButtonWithTooltip
            type="default"
            disabled={
              editingTransactionId != null || isInlineFileGroupEditing
            }
            icon={
              <SettingOutlined
                style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
              />
            }
            style={customiseColumnsIconButtonStyle(
              transactionsSettingsDrawerOpen
            )}
            onClick={() => openTransactionsSettingsDrawerRef.current?.()}
          />
        </div>
      ),
      key: ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY,
      dataIndex: ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY,
      width:
        showDeletedTransactions ||
        !(isExcelProvider || isReceiptsOnlyProvider)
          ? 52
          : 88,
      fixed: 'right' as const,
      align: 'right' as const,
      sorter: false,
      render: (_: unknown, record: Record<string, unknown>) => {
        const rowUuid = record.__transactionUuid as string | undefined;
        const isEditing = isRowInInlineEdit(rowUuid);

        const actionsCellStyle: React.CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
          gap: 6,
          whiteSpace: 'nowrap',
        };

        if (isEditing) {
          return (
            <div style={actionsCellStyle}>
              <ScopeEditSaveButton
                type="default"
                onClick={() => handleSaveTransaction(record)}
                aria-label={t('common.save-transaction')}
              >
                {t('common.save')}
              </ScopeEditSaveButton>
              <ScopeEditCancelIcon
                onClick={handleCancelEditTransaction}
                role="button"
                tabIndex={0}
                aria-label={t('common.cancel-editing-transaction')}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCancelEditTransaction();
                  }
                }}
              />
            </div>
          );
        }

        const original = record.__transactionRow as
          | ValidatedExcelTransaction
          | undefined;
        const isReviewModeRow =
          isReceiptsProvider &&
          !!original &&
          transactionQualifiesForMarkAsReviewed(original);
        const isPostedLocked = isTransactionLockedByPostedFile(record);
        const isDeleted = isTransactionDeleted(record);

        if (isPostedLocked) {
          return (
            <div style={actionsCellStyle}>
              <Tooltip title={t('organisation.transaction-uploaded-provider-locked-tooltip')}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--color-link, #5e87b0)',
                    background: '#f3f8fd',
                    border: '1px solid #C9DBF8',
                    borderRadius: 4,
                    padding: '2px 8px',
                    textTransform: 'uppercase',
                    lineHeight: '16px',
                    cursor: 'default',
                  }}
                >
                  {t('organisation.transaction-posted-badge')}
                </span>
              </Tooltip>
            </div>
          );
        }

        if (isDeleted) {
          return <div style={actionsCellStyle} />;
        }

        const menuItems = [
          {
            key: 'edit',
            label: isReviewModeRow
              ? t('common.review')
              : t('common.edit'),
            icon: <EditOutlined style={{ fontSize: 16 }} />,
            onClick: () => handleEditTransaction(record),
          },
          ...(isReviewModeRow
            ? [
                {
                  key: 'mark-reviewed',
                  label: t('common.mark-as-reviewed'),
                  icon: <CheckOutlined style={{ fontSize: 16 }} />,
                  onClick: () => handleMarkTransactionReviewed(record),
                } as const,
              ]
            : []),
          {
            key: 'delete',
            label: t('common.delete'),
            icon: <DeleteOutlined style={{ fontSize: 16 }} />,
            danger: true,
            onClick: () => setDeleteTransactionRecord(record),
          },
        ];

        const isFirstRow =
          (record.__transactionIndex as number | undefined) === 0;
        return (
          <div
            style={actionsCellStyle}
            ref={(el) => {
              if (el && isFirstRow && !editButtonTourTargetRef.current) {
                (
                  editButtonTourTargetRef as React.MutableRefObject<HTMLDivElement | null>
                ).current = el;
              }
            }}
            {...(isFirstRow ? { 'data-tour-edit-transaction': true } : {})}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <ScopeDropdownButton
                label={isReviewModeRow ? t('common.review') : t('common.edit')}
                menu={{ items: menuItems }}
                onClick={() => handleEditTransaction(record)}
                placement="bottomRight"
                trigger={['click']}
              />
            </span>
          </div>
        );
      },
    };

    const monetaryKeySet = new Set<string>([
      ...MONETARY_AUXILIARY_COLUMN_KEYS,
      ...PRE_TOTAL_COLUMN_BLOCK_KEYS,
    ]);
    const firstMonetaryIdx = dataColumns.findIndex((col) =>
      monetaryKeySet.has(String(col.key))
    );
    const splitAt =
      firstMonetaryIdx >= 0
        ? firstMonetaryIdx
        : dataColumns.findIndex(
            (col) =>
              col.key === 'tax' || col.key === 'net' || col.key === 'gross'
          );
    const dataColumnsBeforeMonetary =
      splitAt >= 0 ? dataColumns.slice(0, splitAt) : dataColumns;
    const dataColumnsFromMonetary =
      splitAt >= 0 ? dataColumns.slice(splitAt) : [];

    return [
      attachmentColumn,
      ...dataColumnsBeforeMonetary,
      ...trackingCategoryColumns,
      ...dataColumnsFromMonetary,
      ...(statusColumn ? [statusColumn] : []),
      // Deleted view: no edit/delete actions ? drop the empty Actions column
      ...(!showDeletedTransactions ? [actionsColumn] : []),
    ];
  }, [
    t,
    i18n.language,
    handleEditTransaction,
    handleSaveTransaction,
    handleCancelEditTransaction,
    handleAddManualTransactionRow,
    handleCellDoubleClick,
    handleDeleteTransaction,
    openTransactionHistoryModal,
    isTransactionLockedByPostedFile,
    isTransactionDeleted,
    isTransactionEdited,
    showDeletedTransactions,
    supportsDeletedAndHistory,
    visibleColumns,
    attachmentsTransactionId,
    entityTransactionAttachments,
    organisation,
    connectionId,
    onAttachmentClick,
    editingTransactionId,
    editingDraft,
    editingColumnKeys,
    isInlineFileGroupEditing,
    isRowInInlineEdit,
    isFileGroupFullRowCellEdit,
    getInlineRowDraft,
    patchInlineRowDraft,
    isReceiptsProvider,
    isExcelProvider,
    isReceiptsOnlyProvider,
    isAccountIdProvider,
    showTrackingCategoryColumns,
    showTransactionItemColumns,
    showQuantityUnitPriceColumns,
    handleReceiptTaxFieldFocus,
    accountNameByIdMap,
    accountNameOptions,
    accountNameOptionsGrouped,
    itemNameByIdMap,
    transactionItemOptions,
    taxNameByIdMap,
    taxNameOptions,
    taxRateByIdMap,
    currencyOptions,
    countryOptions,
    countries,
    formatNumber,
    editingTransactionId,
    entityTransactionsToolbarButtonStyle,
    transactionsSettingsDrawerOpen,
  ]);

  const trackingCategoryColumnKeysSet = React.useMemo(
    () => new Set(ENTITY_TRANSACTION_TRACKING_CATEGORY_COLUMN_KEYS),
    []
  );

  // All columns are filterable; user chooses which filter chips to show (like submissions page).
  const allFilters = React.useMemo(
    () =>
      entityTransactionColumnKeys.filter(
        (k) => k !== 'errorReason' && !trackingCategoryColumnKeysSet.has(k)
      ),
    [entityTransactionColumnKeys, trackingCategoryColumnKeysSet]
  );
  const FORCED_VISIBLE_FILTERS = [
    'transactionDate',
    'description',
    'gross',
    'net',
    'tax',
  ];

  // Filter refactor alignment: separate keys for visible filters vs filter state (like VAT report).
  // const TRANSACTIONS_TAB_VISIBLE_FILTERS_KEY = `transactions-tab-visible-filters-${organisationId}`;
  const TRANSACTIONS_TAB_FILTER_STATE_KEY = `transactions-tab-filter-state-${organisationId}`;
  const TRANSACTIONS_TAB_COLUMNS_CONFIG_KEY = React.useMemo(
    () =>
      resolvedOrganisationId?.trim()
        ? transactionsTabColumnsConfigKeyForOrg(resolvedOrganisationId)
        : '',
    [resolvedOrganisationId]
  );
  // net, tax = fixed amount columns; actions column is always visible (live view only)
  // Status (deleted) is customizable while Show deleted is on ? not always-fixed
  const ENTITY_TRANSACTIONS_ALWAYS_FIXED_COLUMNS = [
    ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY,
    'tax',
    'net',
    'gross',
    ...(showDeletedTransactions
      ? []
      : [ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY]),
  ];

  const transactionsTableAlwaysFixedColumns = pwaMobileNav
    ? showDeletedTransactions
      ? [ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY]
      : [ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY]
    : ENTITY_TRANSACTIONS_ALWAYS_FIXED_COLUMNS;

  // Force a fixed set of visible filters for all users (ignoring saved preferences for now).
  const visibleFilters = FORCED_VISIBLE_FILTERS;

  // Column customization (entity prefs override defaults-for-all prefs)
  const defaultVisibleColumns = React.useMemo(
    () => [
      ...entityTransactionColumnKeys,
      ...(showDeletedTransactions
        ? [ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY]
        : [ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY]),
    ],
    [entityTransactionColumnKeys, showDeletedTransactions]
  );
  // visibleColumns / lockedColumns / columnOrder state declared earlier (near showDeleted)

  const entityTransactionsNonReorderableColumnKeys = React.useMemo(
    () => getEntityTransactionsNonReorderableColumnKeys(showQuantityUnitPriceColumns),
    [showQuantityUnitPriceColumns]
  );

  const entityTransactionsAlwaysVisibleColumnKeys = React.useMemo(
    () => getEntityTransactionsAlwaysVisibleColumnKeys(showQuantityUnitPriceColumns),
    [showQuantityUnitPriceColumns]
  );

  const ensureAlwaysVisibleTransactionColumns = React.useCallback(
    (keys: string[]) => {
      if (entityTransactionsAlwaysVisibleColumnKeys.length === 0) return keys;
      const next = [...keys];
      for (const key of entityTransactionsAlwaysVisibleColumnKeys) {
        if (!next.includes(key)) next.push(key);
      }
      return next;
    },
    [entityTransactionsAlwaysVisibleColumnKeys]
  );

  const withDefaultTransactionColumns = React.useCallback(
    (keys: string[]) => {
      const injected = injectDefaultTransactionTabColumns(
        stripProviderExcludedColumns(keys)
      );
      return showTrackingCategoryColumns
        ? injectTrackingCategoryColumns(injected)
        : injected;
    },
    [stripProviderExcludedColumns, showTrackingCategoryColumns]
  );

  React.useEffect(() => {
    if (!resolvedOrganisationId?.trim()) {
      setVisibleColumns(null);
      setLockedColumns([]);
      setColumnOrder(undefined);
      return;
    }
    const resolved = resolveTransactionsTabColumnsFromPreferences(
      handlePreferences,
      resolvedOrganisationId
    );
    if (resolved?.visibleColumns?.length) {
      setVisibleColumns(
        ensureAlwaysVisibleTransactionColumns(
          withDefaultTransactionColumns(resolved.visibleColumns)
        )
      );
      setLockedColumns(
        stripProviderExcludedColumns(resolved.lockedColumns ?? [])
      );
      setColumnOrder(
        resolved.columnOrder
          ? withDefaultTransactionColumns(resolved.columnOrder)
          : undefined
      );
    } else {
      setVisibleColumns(null);
      setLockedColumns([]);
      setColumnOrder(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when entity id changes only
  }, [
    resolvedOrganisationId,
    stripProviderExcludedColumns,
    withDefaultTransactionColumns,
  ]);

  const handleColumnsChange = React.useCallback(
    (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => {
      const sanitized = {
        visibleColumns: ensureAlwaysVisibleTransactionColumns(
          withDefaultTransactionColumns(config.visibleColumns)
        ),
        lockedColumns: stripProviderExcludedColumns(config.lockedColumns),
        columnOrder: withDefaultTransactionColumns(config.columnOrder),
        editInDrawer,
      };
      const columnState = toTransactionsTabColumnsState(sanitized);
      setVisibleColumns(columnState.visibleColumns);
      setLockedColumns(columnState.lockedColumns);
      setColumnOrder(columnState.columnOrder);
      if (TRANSACTIONS_TAB_COLUMNS_CONFIG_KEY) {
        handlePreferences(PreferenceActions.SET, {
          name: TRANSACTIONS_TAB_COLUMNS_CONFIG_KEY,
          value: sanitized,
        });
      }
    },
    [
      handlePreferences,
      TRANSACTIONS_TAB_COLUMNS_CONFIG_KEY,
      editInDrawer,
      stripProviderExcludedColumns,
      ensureAlwaysVisibleTransactionColumns,
      withDefaultTransactionColumns,
    ]
  );

  /** Apply drawer save to local table state without persisting (drawer persists). */
  const applyDrawerColumnsOnly = React.useCallback(
    (config: TransactionsTabColumnsPersistConfig) => {
      const columnState = toTransactionsTabColumnsState({
        visibleColumns: ensureAlwaysVisibleTransactionColumns(
          withDefaultTransactionColumns(config.visibleColumns)
        ),
        lockedColumns: stripProviderExcludedColumns(config.lockedColumns ?? []),
        columnOrder: withDefaultTransactionColumns(config.columnOrder ?? []),
      });
      setVisibleColumns(columnState.visibleColumns);
      setLockedColumns(columnState.lockedColumns);
      setColumnOrder(
        columnState.columnOrder.length > 0 ? columnState.columnOrder : undefined
      );
    },
    [stripProviderExcludedColumns, ensureAlwaysVisibleTransactionColumns, withDefaultTransactionColumns]
  );

  // When editing a transaction via the edit icon (full-row mode), merge any
  // hidden columns back into the visible list so every field is accessible.
  // Cell-level editing (double-click) does NOT force-show hidden columns.
  const isEditingTransaction =
    editingTransactionId != null || isInlineFileGroupEditing;
  const isFullRowEditing =
    isEditingTransaction &&
    (isInlineFileGroupEditing || editingColumnKeys?.has('__all__'));

  const effectiveVisibleColumns = React.useMemo(() => {
    if (pwaMobileNav) {
      const availableKeys = new Set([
        ...entityTransactionColumnKeys,
        ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY,
        ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY,
      ]);
      return resolvePwaMobileTransactionColumnKeys(
        availableKeys,
        showDeletedTransactions
      );
    }

    let base = ensureAlwaysVisibleTransactionColumns(
      stripProviderExcludedColumns(visibleColumns ?? defaultVisibleColumns)
    );

    if (isFullRowEditing) {
      const baseSet = new Set(base);
      const missing = defaultVisibleColumns.filter((k) => !baseSet.has(k));
      if (missing.length > 0) {
        base = [...base, ...missing];
      }
    }

    if (!base.includes(ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY)) {
      base = [ENTITY_TRANSACTIONS_ATTACHMENT_COLUMN_KEY, ...base];
    }
    if (showDeletedTransactions) {
      // Deleted view: Status (customise + badge), no Actions column
      base = base.filter(
        (k) => k !== ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY
      );
      if (!base.includes(ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY)) {
        base = [...base, ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY];
      }
    } else {
      base = base.filter((k) => k !== ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY);
      if (!base.includes(ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY)) {
        base = [...base, ENTITY_TRANSACTIONS_ACTIONS_COLUMN_KEY];
      }
    }
    return base;
  }, [
    pwaMobileNav,
    entityTransactionColumnKeys,
    visibleColumns,
    defaultVisibleColumns,
    isFullRowEditing,
    stripProviderExcludedColumns,
    ensureAlwaysVisibleTransactionColumns,
    showDeletedTransactions,
  ]);

  const openTransactionsSettingsDrawer = React.useCallback(() => {
    let customizableColumnsForDrawer =
      allTransactionsTableColumns
        ?.filter(
          (col: { key?: string; fixed?: unknown }) =>
            col.key &&
            !col.fixed &&
            !ENTITY_TRANSACTIONS_ALWAYS_FIXED_COLUMNS.includes(col.key) &&
            !ENTITY_TRANSACTIONS_TABLE_EXCLUDED_COLUMN_KEYS.has(col.key) &&
            !excelProviderHiddenColumnKeys.has(col.key)
        )
        .map((col: { key: string; title: unknown; fixed?: unknown }) => ({
          key: col.key,
          title:
            typeof col.title === 'string' || typeof col.title === 'number'
              ? String(col.title)
              : col.key,
          fixed: col.fixed as boolean | 'left' | 'right' | undefined,
        })) || [];

    const tailToggleableKeys = getEntityTransactionsTailToggleableColumnKeys(
      showQuantityUnitPriceColumns
    );
    if (tailToggleableKeys.length > 0) {
      const tailSet = new Set(tailToggleableKeys);
      for (const key of tailToggleableKeys) {
        if (!customizableColumnsForDrawer.some((col) => col.key === key)) {
          customizableColumnsForDrawer.push({
            key,
            title: getEntityTransactionColumnTitle(key, t),
          });
        }
      }
      const head = customizableColumnsForDrawer.filter(
        (col) => !tailSet.has(col.key)
      );
      const tail = tailToggleableKeys
        .map((key) =>
          customizableColumnsForDrawer.find((col) => col.key === key)
        )
        .filter(
          (
            col
          ): col is {
            key: string;
            title: string;
            fixed?: boolean | 'left' | 'right';
          } => col != null
        );
      customizableColumnsForDrawer = [...head, ...tail];
    }

    // Status is fixed:right in the table but listed in Customize as "Deleted"
    if (
      showDeletedTransactions &&
      !customizableColumnsForDrawer.some(
        (col) => col.key === ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY
      )
    ) {
      customizableColumnsForDrawer = [
        ...customizableColumnsForDrawer,
        {
          key: ENTITY_TRANSACTIONS_STATUS_COLUMN_KEY,
          title: t('organisation.transaction-deleted-badge'),
        },
      ];
    }

    dispatch(
      turnDrawerOn({
        title: t('main.customise'),
        type: DrawerType.CUSTOMIZE_COLUMNS,
        component: (
          <TransactionsTabSettingsDrawerContent
            organisationId={resolvedOrganisationId}
            handlePreferences={handlePreferences}
            setEntityPreferencesBatch={setEntityPreferencesBatch}
            initialEditInDrawer={editInDrawer}
            customizableColumnsForDrawer={customizableColumnsForDrawer}
            effectiveVisibleColumns={effectiveVisibleColumns}
            lockedColumns={lockedColumns}
            columnOrder={columnOrder}
            alwaysFixedColumns={ENTITY_TRANSACTIONS_ALWAYS_FIXED_COLUMNS}
            optionalColumnKeys={Array.from(EDIT_DRAWER_OPTIONAL_KEYS)}
            nonReorderableColumnKeys={entityTransactionsNonReorderableColumnKeys}
            alwaysVisibleColumnKeys={entityTransactionsAlwaysVisibleColumnKeys}
            showEditInDrawerSetting={showEditInDrawerSetting}
            onApplyColumns={applyDrawerColumnsOnly}
            onApplyEditInDrawer={setEditInDrawer}
            onClose={() => dispatch(turnDrawerOff())}
          />
        ),
      } satisfies DrawerData)
    );
  }, [
    allTransactionsTableColumns,
    effectiveVisibleColumns,
    lockedColumns,
    columnOrder,
    dispatch,
    applyDrawerColumnsOnly,
    showEditInDrawerSetting,
    showDeletedTransactions,
    t,
    resolvedOrganisationId,
    handlePreferences,
    setEntityPreferencesBatch,
    editInDrawer,
    excelProviderHiddenColumnKeys,
    showQuantityUnitPriceColumns,
    entityTransactionsNonReorderableColumnKeys,
    entityTransactionsAlwaysVisibleColumnKeys,
  ]);

  React.useLayoutEffect(() => {
    openTransactionsSettingsDrawerRef.current = openTransactionsSettingsDrawer;
  }, [openTransactionsSettingsDrawer]);

  /** Enter saves, Escape cancels. Arrow Left/Right and Tab/Shift+Tab move focus between fields in edit mode. */
  React.useEffect(() => {
    if (!editingTransactionId && !isInlineFileGroupEditing) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelEditTransaction();
        return;
      }

      const isArrowOrTab =
        e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Tab';
      const moveNext =
        e.key === 'ArrowRight' || (e.key === 'Tab' && !e.shiftKey);
      const movePrev = e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey);

      if (isArrowOrTab && (moveNext || movePrev)) {
        if (
          target.closest('.ant-select-dropdown') ||
          target.closest('.ant-picker-dropdown')
        )
          return;

        if (target.closest('.validation-errors-table')) {
          const cell = target.closest('.ant-table-cell');
          if (!cell) return;
          const row = cell.closest('tr');
          if (!row) return;
          const cells = Array.from(row.querySelectorAll('.ant-table-cell'));
          const idx = cells.indexOf(cell as Element);
          if (idx === -1) return;
          const focusableSelector =
            'input:not([type=hidden]), select, textarea, .ant-select-selector, .ant-picker-input input';
          let nextIdx = moveNext ? idx + 1 : idx - 1;
          while (nextIdx >= 0 && nextIdx < cells.length) {
            const nextCell = cells[nextIdx];
            const focusable = (
              nextCell as HTMLElement
            ).querySelector<HTMLElement>(focusableSelector);
            if (focusable) {
              e.preventDefault();
              focusable.focus();
              return;
            }
            nextIdx = moveNext ? nextIdx + 1 : nextIdx - 1;
          }
          return;
        }

        if (target.closest('[data-transaction-edit-drawer]')) {
          const container = target.closest(
            '[data-transaction-edit-drawer]'
          ) as HTMLElement;
          if (!container) return;
          const focusables = Array.from(
            container.querySelectorAll<HTMLElement>(
              'input:not([type=hidden]), select, textarea, .ant-select-selector, .ant-picker-input input'
            )
          ).filter((el) => el.offsetParent !== null);
          const idx = focusables.indexOf(document.activeElement as HTMLElement);
          if (idx === -1) return;
          const nextIdx = moveNext ? idx + 1 : idx - 1;
          const next = focusables[nextIdx];
          if (next) {
            e.preventDefault();
            next.focus();
          }
          return;
        }
      }

      if (e.key !== 'Enter') return;
      if (target.tagName === 'TEXTAREA') return;
      if (target.closest('.ant-select-dropdown')) return;
      if (target.closest('.ant-picker-dropdown')) return;
      const record = allTransactionsTableData.find(
        (r) => r.__transactionUuid === editingTransactionId
      );
      if (record) {
        e.preventDefault();
        handleSaveTransaction(record);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    editingTransactionId,
    allTransactionsTableData,
    handleSaveTransaction,
    handleCancelEditTransaction,
  ]);

  // --- Pending row-switch handlers (save / discard / cancel) ---
  const handleSaveAndSwitch = React.useCallback(() => {
    if (!pendingRowSwitch) return;

    if (isInlineFileGroupEditing && fileGroupEditSession) {
      const seedRecord = allTransactionsTableData.find(
        (r) => r.__transactionUuid === fileGroupEditSession.seedUuid
      );
      if (seedRecord) {
        handleSaveTransaction(seedRecord);
      }
      setPendingRowSwitch(null);
      return;
    }

    // Save the currently-edited row (same logic as handleSaveTransaction)
    if (editingDraft && editingTransactionId && organisationId) {
      const currentRecord = allTransactionsTableData.find(
        (r) => r.__transactionUuid === editingTransactionId
      );
      const original = currentRecord?.__transactionRow as
        | ValidatedExcelTransaction
        | undefined;
      if (original) {
        const isReviewModeRow =
          isReceiptsProvider && transactionQualifiesForMarkAsReviewed(original);

        const page = currentPage || 1;
        const pageSize = activeTablePageSize;
        const baseFilters: Record<string, unknown> = {
          ...(currentFiltersState ?? {}),
        };
        if (requestTabFileImportIds && requestTabFileImportIds.length > 0) {
          baseFilters.excelMetadataId = { value: requestTabFileImportIds };
        } else {
          delete (baseFilters as any).excelMetadataId;
        }
        const mergedFilters = baseFilters;

        if (hasRowChanges(editingDraft, original)) {
          const body = buildTransactionSaveBody({
            original: original as ExcelTransaction,
            draft: editingDraft as ExcelTransaction,
            showTrackingCategoryColumns,
            trackingCategories: organisationTrackingCategories,
            isAccountIdProvider,
            accountNameByIdMap,
            accountNameOptions,
            accountNameOptionsGrouped,
            itemNameByIdMap,
            transactionItemOptions,
            taxRateByIdMap,
          deriveGrossNetFromQuantityUnitPrice: showQuantityUnitPriceColumns,
          });
          const missingRequired = validateTransactionEditSave(
            body,
            transactionEditSaveValidation
          );
          if (missingRequired.length > 0) {
            showTransactionEditValidationError(missingRequired);
            setPendingRowSwitch(null);
            return;
          }
          if (isReviewModeRow) {
            queueActiveTabRefreshAfterReview();
          }
          dispatch(
            updateExcelTransactionRequest(
              organisationId,
              editingTransactionId,
              body,
              {
                page,
                pageSize,
                filters: mergedFilters,
                ...(isReviewModeRow
                  ? { markAsReviewedIds: [editingTransactionId] }
                  : {}),
              }
            )
          );
        }
      }
    }

    // Switch to the new row, or just clear editing if click-outside
    if (pendingRowSwitch.uuid && pendingRowSwitch.original) {
      const { uuid, original, dataKey, mode } = pendingRowSwitch;
      const switchRecord = allTransactionsTableData.find(
        (r) => r.__transactionUuid === uuid
      );
      if (!isExcelProvider && switchRecord && uuid) {
        if (mode === 'fullRow') {
          startNonExcelFileGroupEdit(switchRecord, uuid, original);
          setPendingRowSwitch(null);
          return;
        }
        if (mode === 'cell' && dataKey) {
          setEditingTransactionId(uuid);
          setEditingDraft(original);
          setEditingColumnKeys(new Set([dataKey]));
          setPendingRowSwitch(null);
          return;
        }
      } else {
        setEditingTransactionId(uuid);
        setEditingDraft(original);
        setEditingColumnKeys(
          mode === 'fullRow' ? new Set(['__all__']) : new Set([dataKey!])
        );
      }
    } else {
      // Click-outside: just close editing
      setEditingTransactionId(null);
      setEditingDraft(null);
      setEditingColumnKeys(null);
    }
    setPendingRowSwitch(null);
  }, [
    pendingRowSwitch,
    isInlineFileGroupEditing,
    fileGroupEditSession,
    handleSaveTransaction,
    editingDraft,
    editingTransactionId,
    organisationId,
    allTransactionsTableData,
    isExcelProvider,
    isReceiptsProvider,
    startNonExcelFileGroupEdit,
    isAccountIdProvider,
    accountNameByIdMap,
    accountNameOptions,
    taxRateByIdMap,
    showTrackingCategoryColumns,
    transactionEditSaveValidation,
    showTransactionEditValidationError,
    currentPage,
    activeTablePageSize,
    currentFiltersState,
    requestTabFileImportIds,
    dispatch,
    isReceiptsProvider,
    isAccountIdProvider,
    showTrackingCategoryColumns,
    queueActiveTabRefreshAfterReview,
  ]);

  const handleDiscardAndSwitch = React.useCallback(() => {
    if (!pendingRowSwitch) return;

    if (isInlineFileGroupEditing) {
      onCloseAttachments();
      resetInlineEditFilePageSizeOverride();
      clearFileGroupEdit();
      resetInlineEditFilePageSizeOverride();
      setEditingTransactionId(null);
      setEditingDraft(null);
      setEditingColumnKeys(null);
    }

    if (pendingRowSwitch.uuid && pendingRowSwitch.original) {
      const { uuid, original, dataKey, mode } = pendingRowSwitch;
      const switchRecord = allTransactionsTableData.find(
        (r) => r.__transactionUuid === uuid
      );
      if (!isExcelProvider && switchRecord && uuid) {
        if (mode === 'fullRow') {
          startNonExcelFileGroupEdit(switchRecord, uuid, original);
          setPendingRowSwitch(null);
          return;
        }
        if (mode === 'cell' && dataKey) {
          setEditingTransactionId(uuid);
          setEditingDraft(original);
          setEditingColumnKeys(new Set([dataKey]));
          setPendingRowSwitch(null);
          return;
        }
      } else {
        setEditingTransactionId(uuid);
        setEditingDraft(original);
        setEditingColumnKeys(
          mode === 'fullRow' ? new Set(['__all__']) : new Set([dataKey!])
        );
      }
    } else {
      setEditingTransactionId(null);
      setEditingDraft(null);
      setEditingColumnKeys(null);
    }
    setPendingRowSwitch(null);
  }, [
    pendingRowSwitch,
    isInlineFileGroupEditing,
    clearFileGroupEdit,
    onCloseAttachments,
    resetInlineEditFilePageSizeOverride,
    allTransactionsTableData,
    isExcelProvider,
    startNonExcelFileGroupEdit,
  ]);

  const handleCancelSwitch = React.useCallback(() => {
    setPendingRowSwitch(null);
  }, []);

  /** Normalise mass-update value for API (date string, numeric 2dp as number, etc.). */
  const normaliseMassUpdateValue = React.useCallback(
    (field: string, value: unknown): unknown => {
      if (value == null || value === '') {
        return field === 'dueDate' ||
          field === 'exchangeRate' ||
          field === 'discountAmount'
          ? null
          : value;
      }
      if (field === 'transactionDate' || field === 'dueDate') {
        const d = dayjs(value as string | dayjs.Dayjs);
        return d.isValid() ? d.format('YYYY-MM-DD') : value;
      }
      if (NUMERIC_COLUMN_KEYS.has(field)) {
        if (typeof value === 'string' || typeof value === 'number') {
          const parsed = parseLocaleNumber(value);
          return field === 'quantity'
            ? toApiQuantity(parsed)
            : toApiNumber(parsed);
        }
        return value;
      }
      return value;
    },
    []
  );

  const handleMassUpdateApply = React.useCallback(() => {
    if (!organisationId || !massUpdateField || allTransactionsTableData == null)
      return;
    const page = currentPage || 1;
    const pageSize = activeTablePageSize;
    const saveRefresh = getSaveRefreshParams(page, pageSize);
    const rows = allTransactionsTableData ?? [];
    const toUpdate: Record<string, unknown>[] =
      massUpdateSelectedRowKeys.length > 0
        ? massUpdateSelectedRowKeys
            .map((key) =>
              rows.find((r) => r.key === key || r.__transactionUuid === key)
            )
            .filter(
              (r): r is Record<string, unknown> =>
                r != null &&
                (r.__transactionRow as ValidatedExcelTransaction) != null
            )
        : rows.filter(
            (r) =>
              (r.__transactionRow as ValidatedExcelTransaction | undefined) !=
              null
          );
    if (toUpdate.length === 0) return;
    const ids = toUpdate.map(
      (record) => (record.__transactionUuid ?? record.key) as string
    );

    if (massUpdateField === MASS_UPDATE_FIELD_MARK_AS_REVIEWED) {
      for (const record of toUpdate) {
        const original = record.__transactionRow as
          | ValidatedExcelTransaction
          | undefined;
        if (!original) continue;
        const body = buildTransactionSaveBody({
          original: original as ExcelTransaction,
          draft: {},
          showTrackingCategoryColumns,
          trackingCategories: organisationTrackingCategories,
          isAccountIdProvider,
          accountNameByIdMap,
          accountNameOptions,
          accountNameOptionsGrouped,
          itemNameByIdMap,
          transactionItemOptions,
          taxRateByIdMap,
        deriveGrossNetFromQuantityUnitPrice: showQuantityUnitPriceColumns,
        });
        const missingRequired = validateTransactionEditSave(
          body,
          transactionEditSaveValidation
        );
        if (missingRequired.length > 0) {
          const ref = [body.transactionDate, body.transactionNumber]
            .filter((v) => v != null && String(v).trim() !== '')
            .join(' ? ');
          showTransactionEditValidationError(
            missingRequired,
            ref ? `(line: ${ref}) Review status was not changed.` : undefined
          );
          return;
        }
      }
      setMassUpdateApplying(true);
      queueActiveTabRefreshAfterReview();
      skipNextFetchRef.current = true;
      dispatch(
        setTransactionsReviewedRequest(
          organisationId,
          ids,
          page,
          pageSize,
          saveRefresh.filters,
          saveRefresh.tabFileImportIds
        )
      );
      return;
    }

    const normalised = normaliseMassUpdateValue(
      massUpdateField,
      massUpdateValue
    );
    let updates: Record<string, unknown> = {};
    if (massUpdateField === 'accountCode') {
      const raw =
        normalised != null
          ? Array.isArray(normalised)
            ? String(normalised[0] ?? '').trim()
            : String(normalised).trim()
          : '';
      const accountFields = applyAccountSelectionToTransactionFields(
        raw,
        accountNameByIdMap,
        accountNameOptions,
        accountNameOptionsGrouped
      );
      updates = { ...updates, ...accountFields };
    } else if (massUpdateField === 'itemId') {
      const raw =
        normalised != null
          ? Array.isArray(normalised)
            ? String(normalised[0] ?? '').trim()
            : String(normalised).trim()
          : '';
      const itemFields = applyItemSelectionToTransactionFields(
        raw,
        itemNameByIdMap,
        transactionItemOptions
      );
      updates = { ...updates, ...itemFields };
    } else if (massUpdateField === 'contactName') {
      const rawVal =
        normalised != null
          ? Array.isArray(normalised)
            ? String(normalised[0] ?? '').trim()
            : String(normalised).trim()
          : '';
      if (rawVal) {
        updates.contactId = rawVal;
      }
    } else if (massUpdateField === 'taxName') {
      // Column is labelled Tax Name; value is taxRateId.
      updates.taxRateId = normalised;
    } else if (isTrackingOptionIdField(massUpdateField)) {
      const rawVal =
        normalised != null
          ? Array.isArray(normalised)
            ? String(normalised[0] ?? '').trim()
            : String(normalised).trim()
          : '';
      updates = {
        ...updates,
        ...applyTrackingOptionSelectionToTransactionFields(
          massUpdateField,
          rawVal,
          organisationTrackingCategories
        ),
      };
    } else {
      updates[massUpdateField] = normalised;
    }
    setMassUpdateApplying(true);
    skipNextFetchRef.current = true;
    dispatch(
      bulkUpdateExcelTransactionsRequest(organisationId, ids, updates, {
        page,
        pageSize,
        filters: saveRefresh.filters,
        tabFileImportIds: saveRefresh.tabFileImportIds,
      })
    );
    setMassUpdateApplying(false);
  }, [
    organisationId,
    massUpdateField,
    massUpdateValue,
    massUpdateSelectedRowKeys,
    allTransactionsTableData,
    currentPage,
    activeTablePageSize,
    currentFiltersState,
    getSaveRefreshParams,
    normaliseMassUpdateValue,
    rightPanelTransactionTab,
    dispatch,
    showTrackingCategoryColumns,
    organisationTrackingCategories,
    isAccountIdProvider,
    accountNameByIdMap,
    accountNameOptions,
    accountNameOptionsGrouped,
    itemNameByIdMap,
    transactionItemOptions,
    taxRateByIdMap,
    transactionEditSaveValidation,
    showTransactionEditValidationError,
    queueActiveTabRefreshAfterReview,
  ]);

  const showAllTransactionsTable =
    allExcelTransactionsPage?.organisationId === organisationId &&
    allTransactionsTableColumns.length > 0;

  const massUpdateContactOptions = React.useMemo(() => {
    if (massUpdateField !== 'contactName') {
      return contactOptions;
    }
    const rows = allTransactionsTableData ?? [];
    const sourceTypes = massUpdateSelectedRowKeys
      .map((key) =>
        rows.find((r) => r.key === key || r.__transactionUuid === key)
      )
      .filter((r): r is Record<string, unknown> => r != null)
      .map(
        (r) =>
          (r.__transactionRow as ValidatedExcelTransaction | undefined)
            ?.sourceType
      );
    return resolveMassUpdateContactOptionsForSourceTypes(
      contactOptions,
      contactTypeByContactId,
      sourceTypes,
      organisation?.providerName
    );
  }, [
    massUpdateField,
    massUpdateSelectedRowKeys,
    allTransactionsTableData,
    contactOptions,
    contactTypeByContactId,
    organisation?.providerName,
  ]);

  /** Tracking dimensions in mass update ? same gate and labels as table tracking columns. */
  const massUpdateTrackingFieldOptions = React.useMemo(() => {
    if (!showTrackingCategoryColumns) return [];
    return TRACKING_OPTION_ID_FIELDS.map((fieldKey, idx) => {
      const fromApi = organisationTrackingCategories[idx]?.name?.trim();
      const mainLabel =
        fromApi ||
        getEditDrawerLabel(fieldKey, t);
      return {
        label: (
          <>
            {mainLabel}{' '}
            <span style={{ fontSize: 11, color: 'var(--color-text-muted, #5a6a6a)' }}>{t('organisation.optional')}</span>
          </>
        ),
        value: fieldKey,
        filterText: mainLabel,
      };
    });
  }, [showTrackingCategoryColumns, organisationTrackingCategories]);

  const massUpdateTrackingValueOptions = React.useMemo(() => {
    if (!isTrackingOptionIdField(massUpdateField)) return [];
    const idx = trackingOptionIdFieldToDimensionIndex(massUpdateField);
    return trackingSelectOptionsForValue(
      organisationTrackingCategories,
      idx,
      ''
    );
  }, [massUpdateField, organisationTrackingCategories]);

  /** Options for mass-update field dropdown (editable keys + "Mark as reviewed" only when selected rows include at least one not-yet-reviewed). */
  const massUpdateFieldOptions = React.useMemo(() => {
    const rows = allTransactionsTableData ?? [];
    const selectedRows = massUpdateSelectedRowKeys
      .map((key) =>
        rows.find((r) => r.key === key || r.__transactionUuid === key)
      )
      .filter((r): r is Record<string, unknown> => r != null);
    const hasAnyNotReviewed = selectedRows.some((r) => {
      const status = (r.__transactionRow as ValidatedExcelTransaction)
        ?.reviewStatus;
      return status === ReviewStatus.NOT_REVIEWED;
    });
    const baseOptions = [
      ...EDITABLE_ROW_KEYS.filter(
        (key) =>
          key !== 'taxRate' &&
          key !== 'taxRateId' &&
          key !== 'accountId' &&
          !(
            showQuantityUnitPriceColumns &&
            (key === 'gross' || key === 'net')
          ) &&
          !ENTITY_TRANSACTIONS_TABLE_EXCLUDED_COLUMN_KEYS.has(key) &&
          !excelProviderHiddenColumnKeys.has(key) &&
          (showTransactionItemColumns || key !== 'itemId') &&
          (showQuantityUnitPriceColumns ||
            (key !== 'quantity' &&
              key !== 'unitPrice' &&
              key !== 'taxInclusive'))
      ).map((key) => {
        const mainLabel = getEditDrawerLabel(key, t);
        const isOptional = EDIT_DRAWER_OPTIONAL_KEYS.has(key);
        return {
          label: isOptional ? (
            <>
              {mainLabel}{' '}
              <span style={{ fontSize: 11, color: 'var(--color-text-muted, #5a6a6a)' }}>{t('organisation.optional')}</span>
            </>
          ) : (
            mainLabel
          ),
          value: key,
          /** Used by filterOption so search works when label is a React node. */
          filterText: mainLabel,
        };
      }),
      ...massUpdateTrackingFieldOptions,
    ];
    return hasAnyNotReviewed
      ? [
          {
            label: t('common.mark-as-reviewed'),
            value: MASS_UPDATE_FIELD_MARK_AS_REVIEWED,
          },
          ...baseOptions,
        ]
      : baseOptions;
  }, [
    t,
    massUpdateSelectedRowKeys,
    allTransactionsTableData,
    excelProviderHiddenColumnKeys,
    showTrackingCategoryColumns,
    showTransactionItemColumns,
    showQuantityUnitPriceColumns,
    massUpdateTrackingFieldOptions,
  ]);

  // When selection no longer includes any not-reviewed rows, clear "Mark as reviewed" if it was selected.
  const markAsReviewedInOptions = massUpdateFieldOptions.some(
    (o) => o.value === MASS_UPDATE_FIELD_MARK_AS_REVIEWED
  );
  React.useEffect(() => {
    if (
      massUpdateField === MASS_UPDATE_FIELD_MARK_AS_REVIEWED &&
      !markAsReviewedInOptions
    ) {
      setMassUpdateField('');
      setMassUpdateValue(undefined);
    }
  }, [massUpdateField, markAsReviewedInOptions]);

  // Tax rate is not in mass-update options (derived from tax name); clear if it was selected.
  // Net / Total are derived for Xero / QuickBooks / Zoho / Receipts — same as table/drawer.
  React.useEffect(() => {
    if (massUpdateField === 'taxRate') {
      setMassUpdateField('');
      setMassUpdateValue(undefined);
      return;
    }
    if (
      showQuantityUnitPriceColumns &&
      (massUpdateField === 'gross' || massUpdateField === 'net')
    ) {
      setMassUpdateField('');
      setMassUpdateValue(undefined);
    }
  }, [massUpdateField, showQuantityUnitPriceColumns]);

  const uploadFileToProvider = (
    fileId: string,
    status?: ProviderPostStatus
  ) => {
    if (!organisation?.connectedProperly) {
      const name = organisation?.friendlyName || t('common.entity');
      dispatch(
        turnMessageOn({
          title: t('organisation.entity-not-connected-title'),
          description: t(
            'organisation.entity-not-connected-to-provider',
            { entityName: name }
          ) as string,
          type: MessageStates.ERROR,
        } as MessageData)
      );
      return;
    }
    dispatch(
      uploadReceiptToProvider(
        organisationId,
        [fileId],
        status,
        ledgerPostAccountId
      )
    );
  };

  const uploadFilesToProvider = React.useCallback(
    (fileIds: string[], status?: ProviderPostStatus) => {
      if (!organisation?.connectedProperly) {
        const name = organisation?.friendlyName || t('common.entity');
        dispatch(
          turnMessageOn({
            title: t('organisation.entity-not-connected-title'),
            description: t(
              'organisation.entity-not-connected-to-provider',
              { entityName: name }
            ) as string,
            type: MessageStates.ERROR,
          } as MessageData)
        );
        return;
      }

      const uniqueIds = Array.from(new Set(fileIds.filter(Boolean)));
      if (!uniqueIds.length) return;
      dispatch(
        uploadReceiptToProvider(
          organisationId,
          uniqueIds,
          status,
          ledgerPostAccountId
        )
      );
    },
    [
      dispatch,
      organisationId,
      organisation?.connectedProperly,
      organisation?.friendlyName,
      ledgerPostAccountId,
      t,
    ]
  );

  /** Filter-style dimensions and look for mass-update field inputs (match FilterSelect). */
  const massUpdateFilterStyle: React.CSSProperties = {
    minWidth: 120,
    width: 120,
    height: 32,
    borderRadius: 10,
    border: '1px solid var(--color-border, #dbeae3)',
    boxShadow: '0px 0px 10px 0px #0000000d',
    fontSize: 12,
    fontWeight: 600,
  };

  const exportTransactionColumns = React.useMemo(
    () => getEntityTransactionExportColumns(allTransactionsTableColumns),
    [allTransactionsTableColumns]
  );

  const handleExportTransactions = React.useCallback(async () => {
    if (!organisationId || transactionsExporting) return;

    const total =
      allExcelTransactionsPage?.total ??
      displayedTransactionsTableData.length;
    if (!total || exportTransactionColumns.length === 0) return;

    setTransactionsExporting(true);
    try {
      const mergedFilters = buildTransactionsRequestFilters(currentFiltersState);
      const apiFilters = convertScopeTableFiltersToApiBody(mergedFilters);
      const { getAllExcelTransactions } = await import(
        'store/organisation/api'
      );
      const response = await getAllExcelTransactions(
        organisationId,
        1,
        Math.max(total, 1),
        apiFilters
      );
      const content = response?.data?.transactions?.content ?? [];
      const rows: ValidatedExcelTransaction[] = content.map((row) => ({
        ...normalizeExcelTransactionFromApi(row),
        errorReason: '',
      }));
      if (!rows.length) return;

      const exportTab = showFileStateTabs ? rightPanelTransactionTab : 'ALL';
      const sheetName =
        exportTab === 'CAPTURED'
          ? t('organisation.transactions-view-tab-pending-review')
          : exportTab === 'REVIEWED'
          ? t(transactionsReviewedTabLabelKey)
          : exportTab === 'POSTED'
          ? t('organisation.transactions-view-tab-posted')
          : t('organisation.entity-transactions-title');

      await exportEntityTransactionsToExcel(
        rows,
        exportTransactionColumns,
        {
          accountNameByIdMap,
          accountNameOptions,
          itemNameByIdMap,
          transactionItemOptions,
          taxNameByIdMap,
          taxNameOptions,
          countries,
          trackingCategories: organisation?.trackingCategories,
          formatNumber,
          formatNewContactLabel,
          findMatchingContactOption,
          t,
        },
        sheetName,
        getEntityTransactionsExportFilename(exportTab)
      );
    } finally {
      setTransactionsExporting(false);
    }
  }, [
    organisationId,
    transactionsExporting,
    allExcelTransactionsPage?.total,
    displayedTransactionsTableData.length,
    exportTransactionColumns,
    buildTransactionsRequestFilters,
    currentFiltersState,
    showFileStateTabs,
    rightPanelTransactionTab,
    t,
    transactionsReviewedTabLabelKey,
    accountNameByIdMap,
    accountNameOptions,
    itemNameByIdMap,
    transactionItemOptions,
    taxNameByIdMap,
    taxNameOptions,
    countries,
    organisation?.trackingCategories,
    formatNumber,
    formatNewContactLabel,
    findMatchingContactOption,
  ]);

  /** Page totals sit on the filter row (trailing); mass-update / show-deleted stay below. */
  const {
    transactionsFilterTrailingTotals,
    transactionsTableTitleComponent,
  } = React.useMemo(() => {
    const totalItems = allTransactionsTableData?.length ?? 0;
    const selectedCount = massUpdateMode ? massUpdateSelectedRowKeys.length : 0;
    const itemString =
      massUpdateMode && selectedCount > 0
        ? `${formatDisplayInteger(selectedCount, i18n.language)} ${t(
            'organisation.out-of'
          )} ${formatDisplayInteger(totalItems, i18n.language)} ${t(
            'organisation.records-selected'
          )}`
        : `${formatDisplayInteger(totalItems, i18n.language)} ${t(
            'organisation.items-on-page'
          )}`;
    const selectedRows =
      allTransactionsTableData?.filter((r) =>
        massUpdateSelectedRowKeys.includes(r.key as React.Key)
      ) ?? [];
    const dataSource =
      massUpdateMode && selectedCount > 0
        ? selectedRows
        : allTransactionsTableData ?? [];
    const rowsForTotals = dataSource
      .map((r) => r.__transactionRow as ValidatedExcelTransaction)
      .filter((row): row is ValidatedExcelTransaction => Boolean(row?.uuid));
    const draftMapForTotals = Object.fromEntries(
      rowsForTotals.map((row) => [String(row.uuid), row] as const)
    );
    const {
      net: totalNet,
      tax: totalTax,
      gross: totalGross,
    } = sumDocumentDrawerAmountTotals(rowsForTotals, draftMapForTotals);
    const totalsCurrencyCode = resolveTransactionTotalsCurrencyCode(
      rowsForTotals,
      draftMapForTotals,
      organisation?.currency
    );
    const formatTotalAmount = (amount: number) =>
      formatFormattedAmountWithCurrency(
        totalsCurrencyCode,
        locale,
        formatTransactionAmountDisplay(amount)
      );

    const trailingTotals = (
      <EntityTransactionsFilterTotals>
        <EntityTransactionsMetaText>{itemString}</EntityTransactionsMetaText>
        <EntityTransactionsMetaDivider aria-hidden />
        <EntityTransactionsMetaText>
          {t('organisation.transactions-totals-summary', {
            totalsLabel: t('vat-forms.totals'),
            selectedSuffix:
              selectedCount > 0 ? ` ${t('transactions.of-selected')}` : '',
            netLabel: t('transactions.net'),
            net: formatTotalAmount(totalNet),
            taxLabel: t('transactions.tax'),
            tax: formatTotalAmount(totalTax),
            totalLabel: t('transactions.gross'),
            total: formatTotalAmount(totalGross),
          })}
        </EntityTransactionsMetaText>
      </EntityTransactionsFilterTotals>
    );

    const showMetaBar = massUpdateMode || supportsDeletedAndHistory;
    const metaBar = !showMetaBar ? null : (
      <EntityTransactionsMetaBar>
        {massUpdateMode ? (
          <EntityTransactionsMassUpdateControls>
            <FilterSelect
              placeholder={t('organisation.select-field-to-update')}
              value={massUpdateField || undefined}
              onChange={(val) => {
                setMassUpdateField(typeof val === 'string' ? val : '');
                setMassUpdateValue(
                  typeof val === 'string' && val === 'taxInclusive'
                    ? true
                    : undefined
                );
              }}
              options={massUpdateFieldOptions}
              rootClassName="mass-update-field-select"
              style={{ minWidth: 176, width: 176, height: 32 }}
              dropdownStyle={{ minWidth: 240 }}
              popupMatchSelectWidth={false}
            />
            {(massUpdateField === 'transactionDate' ||
              massUpdateField === 'dueDate') && (
              <DatePicker
                value={
                  massUpdateValue
                    ? dayjs(massUpdateValue as string).isValid()
                      ? dayjs(massUpdateValue as string)
                      : null
                    : null
                }
                format="DD/MM/YYYY"
                onChange={(date) =>
                  setMassUpdateValue(
                    date ? date.format('YYYY-MM-DD') : undefined
                  )
                }
                className="mass-update-datepicker"
                style={{ ...massUpdateFilterStyle, minWidth: 160, width: 160 }}
              />
            )}
            {massUpdateField === 'taxInclusive' && (
              <FilterSelect
                value={massUpdateValue !== false ? 'yes' : 'no'}
                options={[
                  { label: t('common.yes'), value: 'yes' },
                  { label: t('common.no'), value: 'no' },
                ]}
                onChange={(val) => setMassUpdateValue(val === 'yes')}
                style={{ ...massUpdateFilterStyle, minWidth: 100, width: 100 }}
                dropdownStyle={{ minWidth: 100 }}
                popupMatchSelectWidth={false}
              />
            )}
            {massUpdateField === 'currency' && (
              <FilterSelect
                showSearch
                value={(massUpdateValue as string) ?? undefined}
                options={currencyOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) => setMassUpdateValue(val)}
                style={{ ...massUpdateFilterStyle, minWidth: 120, width: 120 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
              />
            )}
            {massUpdateField === 'contactType' && (
              <FilterSelect
                value={(massUpdateValue as string) ?? undefined}
                options={contactTypeOptions}
                onChange={(val) => setMassUpdateValue(val)}
                style={{ ...massUpdateFilterStyle, minWidth: 140, width: 140 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
              />
            )}
            {massUpdateField === 'sourceType' && (
              <FilterSelect
                showSearch
                value={(massUpdateValue as string) ?? undefined}
                options={sourceTypeOptionsWithExtras}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) => setMassUpdateValue(val)}
                style={{ ...massUpdateFilterStyle, minWidth: 140, width: 140 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
              />
            )}
            {massUpdateField === 'country' && (
              <FilterSelect
                showSearch
                value={(massUpdateValue as string) ?? undefined}
                options={countryOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) => setMassUpdateValue(val)}
                style={{ ...massUpdateFilterStyle, minWidth: 160, width: 160 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
              />
            )}
            {massUpdateField === 'contactName' && (
              <FilterSelect
                showSearch
                value={(massUpdateValue as string) ?? undefined}
                options={massUpdateContactOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) => setMassUpdateValue(val ?? '')}
                style={{ ...massUpdateFilterStyle, minWidth: 220, width: 220 }}
                dropdownStyle={{ minWidth: 300 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            )}
            {massUpdateField === 'accountCode' && (
              <FilterSelect
                showSearch
                value={(massUpdateValue as string) ?? undefined}
                options={
                  accountNameOptionsGrouped.length > 0
                    ? accountNameOptionsGrouped
                    : accountNameOptions.length > 0
                    ? accountNameOptions
                    : massUpdateValue
                    ? [
                        {
                          label: String(massUpdateValue),
                          value: String(massUpdateValue),
                        },
                      ]
                    : []
                }
                filterOption={(input, option) => {
                  const label = (option?.label ?? '').toString().toLowerCase();
                  if (label.includes((input ?? '').toLowerCase())) return true;
                  const opts = option?.options;
                  if (Array.isArray(opts))
                    return opts.some((o: any) =>
                      (o?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes((input ?? '').toLowerCase())
                    );
                  return false;
                }}
                onChange={(val) => setMassUpdateValue(val ?? '')}
                style={{ ...massUpdateFilterStyle, minWidth: 160, width: 160 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            )}
            {massUpdateField === 'itemId' && (
              <FilterSelect
                showSearch
                value={(massUpdateValue as string) ?? undefined}
                options={
                  transactionItemOptions.length > 0
                    ? transactionItemOptions
                    : massUpdateValue
                    ? [
                        {
                          label:
                            itemNameByIdMap.get(String(massUpdateValue)) ??
                            String(massUpdateValue),
                          value: String(massUpdateValue),
                        },
                      ]
                    : []
                }
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes((input ?? '').toLowerCase())
                }
                onChange={(val) => setMassUpdateValue(val ?? '')}
                style={{ ...massUpdateFilterStyle, minWidth: 160, width: 160 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            )}
            {massUpdateField === 'taxName' && (
              <FilterSelect
                showSearch
                value={(massUpdateValue as string) ?? undefined}
                options={
                  taxNameOptions.length > 0
                    ? taxNameOptions
                    : massUpdateValue
                    ? [
                        {
                          label:
                            taxNameByIdMap.get(String(massUpdateValue)) ??
                            String(massUpdateValue),
                          value: String(massUpdateValue),
                        },
                      ]
                    : []
                }
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes((input ?? '').toLowerCase())
                }
                onChange={(val) => setMassUpdateValue(val ?? '')}
                style={{ ...massUpdateFilterStyle, minWidth: 160, width: 160 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            )}
            {isTrackingOptionIdField(massUpdateField) && (
              <FilterSelect
                showSearch
                allowClear
                value={(massUpdateValue as string) ?? undefined}
                options={massUpdateTrackingValueOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes((input ?? '').toLowerCase())
                }
                onChange={(val) =>
                  setMassUpdateValue(
                    val === undefined || val === null ? '' : String(val)
                  )
                }
                style={{ ...massUpdateFilterStyle, minWidth: 160, width: 160 }}
                dropdownStyle={{ minWidth: 240 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
              />
            )}
            {massUpdateField &&
              ![
                MASS_UPDATE_FIELD_MARK_AS_REVIEWED,
                'transactionDate',
                'dueDate',
                'currency',
                'contactType',
                'sourceType',
                'country',
                'contactName',
                'accountCode',
                'itemId',
                'taxName',
                'taxInclusive',
                'trackingOption1Id',
                'trackingOption2Id',
              ].includes(massUpdateField) && (
                <Input
                  value={
                    massUpdateValue != null && massUpdateValue !== ''
                      ? String(massUpdateValue)
                      : ''
                  }
                  onChange={(e) => setMassUpdateValue(e.target.value)}
                  placeholder={t('organisation.enter-value')}
                  style={{
                    ...massUpdateFilterStyle,
                    minWidth: NUMERIC_COLUMN_KEYS.has(massUpdateField)
                      ? 90
                      : 160,
                    width: NUMERIC_COLUMN_KEYS.has(massUpdateField) ? 90 : 160,
                  }}
                />
              )}
            <MassUpdateApplyButton
              type="dark-orange"
              size="small"
              fontSize="12px"
              lineheight="16px"
              height="32px"
              width="auto"
              disabled={
                !massUpdateField ||
                massUpdateApplying ||
                (massUpdateField === MASS_UPDATE_FIELD_MARK_AS_REVIEWED &&
                  selectedCount === 0) ||
                (massUpdateField !== MASS_UPDATE_FIELD_MARK_AS_REVIEWED &&
                  massUpdateField !== 'taxInclusive' &&
                  !isTrackingOptionIdField(massUpdateField) &&
                  (massUpdateValue == null || massUpdateValue === ''))
              }
              onClick={handleMassUpdateApply}
            >
              {massUpdateApplying
                ? t('common.sending-request')
                : t('organisation.apply-update')}
            </MassUpdateApplyButton>
          </EntityTransactionsMassUpdateControls>
        ) : null}
        {supportsDeletedAndHistory ? (
          <EntityTransactionsShowDeleted>
            <span className="show-deleted-label">
              {t('organisation.show-deleted')}
            </span>
            <StyledTooltip
              title={
                showDeletedTransactions
                  ? t('common.toggle-on-click-to-turn-off')
                  : t('common.toggle-off-click-to-turn-on')
              }
            >
              <Switch
                size={'small'}
                checked={showDeletedTransactions}
                className={showDeletedTransactions ? 'switch-checked' : ''}
                aria-label={t('organisation.show-deleted')}
                onChange={handleShowDeletedTransactionsChange}
              />
            </StyledTooltip>
          </EntityTransactionsShowDeleted>
        ) : null}

      </EntityTransactionsMetaBar>
    );

    return {
      transactionsFilterTrailingTotals: trailingTotals,
      transactionsTableTitleComponent: metaBar,
    };
  }, [
    massUpdateMode,
    massUpdateSelectedRowKeys,
    massUpdateField,
    massUpdateValue,
    massUpdateFieldOptions,
    massUpdateApplying,
    allTransactionsTableData,
    formatNumber,
    locale,
    organisation?.currency,
    currencyOptions,
    countryOptions,
    contactOptions,
    massUpdateContactOptions,
    massUpdateTrackingValueOptions,
    contactTypeOptions,
    sourceTypeOptionsWithExtras,
    accountNameOptionsGrouped,
    accountNameOptions,
    transactionItemOptions,
    itemNameByIdMap,
    taxNameOptions,
    taxNameByIdMap,
    t,
    i18n.language,
    handleMassUpdateApply,
    showDeletedTransactions,
    handleShowDeletedTransactionsChange,
    supportsDeletedAndHistory,
  ]);

  const getUploadAvailability = React.useCallback(
    (file: {
      isPosted?: boolean;
      excelMetadataId?: string;
      importId?: string;
    }) => {
      if (!is3rdPartyProvider || file.isPosted) {
        return { show: false, disabled: false };
      }
      const excelMetadataId = String(file.excelMetadataId ?? '').trim();
      const importId = String(file.importId ?? '').trim();
      const disabled =
        (!!excelMetadataId && fileIdsWithPendingReview.has(excelMetadataId)) ||
        (!!importId && fileIdsWithPendingReview.has(importId));
      return { show: true, disabled };
    },
    [is3rdPartyProvider, fileIdsWithPendingReview]
  );

  const selectableUploadFileIds = React.useMemo(
    () =>
      filteredExcelFiles
        .filter((file) => {
          const state = getUploadAvailability(file as ExcelFileMetadata);
          return state.show && !state.disabled;
        })
        .map((file) => String(file.excelMetadataId))
        .filter(Boolean),
    [filteredExcelFiles, getUploadAvailability]
  );

  React.useEffect(() => {
    if (!uploadSelectionMode || rightPanelTransactionTab !== 'REVIEWED') {
      return;
    }
    setSelectedUploadFileIds(selectableUploadFileIds);
    if (selectableUploadFileIds.length === 0) {
      setUploadSelectionMode(false);
    }
  }, [
    uploadSelectionMode,
    rightPanelTransactionTab,
    selectableUploadFileIds,
  ]);

  const allUploadFilesSelected =
    selectableUploadFileIds.length > 0 &&
    selectableUploadFileIds.every((id) => selectedUploadFileIds.includes(id));

  const someUploadFilesSelected = selectableUploadFileIds.some((id) =>
    selectedUploadFileIds.includes(id)
  );

  const handleToggleSelectAllUploadFiles = React.useCallback(() => {
    setSelectedUploadFileIds((prev) => {
      const allSelected =
        selectableUploadFileIds.length > 0 &&
        selectableUploadFileIds.every((id) => prev.includes(id));
      return allSelected ? [] : selectableUploadFileIds;
    });
  }, [selectableUploadFileIds]);

  const wasPostingToProviderRef = React.useRef(false);
  React.useEffect(() => {
    const wasPosting = wasPostingToProviderRef.current;
    wasPostingToProviderRef.current = !!isPostingToProvider;

    if (
      !wasPosting ||
      isPostingToProvider ||
      !uploadSelectionMode ||
      rightPanelTransactionTab !== 'REVIEWED'
    ) {
      return;
    }

    if (selectableUploadFileIds.length > 0) {
      setSelectedUploadFileIds(selectableUploadFileIds);
    }
  }, [
    isPostingToProvider,
    uploadSelectionMode,
    rightPanelTransactionTab,
    selectableUploadFileIds,
  ]);

  // handleOpenFilterSelection is temporarily disabled while filters are forced.
  // Re-enable together with onOpenFilterSelection prop on ScopeTable when
  // the "+" button and user-selectable filters are restored.

  /** Fixed viewer uses viewport `right` inset; column width spans resize strip + white panel when sidebar is visible. */
  const attachmentSideColumnWidthPx =
    FILE_PANEL_RESIZE_STRIP_HIT_PX + filePanelWidth;
  const attachmentViewerDrawerOffsetPx =
    ENTITY_TRANSACTIONS_TAB_HORIZONTAL_PADDING_PX;
  const attachmentViewerStandaloneWidthPx =
    showUploadsPanel !== false ? attachmentSideColumnWidthPx : filePanelWidth;

  const transactionsTableScrollHostRef = React.useRef<HTMLDivElement>(null);
  const [transactionsTableBodyScrollY, setTransactionsTableBodyScrollY] =
    React.useState<number>();

  React.useLayoutEffect(() => {
    if (pwaMobileNav || !showAllTransactionsTable) return;
    const host = transactionsTableScrollHostRef.current;
    if (!host) return;

    const measure = () => {
      const thead = host.querySelector('.ant-table-thead');
      const header = host.querySelector('.ant-table-header') ?? thead;
      const tableWrapper = host.querySelector('.ant-table-wrapper');
      const pager = tableWrapper?.nextElementSibling;
      const panel = host.closest('.entity-details-tab-panel');
      const card = host.closest('.entity-transactions-table-card');
      if (
        !(header instanceof HTMLElement) ||
        !(pager instanceof HTMLElement) ||
        !(panel instanceof HTMLElement) ||
        !(card instanceof HTMLElement)
      ) {
        return;
      }

      const panelStyle = window.getComputedStyle(panel);
      const panelBottom =
        panel.getBoundingClientRect().bottom -
        (Number.parseFloat(panelStyle.paddingBottom) || 0) -
        (Number.parseFloat(panelStyle.borderBottomWidth) || 0);
      const availableCard = Math.floor(
        panelBottom - card.getBoundingClientRect().top
      );
      if (availableCard > 80) {
        const nextMax = `${availableCard}px`;
        if (card.style.maxHeight !== nextMax) {
          card.style.maxHeight = nextMax;
        }
      }

      const cardBody = host.closest('.ant-card-body');
      const padBottom =
        cardBody instanceof HTMLElement
          ? Number.parseFloat(
              window.getComputedStyle(cardBody).paddingBottom
            ) || 0
          : 0;
      const pagerStyles = window.getComputedStyle(pager);
      const pagerBlock =
        pager.offsetHeight +
        (Number.parseFloat(pagerStyles.marginTop) || 0) +
        (Number.parseFloat(pagerStyles.marginBottom) || 0);
      const room = Math.floor(
        panelBottom -
          header.getBoundingClientRect().bottom -
          pagerBlock -
          padBottom
      );

      const scrollBody = host.querySelector('.ant-table-body');
      const tbody = host.querySelector('.ant-table-tbody');
      const natural =
        scrollBody instanceof HTMLElement
          ? scrollBody.scrollHeight
          : tbody instanceof HTMLElement
            ? tbody.offsetHeight
            : 0;

      // Rows fit on screen: drop the inner scroll so the card hugs them.
      // Rows overflow: cap the body at the space left above the screen edge.
      if (room < 80 || natural <= room + 1) {
        setTransactionsTableBodyScrollY((prev) =>
          prev == null ? prev : undefined
        );
        return;
      }

      setTransactionsTableBodyScrollY((prev) =>
        prev != null && Math.abs(prev - room) < 2 ? prev : room
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    const panel = host.closest('.entity-details-tab-panel');
    if (panel) observer.observe(panel);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [
    pwaMobileNav,
    showAllTransactionsTable,
    rightPanelTransactionTab,
    massUpdateMode,
    transactionsTableBodyScrollY,
  ]);

  const showRescanGroupingOptions =
    !isExcelProvider && rescanFileImportId != null;

  const closeRescanModal = React.useCallback(() => {
    rescanFromDrawerFileIdRef.current = null;
    setRescanFileImportId(null);
  }, []);

  return (
    <div
      className={`entity-transactions-tab${
        pwaMobileNav ? ' scope-pwa-transactions-tab' : ''
      }`}
      style={{
        backgroundColor: pwaMobileNav
          ? 'transparent'
          : 'var(--color-surface, #f3f7f8)',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: pwaMobileNav ? transactionsTabPaddingPx : 12,
        paddingBottom: pwaMobileNav ? transactionsTabPaddingPx : 4,
        paddingLeft: transactionsTabPaddingPx,
        paddingRight: transactionsTabPaddingPx,
        boxSizing: 'border-box',
        minHeight: pwaMobileNav ? undefined : 0,
      }}
    >
      {pwaMobileNav && organisationId ? (
        <PwaReviewActivitySheet
          open={pwaActivitySheetOpen}
          onClose={() => setPwaActivitySheetOpen(false)}
          organisationId={organisationId}
          contextLabel={
            organisation?.friendlyName || organisation?.legalName || undefined
          }
        />
      ) : null}
      <MassUpdateFieldSelectHighlightStyle />
      <ConfirmModal
        open={pendingRowSwitch != null}
        title={t('organisation.transactions-unsaved-row-title')}
        onCancel={handleCancelSwitch}
        size="compact"
        showCancel={false}
        secondaryAction={{
          label: t('organisation.transactions-unsaved-row-discard'),
          onClick: handleDiscardAndSwitch,
        }}
        primaryAction={{
          label: t('common.save'),
          onClick: handleSaveAndSwitch,
        }}
      >
        <p style={{ margin: 0 }}>
          {t('organisation.transactions-unsaved-row-description')}
        </p>
      </ConfirmModal>

      <Modal
        title={t('common.view-history')}
        open={historyModalOpen}
        onCancel={closeTransactionHistoryModal}
        footer={null}
        centered
        width="min(920px, 92vw)"
        className="transaction-history-modal"
        styles={{
          content: {
            maxHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            paddingBottom: 16,
          },
          header: {
            flexShrink: 0,
            marginBottom: 0,
            paddingBottom: 12,
          },
          body: {
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'none',
            overflow: 'auto',
            paddingTop: 12,
          },
        }}
        destroyOnClose
      >
        {historyModalOpen &&
        historyTransactionUuid &&
        resolvedOrganisationId ? (
          <TransactionHistoryModalContent
            key={historyTransactionUuid}
            transactionUuid={historyTransactionUuid}
            applicationConnectionId={resolvedOrganisationId}
            organisationId={resolvedOrganisationId}
            providerName={organisation?.providerName}
          />
        ) : null}
      </Modal>

      <ConfirmModal
        open={deleteTransactionRecord != null}
        title={t('organisation.delete-transaction')}
        onCancel={() => setDeleteTransactionRecord(null)}
        size="compact"
        cancelAction={{
          label: t('common.cancel'),
          onClick: () => setDeleteTransactionRecord(null),
        }}
        primaryAction={{
          label: t('common.yes'),
          variant: 'danger',
          onClick: () => {
            if (deleteTransactionRecord) {
              handleDeleteTransaction(deleteTransactionRecord);
              setDeleteTransactionRecord(null);
            }
          },
        }}
      >
        <p style={{ margin: 0 }}>
          {t('organisation.delete-transaction-description')}
        </p>
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--color-text-muted, #5a6a6a)' }}>
          {t('organisation.delete-transaction-hint')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={deleteFileImportId != null}
        title={t('organisation.file-delete-modal-title')}
        onCancel={() => setDeleteFileImportId(null)}
        size="compact"
        cancelAction={{
          label: t('common.cancel'),
          onClick: () => setDeleteFileImportId(null),
        }}
        primaryAction={{
          label: t('common.yes'),
          variant: 'danger',
          onClick: () => {
            if (deleteFileImportId) {
              handleDeleteFile(deleteFileImportId);
              setDeleteFileImportId(null);
            }
          },
        }}
      >
        <p style={{ margin: 0 }}>
          {t('organisation.file-delete-modal-description')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={rescanFileImportId != null}
        title={t('organisation.file-rescan-modal-title')}
        onCancel={closeRescanModal}
        // Above DrawerContainer (z-index 1200) so rescan confirm is visible
        // when opened from the transaction edit drawer.
        zIndex={1300}
        size={showRescanGroupingOptions ? 'standard' : 'compact'}
        showCancel={false}
        secondaryAction={
          showRescanGroupingOptions
            ? {
                label: t('organisation.file-rescan-group-lines'),
                variant: 'primary',
                onClick: () => {
                  if (rescanFileImportId) {
                    handleConfirmRescanFile(rescanFileImportId, true);
                  }
                },
              }
            : undefined
        }
        primaryAction={
          showRescanGroupingOptions
            ? {
                label: t('organisation.file-rescan-all-lines'),
                variant: 'primary',
                onClick: () => {
                  if (rescanFileImportId) {
                    handleConfirmRescanFile(rescanFileImportId, false);
                  }
                },
              }
            : {
                label: t('common.proceed'),
                onClick: () => {
                  if (rescanFileImportId) {
                    handleConfirmRescanFile(rescanFileImportId);
                  }
                },
              }
        }
      >
        <p style={{ margin: 0 }}>
          {t('organisation.file-rescan-modal-description')}
        </p>
        {showRescanGroupingOptions ? (
          <p style={{ margin: '12px 0 0' }}>
            {t('organisation.file-rescan-modal-description-grouping')}
          </p>
        ) : null}
      </ConfirmModal>

      {attachmentsTransactionId !== NOT_CLICKED_ATTACHMENT &&
        !editInDrawer &&
        !(drawerOpen && drawerData?.type === DrawerType.EDIT) && (
          <AttachmentViewer
            onClose={onCloseAttachments}
            transactionReference={attachmentDrawerTransactionRef || undefined}
            attachments={entityTransactionAttachments}
            attachmentSourceUrl=""
            attachmentLoading={attachmentLoading}
            providerName={organisation?.providerName ?? ''}
            drawerOffset={attachmentViewerDrawerOffsetPx}
            standaloneWidth={attachmentViewerStandaloneWidthPx}
            standaloneFullHeight
            showUpload={isAccountIdProvider}
            onUploadAttachment={handleUploadAttachment}
            currentUser={undefined}
            currentOrganisation={organisation as any}
            hasAdvancedUserRights={undefined}
            topSlot={
              showEditInDrawerSetting ? (
                <TransactionReviewDrawerSegmentedWrap>
                  <TransactionEditModeSegmented
                    value="table"
                    onChange={(mode) => {
                      if (mode === 'drawer') {
                        handlePersistedEditInDrawerChangeRef.current(true);
                      }
                    }}
                  />
                </TransactionReviewDrawerSegmentedWrap>
              ) : undefined
            }
          />
        )}
      <div
        className={`transactions-tab-wrapper${
          pwaMobileNav ? ' scope-pwa-transactions-layout' : ''
        }`}
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: pwaMobileNav ? 'flex-start' : 'stretch',
          gap: pwaMobileNav ? 0 : TRANSACTIONS_TAB_SIDE_GAP_PX,
          minHeight: pwaMobileNav ? undefined : 0,
          flex: pwaMobileNav ? undefined : 1,
          paddingRight:
            !pwaMobileNav && showUploadsPanel !== false
              ? FILE_PANEL_RESIZE_STRIP_HIT_PX +
                filePanelWidth +
                TRANSACTIONS_TAB_SIDE_GAP_PX
              : 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Main content: card + table. Desktop rows scroll inside the table.
            PWA uses the file panel as the page, so skip this column unless
            a Capture promo still needs to sit above it. */}
        {(!pwaMobileNav || captureDocumentsPromo) && (
        <div
          className={pwaMobileNav ? undefined : 'transactions-tab-main'}
          style={{
            flex: pwaMobileNav ? '0 0 auto' : 1,
            minWidth: 0,
            minHeight: pwaMobileNav ? undefined : 0,
            maxWidth: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {captureDocumentsPromo ? (
            <div style={{ width: '100%' }}>{captureDocumentsPromo}</div>
          ) : null}
          {showAllTransactionsTable && !pwaMobileNav && (
            <StyledCard
              width="100%"
              $padding={pwaMobileNav ? undefined : '24px'}
              className={
                pwaMobileNav
                  ? 'scope-pwa-transactions-card'
                  : 'entity-transactions-table-card'
              }
              style={{
                boxShadow: pwaMobileNav ? undefined : '0px 4px 10px 0px #0000000D',
              }}
            >
              <EntityTransactionsCardInner
                className={
                  pwaMobileNav
                    ? 'scope-pwa-transactions-card-inner'
                    : 'entity-transactions-card-fill'
                }
              >
                {/* Title and buttons above the filters, inside the white box */}
                <EntityTransactionsHeader
                  className={
                    pwaMobileNav ? 'scope-pwa-transactions-header' : undefined
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: pwaMobileNav ? 'nowrap' : 'wrap',
                      minHeight: 32,
                      width: pwaMobileNav ? '100%' : undefined,
                      maxWidth: pwaMobileNav ? '100%' : undefined,
                      minWidth: pwaMobileNav ? 0 : undefined,
                      overflow: pwaMobileNav ? 'hidden' : undefined,
                    }}
                  >
                    {showFileStateTabs ? (
                      <div
                        className={
                          pwaMobileNav
                            ? 'scope-pwa-transactions-stepper-row'
                            : undefined
                        }
                        style={{
                          display: 'flex',
                          alignItems: pwaMobileNav ? 'stretch' : 'center',
                          flexDirection: pwaMobileNav ? 'column' : undefined,
                          gap: pwaMobileNav ? 6 : 8,
                          flexWrap: pwaMobileNav ? 'nowrap' : 'wrap',
                          width: pwaMobileNav ? '100%' : undefined,
                          minWidth: 0,
                        }}
                      >
                        <TransactionsFileStateStepper
                          activeTab={rightPanelTransactionTab}
                          showPosted={showPostedFileState}
                          reviewedTabLabelKey={transactionsReviewedTabLabelKey}
                          counts={{
                            CAPTURED: tabViewFileCounts.CAPTURED,
                            REVIEWED: tabViewFileCounts.REVIEWED,
                          }}
                          onTabChange={handleFileStateTabChange}
                        />
                        {!pwaMobileNav && showReadyToPostTour ? (
                          <QuestionMarkTooltip
                            iconSize={20}
                            title={t('organisation.ready-to-post-tour-show-tip')}
                            onClick={() => handleShowReadyToPostTour()}
                          />
                        ) : null}
                      </div>
                    ) : (
                      <CardTitleLargeSpan
                        style={{ fontSize: 20, lineHeight: '32px' }}
                      >
                        {t('organisation.entity-transactions-title')}
                      </CardTitleLargeSpan>
                    )}
                  </div>
                  <EntityTransactionsHeaderActions
                    className={
                      pwaMobileNav
                        ? 'scope-pwa-transactions-header-actions'
                        : undefined
                    }
                  >
                    {!pwaMobileNav ? (
                      <div ref={massUpdateButtonTourTargetRef}>
                        <EntityTransactionsToolbarIconButton
                          type="default"
                          disabled={
                            showFileStateTabs &&
                            rightPanelTransactionTab === 'POSTED'
                          }
                          icon={<CheckSquareOutlined style={{ fontSize: 14 }} />}
                          style={entityTransactionsToolbarButtonStyle(
                            massUpdateMode
                          )}
                          onClick={() => {
                            setMassUpdateMode((prev) => {
                              if (prev) {
                                setMassUpdateSelectedRowKeys([]);
                                setMassUpdateField('');
                                setMassUpdateValue(undefined);
                                return false;
                              }
                              return true;
                            });
                          }}
                        >
                          {massUpdateMode
                            ? t('organisation.mass-update-cancel')
                            : t('organisation.mass-update')}
                        </EntityTransactionsToolbarIconButton>
                      </div>
                    ) : null}
                    {!pwaMobileNav ? (
                      <Tooltip title={t('organisation.export-transactions-excel-tooltip')}>
                        <span>
                          <ExportButton
                            icon={
                              <IconExport style={{ width: 20, height: 20 }} />
                            }
                            onClick={handleExportTransactions}
                            loading={transactionsExporting}
                            disabled={
                              transactionsExporting ||
                              (allExcelTransactionsPage?.total ?? 0) === 0
                            }
                          >
                            <span>{t('common.export')}</span>
                          </ExportButton>
                        </span>
                      </Tooltip>
                    ) : null}
                    {onUploadFile && (
                      <span
                        ref={uploadButtonTourTargetRef}
                        className={
                          pwaMobileNav
                            ? 'scope-pwa-transactions-action scope-pwa-transactions-action--upload'
                            : undefined
                        }
                      >
                        <ScopeButton
                          type="dark-orange" 
                          fontSize="12px"
                          lineheight="16px"
                          height="32px"
                          width={pwaMobileNav ? '100%' : 'max-content'}
                          style={{
                            paddingLeft: 16,
                            paddingRight: 16,
                            ...(pwaMobileNav ? { width: '100%' } : {}),
                          }}
                          disabled={!scanPrerequisitesMet}
                          onClick={() => {
                            if (isReceiptsProvider) {
                              // Switch to Capture first, then trigger the
                              // Capture panel's file input (same as clicking
                              // the drag-and-drop area) so user sees progress.
                              onReceiptsUploadStart?.();
                              setTimeout(
                                () =>
                                  document
                                    .getElementById('scan-files-input')
                                    ?.click(),
                                350
                              );
                            } else if (onUploadFile) {
                              onUploadFile();
                            }
                          }}
                        >
                          {pwaMobileNav
                            ? t('common.upload')
                            : isReceiptsProvider
                            ? t('organisation.file-upload-more')
                            : t('organisation.upload-another-excel-csv')}
                        </ScopeButton>
                      </span>
                    )}
                    {context != null && !showUploadsPanel ? (
                      <>
                        <button
                          type="button"
                          onClick={() => context.setShowUploadsPanel(true)}
                          aria-label={t('organisation.show-uploads')}
                          style={{
                            cursor: 'pointer',
                            width: 30,
                            height: 30,
                            borderRadius: 5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid transparent',
                            background: 'none',
                            boxShadow: 'none',
                            padding: 0,
                            margin: 0,
                            appearance: 'none',
                            color: 'inherit',
                          }}
                        >
                          <MenuOutlined aria-hidden />
                        </button>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: '14px',
                            color: 'var(--color-text-muted, #5a6a6a)',
                          }}
                        >
                          {t('organisation.show-uploads')}
                        </span>
                      </>
                    ) : null}
                  </EntityTransactionsHeaderActions>
                </EntityTransactionsHeader>
                <PreviewTableWrapper
                  className={
                    pwaMobileNav
                      ? 'scope-pwa-transactions-table-wrap'
                      : 'transactions-table-fill'
                  }
                >
                  <Spin
                    spinning={
                      !!allExcelTransactionsLoading || !!isPostingToProvider
                    }
                  >
                    <div
                      className={
                        pwaMobileNav ? 'scope-pwa-transactions-filters' : undefined
                      }
                    >
                      <ScopeTableFilters
                        filters={allFilters}
                        mainLocaleKey="transactions"
                        visibleFilters={visibleFilters}
                        customFilterOptions={transactionsTableCustomFilterOptions}
                        value={currentFiltersState ?? {}}
                        onChange={(next) => {
                          setCurrentFiltersState(next);
                          handlePreferences(PreferenceActions.SET, {
                            name: TRANSACTIONS_TAB_FILTER_STATE_KEY,
                            value: sanitizeTableManagedFilters(
                              next as Record<string, unknown>
                            ),
                          });
                        }}
                        originalDataSource={displayedTransactionsTableData}
                        tableDataSource={displayedTransactionsTableData}
                        customPagination={true}
                        onServerFilter={handleExcelTransactionsServerFilter}
                        embedded={false}
                        filterRowMarginBottom={pwaMobileNav ? 8 : 12}
                        filterTrailingContent={
                          pwaMobileNav
                            ? undefined
                            : transactionsFilterTrailingTotals
                        }
                        matchTriggerWidth={pwaMobileNav}
                      />
                    </div>
                    {transactionsTableTitleComponent}
                    <div
                      ref={transactionsTableScrollHostRef}
                      className={
                        pwaMobileNav ? undefined : 'transactions-table-scroll-host'
                      }
                    >
                    <ScopeTable
                      key={tableInstanceKey}
                      className="validation-errors-table transaction-table"
                      mainLocaleKey="transactions"
                      fontSize={12}
                      externalFiltersState={currentFiltersState ?? {}}
                      tableDataSource={displayedTransactionsTableData}
                      originalDataSource={displayedTransactionsTableData}
                      columns={allTransactionsTableColumns}
                      pagination={true}
                      rowsPerPage={activeTablePageSize}
                      page={
                        !allExcelTransactionsLoading &&
                        allExcelTransactionsPage?.organisationId ===
                          organisationId
                          ? allExcelTransactionsPage.page
                          : undefined
                      }
                      paginationSummaryOverride={paginationSummaryOverride}
                      enableColumnsCustomization={true}
                      disableColumnsCustomization={isEditingTransaction}
                      hideToolbarColumnsCustomizationButton
                      onColumnsChange={handleColumnsChange}
                      visibleColumns={effectiveVisibleColumns}
                      lockedColumns={lockedColumns}
                      columnOrder={columnOrder}
                      alwaysFixedColumns={transactionsTableAlwaysFixedColumns}
                      optionalColumnKeys={Array.from(EDIT_DRAWER_OPTIONAL_KEYS)}
                      rowHeight={pwaMobileNav ? 52 : 45}
                      showSizeChanger={!pwaMobileNav && !inlineEditFilePageSizeOverride}
                      getNewPageSize={savePageSize}
                      rowSelection={
                        massUpdateMode
                          ? {
                              // Keep checkbox column fixed with attachment icons;
                              // otherwise body cells slide under the left fixed cols.
                              fixed: true,
                              columnWidth: 40,
                              selectedRowKeys: massUpdateSelectedRowKeys,
                              onChange: (keys) =>
                                setMassUpdateSelectedRowKeys(keys),
                            }
                          : undefined
                      }
                      haveBorder
                      transactionType=""
                      reportId=""
                      total={allExcelTransactionsPage?.total}
                      customPagination={true}
                      onPageChange={handleExcelTransactionsPageChange}
                      scroll={
                        pwaMobileNav
                          ? { x: '100%' }
                          : {
                              x: 'max-content',
                              ...(transactionsTableBodyScrollY
                                ? { y: transactionsTableBodyScrollY }
                                : {}),
                            }
                      }
                      rowClassName={(record) => {
                        const row = record?.__transactionRow as
                          | ValidatedExcelTransaction
                          | undefined;
                        if (
                          supportsDeletedAndHistory &&
                          isTransactionDeletedRow(
                            row ?? { reversalType: record?.reversalType }
                          )
                        ) {
                          return 'transaction-row-deleted';
                        }
                        if (!isReceiptsProvider) return '';
                        const status = row?.reviewStatus;
                        if (
                          status === ReviewStatus.NOT_REVIEWED ||
                          status === ReviewStatus.NONE
                        ) {
                          return 'transaction-row-not-reviewed';
                        }
                        return '';
                      }}
                    />
                    </div>
                  </Spin>

                </PreviewTableWrapper>
              </EntityTransactionsCardInner>
            </StyledCard>
          )}
        </div>
        )}

        {/* Right-hand strip: portal + fixed so Spin/layout parents can't skew first paint.
            PWA renders the panel in-flow as the page (entity tabs stay clickable). */}
        {showUploadsPanel !== false &&
          (() => {
          const filePanel = (
          <div
            className={`transactions-file-panel${pwaMobileNav ? ' transactions-file-panel--pwa' : ''}`}
            style={
              pwaMobileNav
                ? undefined
                : {
              position: 'fixed',
              top: filePanelFixedTopPx,
              insetInlineEnd: ENTITY_TRANSACTIONS_TAB_HORIZONTAL_PADDING_PX,
              display: 'flex',
              flexDirection: 'row',
              flexShrink: 0,
              alignItems: 'stretch',
              height: `calc(100vh - ${filePanelFixedTopPx}px)`,
              maxHeight: `calc(100vh - ${filePanelFixedTopPx}px)`,
              width: FILE_PANEL_RESIZE_STRIP_HIT_PX + filePanelWidth,
              overflow: 'hidden',
              zIndex: 18,
              backgroundColor: 'var(--color-surface, #f3f7f8)',
            }}
          >
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label={t(
                'organisation.transactions-file-panel-resize-handle-aria'
              )}
              aria-valuemin={FILE_PANEL_WIDTH_MIN}
              aria-valuemax={FILE_PANEL_WIDTH_MAX}
              aria-valuenow={Math.round(filePanelWidth)}
              title={t(
                'organisation.transactions-file-panel-resize-handle-title'
              )}
              tabIndex={0}
              onMouseDown={(e) => {
                e.preventDefault();
                filePanelResizeStartRef.current = {
                  clientX: e.clientX,
                  width: filePanelWidth,
                };
                setFilePanelResizeActive(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  const widen = e.key === 'ArrowLeft';
                  const delta = widen ? 8 : -8;
                  setFilePanelWidth((prev) => {
                    const next = clampFilePanelWidth(prev + delta);
                    if (transactionsFilePanelWidthPrefKey)
                      handlePreferences(PreferenceActions.SET, {
                        name: transactionsFilePanelWidthPrefKey,
                        value: next,
                      });
                    return next;
                  });
                }
              }}
              style={{
                width: FILE_PANEL_RESIZE_STRIP_HIT_PX,
                flexShrink: 0,
                marginLeft: -1,
                marginRight: -1,
                cursor: 'col-resize',
                alignSelf: 'stretch',
                minHeight: 48,
                borderRadius: 0,
                background: filePanelResizeActive
                  ? FILE_PANEL_RESIZE_STRIP_BG_ACTIVE
                  : FILE_PANEL_RESIZE_STRIP_BG_IDLE,
                transition: 'background 0.15s ease',
                boxSizing: 'border-box',
                border: 'none',
              }}
              onMouseEnter={(e) => {
                if (filePanelResizeActive) return;
                (e.currentTarget as HTMLElement).style.background =
                  FILE_PANEL_RESIZE_STRIP_BG_HOVER;
              }}
              onMouseLeave={(e) => {
                if (filePanelResizeActive) return;
                (e.currentTarget as HTMLElement).style.background =
                  FILE_PANEL_RESIZE_STRIP_BG_IDLE;
              }}
            />
            <div
              className={
                pwaMobileNav ? 'scope-pwa-file-panel-inner' : undefined
              }
              style={{
                width: pwaMobileNav ? '100%' : filePanelWidth,
                flexShrink: 0,
                alignSelf: 'stretch',
                height: pwaMobileNav ? 'auto' : '100%',
                minHeight: 0,
                padding: pwaMobileNav ? '0' : '16px 20px 16px 16px',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                overflow: pwaMobileNav ? 'visible' : 'hidden',
              }}
            >
              <div style={{ flexShrink: 0 }}>
                {isReceiptsProvider && (
                  <input
                    ref={receiptFileInputRef}
                    type="file"
                    multiple
                    accept={RECEIPT_UPLOAD_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={handleReceiptFileChange}
                  />
                )}
                {pwaMobileNav ? (
                  <PwaReviewPage
                    queue={pwaReviewQueue}
                    onQueueChange={handlePwaReviewQueueChange}
                    counts={{
                      review: tabViewFileCounts.CAPTURED,
                      post: tabViewFileCounts.REVIEWED,
                    }}
                    showPosted={showPostedFileState}
                    showQueue={showFileStateTabs}
                    search={fileSearchQuery}
                    onSearchChange={setFileSearchQuery}
                    sort={pwaReviewSort}
                    onSortChange={setPwaReviewSort}
                    files={displayedFiles}
                    connectionId={connectionId}
                    onOpenFile={(excelMetadataId) => {
                      void navigateDrawerToFile(excelMetadataId);
                    }}
                    hasMore={pwaReviewHasMore}
                    onLoadMore={() =>
                      setFileListCurrentPage((page) =>
                        Math.min(fileListTotalPages, page + 1)
                      )
                    }
                    toolbar={
                      uploadSelectionMode && is3rdPartyProvider ? (
                        <div className="scope-pwa-review-post-toolbar">
                          {isLedgerProviderPlan && (
                            <p className="scope-pwa-review-post-hint">
                              {t('organisation.file-ledger-post-hint', {
                                provider: getProviderName(
                                  organisation?.providerName
                                ),
                                planName: ledgerProviderPlanName,
                              })}
                            </p>
                          )}
                          <div
                            ref={readyToPostTourPostButtonsRef}
                            className="scope-pwa-review-post-actions"
                          >
                            {isLedgerProviderPlan && (
                              <FilterSelect
                                showSearch
                                allowClear
                                placeholder={t('vat-report.select-account')}
                                value={ledgerPostAccountId}
                                options={ledgerBankAccountOptions}
                                filterOption={(input, option) =>
                                  (option?.label ?? '')
                                    .toString()
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                onChange={(val) =>
                                  persistLedgerPostAccountId(
                                    typeof val === 'string' ? val : undefined
                                  )
                                }
                                className={
                                  ledgerPostAccountId
                                    ? 'selected-filter'
                                    : undefined
                                }
                                style={{
                                  flex: '1 1 auto',
                                  minWidth: 120,
                                  width: '100%',
                                  maxWidth: 220,
                                  height: 36,
                                }}
                                dropdownStyle={{ minWidth: 240 }}
                                popupMatchSelectWidth={false}
                              />
                            )}
                            {showProviderPostDropdown ? (
                              <>
                                <ProviderPostDropdownMenuStyle />
                                <ProviderPostSplitButton className="scope-pwa-provider-post-split">
                                  <ScopeButton
                                    type="primary"
                                    width="auto"
                                    height="36px"
                                    fontSize="12px"
                                    style={{
                                      paddingLeft: 12,
                                      paddingRight: 12,
                                      flexShrink: 1,
                                      minWidth: 0,
                                    }}
                                    disabled={
                                      selectedUploadFileIds.length === 0 ||
                                      (isLedgerProviderPlan &&
                                        !ledgerPostAccountId)
                                    }
                                    onClick={() =>
                                      uploadFilesToProvider(
                                        selectedUploadFileIds,
                                        'AUTHORISED'
                                      )
                                    }
                                  >
                                    {t(
                                      'organisation.file-post-as-authorised'
                                    )}
                                  </ScopeButton>
                                  <Dropdown
                                    trigger={['click']}
                                    disabled={
                                      selectedUploadFileIds.length === 0 ||
                                      (isLedgerProviderPlan &&
                                        !ledgerPostAccountId)
                                    }
                                    placement="bottomRight"
                                    overlayClassName="provider-post-dropdown-menu"
                                    menu={{
                                      items: providerPostMenuItems,
                                      onClick: ({ key, domEvent }) => {
                                        domEvent.stopPropagation();
                                        if (
                                          key === 'DRAFT' ||
                                          key === 'AUTHORISED' ||
                                          key === 'SUBMITTED'
                                        ) {
                                          uploadFilesToProvider(
                                            selectedUploadFileIds,
                                            key
                                          );
                                        }
                                      },
                                    }}
                                  >
                                    <span className="provider-post-split-arrow">
                                      <ScopeButton
                                        type="primary"
                                        width="36px"
                                        height="36px"
                                        fontSize="12px"
                                        htmlType="button"
                                        aria-label={t('common.more-actions')}
                                        disabled={
                                          selectedUploadFileIds.length ===
                                            0 ||
                                          (isLedgerProviderPlan &&
                                            !ledgerPostAccountId)
                                        }
                                        icon={
                                          <DownOutlined
                                            style={{ fontSize: 10 }}
                                          />
                                        }
                                      />
                                    </span>
                                  </Dropdown>
                                </ProviderPostSplitButton>
                              </>
                            ) : (
                              <ScopeButton
                                type="primary"
                                width="auto"
                                height="36px"
                                fontSize="12px"
                                style={{
                                  paddingLeft: 12,
                                  paddingRight: 12,
                                  flexShrink: 0,
                                }}
                                disabled={
                                  selectedUploadFileIds.length === 0 ||
                                  (isLedgerProviderPlan &&
                                    !ledgerPostAccountId)
                                }
                                onClick={() =>
                                  uploadFilesToProvider(selectedUploadFileIds)
                                }
                              >
                                {t('organisation.file-post-to-provider', {
                                  provider: isQboProvider
                                    ? 'QuickBooks'
                                    : getProviderName(
                                        organisation?.providerName
                                      ),
                                })}
                              </ScopeButton>
                            )}
                          </div>
                        </div>
                      ) : undefined
                    }
                  />
                ) : context != null && showUploadsPanel ? (
                  <div
                    style={{
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => context.setShowUploadsPanel(false)}
                        aria-label={t('organisation.hide-uploads')}
                        style={{
                          cursor: 'pointer',
                          width: 30,
                          height: 30,
                          borderRadius: 5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid var(--color-border, #dbeae3)',
                          background: 'var(--color-surface, #f3f7f8)',
                          boxShadow: '2px 2px 2px 0px #adc2b94d inset',
                          padding: 0,
                          margin: 0,
                          appearance: 'none',
                          color: 'inherit',
                        }}
                      >
                        <MenuOutlined aria-hidden />
                      </button>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 400,
                          lineHeight: '14px',
                          color: 'var(--color-text-base, #2e2e2e)',
                        }}
                      >
                        {t('organisation.hide-uploads')}
                      </span>
                      {excelFiles.length > 0 && (
                        <QuestionMarkTooltip
                          iconSize={20}
                          title={t('organisation.file-filter-tour-show-tip')}
                          onClick={() => handleShowFileFilterTour()}
                        />
                      )}
                    </div>
                    <EntityTransactionsToolbarIconButton
                      type="default"
                      icon={
                        <IconBell
                          style={{
                            width: 14,
                            height: 14,
                            color: ENTITY_TOOLBAR_MUTED_ICON_COLOR,
                          }}
                        />
                      }
                      style={entityTransactionsToolbarButtonStyle(false)}
                      onClick={handleActivityClick}
                    >
                      {t('organisation.reconciliation-activity')}
                    </EntityTransactionsToolbarIconButton>
                  </div>
                ) : null}

                {!pwaMobileNav && excelFiles.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 6,
                      width: '100%',
                      marginBottom:
                        uploadSelectionMode && is3rdPartyProvider ? 10 : 12,
                    }}
                  >
                    <div
                      style={{
                        flex: '1 1 0',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <RoundInput
                        prefix={
                          <SearchOutlined
                            style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
                          />
                        }
                        placeholder={t(
                          'organisation.file-filter-search-placeholder'
                        )}
                        value={fileSearchQuery}
                        onChange={(e) => setFileSearchQuery(e.target.value)}
                        allowClear
                        bg_color="var(--color-surface, #f3f7f8)"
                        width="100%"
                        height="34px"
                        style={{
                          fontSize: 11,
                        }}
                      />
                    </div>
                    <DrawerDateRangeFilter
                      {...fileDateFilterProps}
                      variant="inline"
                      inlineHeight="34px"
                    />
                  </div>
                )}

                {!pwaMobileNav && uploadSelectionMode && is3rdPartyProvider && (
                  <>
                    {isLedgerProviderPlan && (
                      <p
                        style={{
                          margin: '0 0 8px',
                          fontSize: 12,
                          lineHeight: '16px',
                          color: 'var(--color-text-muted, #5a6a6a)',
                        }}
                      >
                        {t('organisation.file-ledger-post-hint', {
                          provider: getProviderName(organisation?.providerName),
                          planName: ledgerProviderPlanName,
                        })}
                      </p>
                    )}
                  <div
                    ref={readyToPostTourPostButtonsRef}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 12,
                      minHeight: 30,
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        justifyContent: isLedgerProviderPlan
                          ? 'flex-start'
                          : 'center',
                      }}
                    >
                      {isLedgerProviderPlan && (
                        <FilterSelect
                          showSearch
                          allowClear
                          placeholder={t('vat-report.select-account')}
                          value={ledgerPostAccountId}
                          options={ledgerBankAccountOptions}
                          filterOption={(input, option) =>
                            (option?.label ?? '')
                              .toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          onChange={(val) =>
                            persistLedgerPostAccountId(
                              typeof val === 'string' ? val : undefined
                            )
                          }
                          className={
                            ledgerPostAccountId ? 'selected-filter' : undefined
                          }
                          style={{
                            flex: '1 1 auto',
                            minWidth: 120,
                            width: '100%',
                            maxWidth: 200,
                            height: 30,
                          }}
                          dropdownStyle={{ minWidth: 240 }}
                          popupMatchSelectWidth={false}
                        />
                      )}
                      {showProviderPostDropdown ? (
                        <>
                          <ProviderPostDropdownMenuStyle />
                          <ProviderPostSplitButton>
                            <ScopeButton
                              type="primary"
                              width="auto"
                              height="30px"
                              fontSize="12px"
                              style={{
                                paddingLeft: 12,
                                paddingRight: 12,
                                flexShrink: 0,
                              }}
                              disabled={
                                selectedUploadFileIds.length === 0 ||
                                (isLedgerProviderPlan && !ledgerPostAccountId)
                              }
                              onClick={() =>
                                uploadFilesToProvider(
                                  selectedUploadFileIds,
                                  'AUTHORISED'
                                )
                              }
                            >
                              {t('organisation.file-post-as-authorised')}
                            </ScopeButton>
                            <Dropdown
                              trigger={['click']}
                              disabled={
                                selectedUploadFileIds.length === 0 ||
                                (isLedgerProviderPlan && !ledgerPostAccountId)
                              }
                              placement="bottomRight"
                              overlayClassName="provider-post-dropdown-menu"
                              menu={{
                                items: providerPostMenuItems,
                                onClick: ({ key, domEvent }) => {
                                  domEvent.stopPropagation();
                                  if (
                                    key === 'DRAFT' ||
                                    key === 'AUTHORISED' ||
                                    key === 'SUBMITTED'
                                  ) {
                                    uploadFilesToProvider(
                                      selectedUploadFileIds,
                                      key
                                    );
                                  }
                                },
                              }}
                            >
                              <span className="provider-post-split-arrow">
                                <ScopeButton
                                  type="primary"
                                  width="30px"
                                  height="30px"
                                  fontSize="12px"
                                  htmlType="button"
                                  aria-label={t('common.more-actions')}
                                  disabled={
                                    selectedUploadFileIds.length === 0 ||
                                    (isLedgerProviderPlan &&
                                      !ledgerPostAccountId)
                                  }
                                  icon={
                                    <DownOutlined style={{ fontSize: 10 }} />
                                  }
                                />
                              </span>
                            </Dropdown>
                          </ProviderPostSplitButton>
                        </>
                      ) : (
                        <ScopeButton
                          type="primary"
                          width="auto"
                          height="30px"
                          fontSize="12px"
                          style={{
                            paddingLeft: 12,
                            paddingRight: 12,
                            flexShrink: 0,
                          }}
                          disabled={
                            selectedUploadFileIds.length === 0 ||
                            (isLedgerProviderPlan && !ledgerPostAccountId)
                          }
                          onClick={() =>
                            uploadFilesToProvider(selectedUploadFileIds)
                          }
                        >
                          {t('organisation.file-post-to-provider', {
                            provider: isQboProvider
                              ? 'QuickBooks'
                              : getProviderName(organisation?.providerName),
                          })}
                        </ScopeButton>
                      )}
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      {selectableUploadFileIds.length > 0 ? (
                        <Tooltip
                          title={
                            allUploadFilesSelected
                              ? t('organisation.file-unselect-all-documents')
                              : t('organisation.file-select-all-documents')
                          }
                        >
                          <span
                            role="checkbox"
                            aria-checked={
                              allUploadFilesSelected
                                ? true
                                : someUploadFilesSelected
                                ? 'mixed'
                                : false
                            }
                            tabIndex={0}
                            onClick={handleToggleSelectAllUploadFiles}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleToggleSelectAllUploadFiles();
                              }
                            }}
                            style={{
                              cursor: 'pointer',
                              display: 'inline-flex',
                              color:
                                allUploadFilesSelected ||
                                someUploadFilesSelected
                                  ? 'var(--color-primary, #41aaaf)'
                                  : 'var(--color-text-muted, #5a6a6a)',
                            }}
                          >
                            <span
                              style={{
                                width: 14,
                                height: 14,
                                border: `1.5px solid ${
                                  allUploadFilesSelected ||
                                  someUploadFilesSelected
                                    ? 'var(--color-primary, #41aaaf)'
                                    : 'var(--color-text-muted, #5a6a6a)'
                                }`,
                                borderRadius: 3,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background:
                                  allUploadFilesSelected ||
                                  someUploadFilesSelected
                                    ? 'var(--color-layout-bg, #e9f6f7)'
                                    : 'transparent',
                              }}
                            >
                              {allUploadFilesSelected ? (
                                <CheckOutlined
                                  style={{ fontSize: 10, color: 'var(--color-primary, #41aaaf)' }}
                                />
                              ) : someUploadFilesSelected ? (
                                <span
                                  style={{
                                    width: 8,
                                    height: 1.5,
                                    background: 'var(--color-primary, #41aaaf)',
                                    borderRadius: 1,
                                  }}
                                />
                              ) : null}
                            </span>
                          </span>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                  </>
                )}
              </div>

              {/* Files list: desktop fits cards to the panel. PWA Review owns its list. */}
              {!pwaMobileNav ? (
              <div
                ref={setFileListContainerNode}
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                  <div>
                  {excelFiles.length === 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--color-text-muted, #5a6a6a)',
                      }}
                    >
                      {t('organisation.file-no-uploads-yet')}
                    </div>
                  )}

                  {excelFiles.length > 0 && filteredExcelFiles.length === 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--color-text-muted, #5a6a6a)',
                      }}
                    >
                      {t('organisation.file-filter-no-results')}
                    </div>
                  )}

                  {displayedFiles.map((file, index) => {
                    const isSelected = selectedImportIds.includes(
                      file.excelMetadataId
                    );
                    const isReviewed =
                      file.isReviewed === true ||
                      (file as { isReviwed?: boolean }).isReviwed === true;
                    const showReviewedBadge = !isExcelProvider && isReviewed;
                    const showPostedBadge =
                      !isExcelProvider &&
                      !isReceiptsOnlyProvider &&
                      file.isPosted;
                    const uploadState = getUploadAvailability(file);
                    const borderColor = isSelected
                      ? 'var(--color-primary, #41aaaf)'
                      : 'var(--color-border, #dbeae3)';
                    const tourPendingImportId = currentUser?.userUuid
                      ? (preferencesHook(
                          PreferenceActions.GET,
                          PreferenceSections.TRANSACTIONS,
                          {
                            name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
                          },
                          currentUser.userUuid
                        ) as string | undefined)
                      : null;
                    const tourImportIdStr =
                      typeof tourPendingImportId === 'string' &&
                      tourPendingImportId.trim()
                        ? tourPendingImportId
                        : null;
                    const isTourTarget =
                      (tourImportIdStr !== null &&
                        file.excelMetadataId === tourImportIdStr) ||
                      fileFilterTourManualTargetIndex === index;

                    const deleteFileTooltip = getDeleteFileTooltipText();
                    const rescanFileTooltip = t(
                      'organisation.file-rescan-tooltip'
                    );

                    const showFileCardRescan =
                      !isExcelProvider && !file.isPosted;

                    return (
                      <TransactionsFileCardWrap
                        key={file.excelMetadataId}
                        ref={(node) => {
                          if (index === 0) {
                            setFileListItemNode(node);
                          }
                          if (isTourTarget) {
                            fileFilterTourTargetRef.current = node;
                          }
                        }}
                        className={[
                          isSelected
                            ? 'transactions-file-card-wrap--filter-active'
                            : '',
                          rightPanelTransactionTab === 'POSTED'
                            ? 'transactions-file-card-wrap--posted'
                            : '',
                          uploadSelectionMode &&
                          uploadState.show &&
                          !uploadState.disabled
                            ? 'transactions-file-card-wrap--upload-select'
                            : '',
                          showFileCardRescan
                            ? 'transactions-file-card-wrap--has-rescan'
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined}
                      >
                        {(() => {
                            if (rightPanelTransactionTab === 'POSTED') {
                              return null;
                            }

                            const fileId = String(file.excelMetadataId);
                            const isSelectedForUpload =
                              selectedUploadFileIds.includes(fileId);

                            if (
                              uploadSelectionMode &&
                              uploadState.show &&
                              !uploadState.disabled
                            ) {
                              const handleDeleteFile = () => {
                                setDeleteFileImportId(file.excelMetadataId);
                              };

                              return (
                                <span
                                  className="transactions-file-card-reviewed-actions"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="transactions-file-card-reviewed-hover-delete">
                                    <Tooltip
                                      title={deleteFileTooltip}
                                      placement="top"
                                    >
                                      <button
                                        type="button"
                                        className="transactions-file-card-hover-action"
                                        onClick={handleDeleteFile}
                                      >
                                        {t('common.delete')}
                                      </button>
                                    </Tooltip>
                                  </span>
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label={
                                      isSelectedForUpload
                                        ? t('organisation.file-unselect-document')
                                        : t(
                                            'organisation.file-menu-select-document'
                                          )
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedUploadFileIds((prev) =>
                                        prev.includes(fileId)
                                          ? prev.filter((id) => id !== fileId)
                                          : [...prev, fileId]
                                      );
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedUploadFileIds((prev) =>
                                          prev.includes(fileId)
                                            ? prev.filter((id) => id !== fileId)
                                            : [...prev, fileId]
                                        );
                                      }
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      color: isSelectedForUpload
                                        ? 'var(--color-primary, #41aaaf)'
                                        : 'var(--color-text-muted, #5a6a6a)',
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        border: `1.5px solid ${
                                          isSelectedForUpload
                                            ? 'var(--color-primary, #41aaaf)'
                                            : 'var(--color-text-muted, #5a6a6a)'
                                        }`,
                                        borderRadius: 3,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: isSelectedForUpload
                                          ? 'var(--color-layout-bg, #e9f6f7)'
                                          : 'transparent',
                                      }}
                                    >
                                      {isSelectedForUpload ? (
                                        <CheckOutlined
                                          style={{
                                            fontSize: 10,
                                            color: 'var(--color-primary, #41aaaf)',
                                          }}
                                        />
                                      ) : null}
                                    </span>
                                  </span>
                                </span>
                              );
                            }

                            const handleRescanFile = () => {
                              setRescanFileImportId(file.excelMetadataId);
                            };

                            const handleDeleteFile = () => {
                              setDeleteFileImportId(file.excelMetadataId);
                            };

                            if (pwaMobileNav) {
                              return (
                                <TransactionsFileCardMenu
                                  showRescan={showFileCardRescan}
                                  onViewFile={() => {
                                    void navigateDrawerToFile(
                                      file.excelMetadataId
                                    );
                                  }}
                                  onRescan={handleRescanFile}
                                  onDelete={handleDeleteFile}
                                  viewFileLabel={t(
                                    'organisation.file-view-file'
                                  )}
                                  rescanLabel={t('organisation.file-rescan')}
                                  deleteLabel={t('common.delete')}
                                  moreActionsLabel={t('common.more-actions')}
                                />
                              );
                            }

                            return (
                              <TransactionsFileCardHoverActions
                                showRescan={showFileCardRescan}
                                onRescan={handleRescanFile}
                                onDelete={handleDeleteFile}
                                rescanLabel={t('organisation.file-rescan')}
                                deleteLabel={t('common.delete')}
                                rescanTooltip={rescanFileTooltip}
                                deleteTooltip={deleteFileTooltip}
                              />
                            );
                          })()}
                        <div
                          role="button"
                          tabIndex={0}
                          className="transactions-file-card-body"
                          aria-label={
                            pwaMobileNav
                              ? t('organisation.review-document-drawer-title')
                              : isSelected
                              ? t('organisation.file-unselect-document')
                              : t('organisation.file-menu-select-document')
                          }
                          aria-pressed={pwaMobileNav ? undefined : isSelected}
                          onClick={() => {
                            if (pwaMobileNav) {
                              void navigateDrawerToFile(file.excelMetadataId);
                              return;
                            }
                            toggleFileSelection(file.excelMetadataId);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (pwaMobileNav) {
                                void navigateDrawerToFile(file.excelMetadataId);
                                return;
                              }
                              toggleFileSelection(file.excelMetadataId);
                            }
                          }}
                          style={
                            pwaMobileNav
                              ? {
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  minWidth: 0,
                                  position: 'relative',
                                  borderRadius: 10,
                                  padding: '10px 40px 10px 12px',
                                  border: '1px solid var(--color-border, #dbeae3)',
                                  background: isSelected
                                    ? 'var(--color-primary-soft, #e6f7f7)'
                                    : 'var(--color-surface-raised, #ffffff)',
                                  boxShadow: '0 2px 10px rgba(4, 42, 42, 0.08)',
                                }
                              : {
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  textAlign: 'left',
                                  borderRadius: 16,
                                  padding: '10px 12px',
                                  border: `2px solid ${borderColor}`,
                                  background: isSelected
                                    ? 'var(--color-primary-soft, #e6f7f7)'
                                    : 'var(--color-surface-raised, #ffffff)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  gap: 4,
                                  minWidth: 0,
                                  position: 'relative',
                                  opacity: 1,
                                }
                          }
                        >
                          {showReviewedBadge && !showPostedBadge && (
                            <Tooltip
                              title={t(
                                'organisation.file-badge-reviewed-tooltip'
                              )}
                              placement="left"
                            >
                              <span
                                aria-label={t(
                                  'organisation.file-badge-reviewed-tooltip'
                                )}
                                className="transactions-file-card-badge transactions-file-card-badge--reviewed"
                              >
                                {t('organisation.file-badge-reviewed')}
                              </span>
                            </Tooltip>
                          )}
                          {showPostedBadge && (
                            <Tooltip
                              title={t(
                                'organisation.file-badge-posted-tooltip'
                              )}
                              placement="left"
                            >
                              <span
                                aria-label={t(
                                  'organisation.file-badge-posted-tooltip'
                                )}
                                className="transactions-file-card-badge transactions-file-card-badge--posted"
                              >
                                {t('organisation.transactions-view-tab-posted')}
                              </span>
                            </Tooltip>
                          )}
                          {pwaMobileNav ? (
                            (() => {
                              const emptyPlaceholder = '-';
                              const txCount =
                                file.totalRows != null
                                  ? String(file.totalRows)
                                  : emptyPlaceholder;
                              const uploadedOn = formatTransactionTableDate(
                                file.uploadDate,
                                i18n.language
                              );
                              return (
                                <div className="transactions-file-card-meta">
                                  <div className="transactions-file-card-meta__file">
                                    <FileOutlined
                                      className="transactions-file-card-meta__icon"
                                      aria-hidden
                                    />
                                    <div className="transactions-file-card-meta__file-text">
                                      <span className="transactions-file-card-meta__label">
                                        {t('organisation.last-uploaded-file')}
                                      </span>
                                      <Tooltip title={file.fileName}>
                                        <span className="transactions-file-card-meta__file-name">
                                          {file.fileName || emptyPlaceholder}
                                        </span>
                                      </Tooltip>
                                    </div>
                                  </div>
                                  <div className="transactions-file-card-meta__grid">
                                    <div className="transactions-file-card-meta__cell">
                                      <UnorderedListOutlined
                                        className="transactions-file-card-meta__icon"
                                        aria-hidden
                                      />
                                      <div className="transactions-file-card-meta__cell-text">
                                        <span className="transactions-file-card-meta__label">
                                          {t('organisation.transactions')}
                                        </span>
                                        <span className="transactions-file-card-meta__value">
                                          {txCount}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="transactions-file-card-meta__cell">
                                      <CalendarOutlined
                                        className="transactions-file-card-meta__icon"
                                        aria-hidden
                                      />
                                      <div className="transactions-file-card-meta__cell-text">
                                        <span className="transactions-file-card-meta__label">
                                          {t('organisation.uploaded-on')}
                                        </span>
                                        <span className="transactions-file-card-meta__value">
                                          {uploadedOn || emptyPlaceholder}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            <>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: 'var(--color-text-muted, #5a6a6a)',
                            }}
                          >
                            {formatTransactionTableDate(
                              file.uploadDate,
                              i18n.language
                            )}
                          </span>
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            {is3rdPartyProvider && file.isPosted && (
                              <Tooltip
                                title={t('organisation.file-view-in-provider', {
                                  provider: getProviderName(
                                    organisation?.providerName
                                  ),
                                })}
                              >
                                {file.linkToProvider ? (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        file.linkToProvider,
                                        '_blank',
                                        'noopener,noreferrer'
                                      );
                                    }}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {providerIcons[
                                      String(
                                        organisation?.providerName ?? ''
                                      ).toLowerCase()
                                    ] ? (
                                      <ProviderIcon
                                        providerName={
                                          organisation?.providerName
                                        }
                                        width={22}
                                        style={{ display: 'block' }}
                                      />
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color:
                                            'var(--color-primary, #41aaaf)',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {t('organisation.reconciliation-view')}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span
                                    aria-hidden
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      opacity: 0.5,
                                      cursor: 'not-allowed',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {providerIcons[
                                      String(
                                        organisation?.providerName ?? ''
                                      ).toLowerCase()
                                    ] ? (
                                      <ProviderIcon
                                        providerName={
                                          organisation?.providerName
                                        }
                                        width={22}
                                        style={{ display: 'block' }}
                                      />
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color:
                                            'var(--color-text-muted, #5a6a6a)',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {t('organisation.reconciliation-view')}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </Tooltip>
                            )}
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: '18px',
                                color: 'var(--color-text-base, #2e2e2e)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                width: '100%',
                                minWidth: 0,
                              }}
                            >
                              {file.fileName}
                            </span>
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              color: 'var(--color-text-muted, #5a6a6a)',
                            }}
                          >
                            {t('organisation.file-transactions-count', {
                              count: file.totalRows,
                            })}
                          </span>
                            </>
                          )}
                        </div>
                        {false && (
                          <div
                            style={{
                              width: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: !file.isPosted ? 4 : 0,
                              flexShrink: 0,
                              visibility: 'hidden',
                              pointerEvents: 'none',
                            }}
                          >
                            {!file.isPosted ? (
                              <Tooltip
                                title={t(
                                  'organisation.file-menu-rescan-replace'
                                )}
                                placement="left"
                              >
                                <span
                                  role="button"
                                  tabIndex={0}
                                  aria-label={t(
                                    'organisation.file-menu-rescan-replace'
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onReceiptsUploadStart?.(
                                      file.fileName ??
                                        t('organisation.file-document-fallback')
                                    );
                                    dispatch(
                                      rescanReceipt(
                                        organisationId,
                                        file.excelMetadataId
                                      )
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onReceiptsUploadStart?.(
                                        file.fileName ??
                                          t(
                                            'organisation.file-document-fallback'
                                          )
                                      );
                                      dispatch(
                                        rescanReceipt(
                                          organisationId,
                                          file.excelMetadataId
                                        )
                                      );
                                    }
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    padding: 1,
                                    display: 'inline-flex',
                                    color: 'var(--color-text-muted, #5a6a6a)',
                                    transition: 'color 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    (
                                      e.currentTarget as HTMLSpanElement
                                    ).style.color = 'var(--color-primary, #41aaaf)';
                                  }}
                                  onMouseLeave={(e) => {
                                    (
                                      e.currentTarget as HTMLSpanElement
                                    ).style.color = 'var(--color-text-muted, #5a6a6a)';
                                  }}
                                >
                                  <ScanOutlined style={{ fontSize: 16 }} />
                                </span>
                              </Tooltip>
                            ) : null}
                            {(() => {
                              const uploadState = getUploadAvailability(file);
                              if (!uploadState.show) return null;
                              const fileId = String(file.excelMetadataId);
                              const isSelectedForUpload =
                                selectedUploadFileIds.includes(fileId);

                              if (uploadSelectionMode) {
                                if (uploadState.disabled) {
                                  return (
                                    <Tooltip
                                      title={t(
                                        'organisation.file-upload-review-all-before'
                                      )}
                                      placement="left"
                                    >
                                      <span
                                        style={{
                                          padding: 1,
                                          display: 'inline-flex',
                                          color: '#B7C3C3',
                                          opacity: 0.75,
                                        }}
                                      >
                                        <UploadOutlined
                                          style={{ fontSize: 16 }}
                                        />
                                      </span>
                                    </Tooltip>
                                  );
                                }
                                return (
                                  <Tooltip
                                    title={
                                      isSelectedForUpload
                                        ? t(
                                            'organisation.file-unselect-document'
                                          )
                                        : t(
                                            'organisation.file-menu-select-document'
                                          )
                                    }
                                    placement="left"
                                  >
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      aria-label={
                                        isSelectedForUpload
                                          ? t(
                                              'organisation.file-unselect-document'
                                            )
                                          : t(
                                              'organisation.file-menu-select-document'
                                            )
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedUploadFileIds((prev) =>
                                          prev.includes(fileId)
                                            ? prev.filter((id) => id !== fileId)
                                            : [...prev, fileId]
                                        );
                                      }}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === 'Enter' ||
                                          e.key === ' '
                                        ) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setSelectedUploadFileIds((prev) =>
                                            prev.includes(fileId)
                                              ? prev.filter(
                                                  (id) => id !== fileId
                                                )
                                              : [...prev, fileId]
                                          );
                                        }
                                      }}
                                      style={{
                                        cursor: 'pointer',
                                        padding: 1,
                                        display: 'inline-flex',
                                        color: isSelectedForUpload
                                          ? 'var(--color-primary, #41aaaf)'
                                          : 'var(--color-text-muted, #5a6a6a)',
                                        transition: 'color 0.2s ease',
                                      }}
                                      onMouseEnter={(e) => {
                                        (
                                          e.currentTarget as HTMLSpanElement
                                        ).style.color = 'var(--color-primary, #41aaaf)';
                                      }}
                                      onMouseLeave={(e) => {
                                        (
                                          e.currentTarget as HTMLSpanElement
                                        ).style.color = isSelectedForUpload
                                          ? 'var(--color-primary, #41aaaf)'
                                          : 'var(--color-text-muted, #5a6a6a)';
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: 14,
                                          height: 14,
                                          border: `1.5px solid ${
                                            isSelectedForUpload
                                              ? 'var(--color-primary, #41aaaf)'
                                              : 'var(--color-text-muted, #5a6a6a)'
                                          }`,
                                          borderRadius: 3,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          background: isSelectedForUpload
                                            ? 'var(--color-layout-bg, #e9f6f7)'
                                            : 'transparent',
                                        }}
                                      >
                                        {isSelectedForUpload ? (
                                          <CheckOutlined
                                            style={{
                                              fontSize: 10,
                                              color: 'var(--color-primary, #41aaaf)',
                                            }}
                                          />
                                        ) : null}
                                      </span>
                                    </span>
                                  </Tooltip>
                                );
                              }

                              const uploadMenuItems: MenuProps['items'] = [
                                {
                                  key: 'upload-now',
                                  label: t(
                                    'organisation.file-menu-post-transactions'
                                  ),
                                  disabled: uploadState.disabled,
                                },
                                {
                                  key: 'select-file',
                                  label: t(
                                    'organisation.file-menu-select-document'
                                  ),
                                  disabled: uploadState.disabled,
                                },
                              ];
                              return (
                                <Dropdown
                                  trigger={['click']}
                                  menu={{
                                    items: uploadMenuItems,
                                    onClick: (info) => {
                                      info.domEvent.stopPropagation();
                                      if (uploadState.disabled) return;
                                      if (info.key === 'upload-now') {
                                        uploadFileToProvider(
                                          file.excelMetadataId,
                                          showProviderPostDropdown
                                            ? 'AUTHORISED'
                                            : undefined
                                        );
                                        return;
                                      }
                                      setUploadSelectionMode(true);
                                      setSelectedUploadFileIds((prev) =>
                                        prev.includes(fileId)
                                          ? prev
                                          : [...prev, fileId]
                                      );
                                    },
                                  }}
                                >
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label={t(
                                      'organisation.file-menu-post-transactions'
                                    )}
                                    aria-haspopup="menu"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        (
                                          e.currentTarget as HTMLElement
                                        ).click();
                                      }
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      padding: 1,
                                      display: 'inline-flex',
                                      color: uploadState.disabled
                                        ? '#B7C3C3'
                                        : 'var(--color-text-muted, #5a6a6a)',
                                      opacity: uploadState.disabled ? 0.75 : 1,
                                      transition: 'color 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (uploadState.disabled) return;
                                      (
                                        e.currentTarget as HTMLSpanElement
                                      ).style.color = 'var(--color-primary, #41aaaf)';
                                    }}
                                    onMouseLeave={(e) => {
                                      (
                                        e.currentTarget as HTMLSpanElement
                                      ).style.color = uploadState.disabled
                                        ? '#B7C3C3'
                                        : 'var(--color-text-muted, #5a6a6a)';
                                    }}
                                    title={
                                      uploadState.disabled
                                        ? t(
                                            'organisation.file-upload-review-all-before'
                                          )
                                        : undefined
                                    }
                                  >
                                    <UploadOutlined style={{ fontSize: 16 }} />
                                  </span>
                                </Dropdown>
                              );
                            })()}
                            <Tooltip
                              title={getDeleteFileTooltipText()}
                              placement="left"
                            >
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={t('common.delete')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteFileImportId(file.excelMetadataId);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDeleteFileImportId(file.excelMetadataId);
                                  }
                                }}
                                style={{
                                  cursor: 'pointer',
                                  padding: 1,
                                  display: 'inline-flex',
                                  color: 'var(--color-text-muted, #5a6a6a)',
                                  transition: 'color 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  (
                                    e.currentTarget as HTMLSpanElement
                                  ).style.color = 'var(--color-primary, #41aaaf)';
                                }}
                                onMouseLeave={(e) => {
                                  (
                                    e.currentTarget as HTMLSpanElement
                                  ).style.color = 'var(--color-text-muted, #5a6a6a)';
                                }}
                              >
                                <DeleteOutlined style={{ fontSize: 16 }} />
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </TransactionsFileCardWrap>
                    );
                  })}
                  </div>
                 
                {filteredExcelFiles.length > fileListPageSize && (
                  <div
                    ref={setFileListPagerNode}
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexShrink: 0,
                      marginTop: 8,
                      marginBottom: 12,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <ScopeButton
                      width="88px"
                      height="25px"
                      fontSize="12px"
                      lineheight="12px"
                      onClick={() =>
                        setFileListCurrentPage((p) => Math.max(1, p - 1))
                      }
                      type="default"
                      style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
                      disabled={fileListCurrentPage === 1}
                    >
                      {t('main.previous')}
                    </ScopeButton>
                    <ScopeButton
                      width="75px"
                      height="25px"
                      fontSize="12px"
                      lineheight="12px"
                      onClick={() =>
                        setFileListCurrentPage((p) =>
                          Math.min(fileListTotalPages, p + 1)
                        )
                      }
                      type="default"
                      disabled={fileListCurrentPage >= fileListTotalPages}
                    >
                      {t('main.next')}
                    </ScopeButton>
                  </div>
                )}
              </div>
              ) : null}
            </div>
          </div>
          );
          return pwaMobileNav
            ? filePanel
            : createPortal(filePanel, document.body);
          })()}
      </div>
      {!pwaMobileNav ? (
        <>
          <ScopeTour
            open={fileFilterTourOpen}
            onClose={() => {
              setFileFilterTourOpen(false);
              setFileFilterTourManualTargetIndex(null);
              if (currentUser?.userUuid) {
                const providerShownKey = isReceiptsProvider
                  ? TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_RECEIPTS_KEY
                  : TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_EXCEL_KEY;
                preferencesHook(
                  PreferenceActions.SET,
                  PreferenceSections.TRANSACTIONS,
                  {
                    name: TRANSACTIONS_FILE_FILTER_TOUR_PENDING_IMPORT_ID_KEY,
                    value: null,
                  },
                  currentUser.userUuid
                );
                preferencesHook(
                  PreferenceActions.SET,
                  PreferenceSections.TRANSACTIONS,
                  {
                    name: providerShownKey,
                    value: true,
                  },
                  currentUser.userUuid
                );
                // Also set legacy key for backwards compatibility with older builds.
                preferencesHook(
                  PreferenceActions.SET,
                  PreferenceSections.TRANSACTIONS,
                  {
                    name: TRANSACTIONS_FILE_FILTER_TOUR_SHOWN_KEY,
                    value: true,
                  },
                  currentUser.userUuid
                );
              }
            }}
            steps={fileFilterTourSteps}
          />
          <ScopeTour
            open={readyToPostTourOpen}
            onClose={() => {
              setReadyToPostTourOpen(false);
              if (currentUser?.userUuid) {
                preferencesHook(
                  PreferenceActions.SET,
                  PreferenceSections.TRANSACTIONS,
                  {
                    name: TRANSACTIONS_READY_TO_POST_TOUR_SHOWN_KEY,
                    value: true,
                  },
                  currentUser.userUuid
                );
              }
            }}
            steps={readyToPostTourSteps}
          />
        </>
      ) : null}
    </div>
  );
};


